import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  isDevMode,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Chart } from 'angular-highcharts';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ToasterMessages } from 'src/app/shared/constants/constants';
import { DashboardService } from 'src/app/shared/service/dashboard/dashboard.service';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { ExpandChartComponent } from '../expand-chart/expand-chart.component';
import { PortDeleteConfirmPopupComponent } from '../port-delete-confirm-popup/port-delete-confirm-popup.component';
import { PcMultiSelectFilterComponent } from 'src/app/shared/components/pc-multi-select-filter/pc-multi-select-filter.component';
import {
  applyUsernameClientFilter,
  extractClientOptionsFromRecords,
  extractSegmentOptionsFromRecords,
  matchSegmentFilter,
  matchesAnyClient,
  matchesAnySegment,
  matchesPortCallDateRange,
  matchesSelectedPorts,
  matchesSelectedValues,
  matchesSelectedVesselNames,
  pruneSegmentSelectionsToOptions,
} from 'src/app/shared/helpers/pc-filter-options.util';
import { ScLookerFilterCascadeService } from 'src/app/shared/services/sc-looker-filter-cascade.service';
import {
  calculateCpOverallPercent,
  calculateDatabaseSummary,
  calculateDesignOverallPercent,
  formatCpOverallPercent,
  formatDesignOverallPercent,
} from 'src/app/shared/helpers/port-database-summary.util';
import {
  getPortDatabaseTableFilterSegmentOptions,
  PORT_DATABASE_TABLE_FILTER_SEGMENTS,
} from 'src/app/shared/helpers/port-database-segments.util';
import {
  formatPcPortLocalAsUtc,
  formatPcUtcDateTime,
} from 'src/app/shared/helpers/pc-utc-datetime.util';
import { parseTotalTimeToDays } from 'src/app/shared/helpers/port-database-column-totals.util';
import {
  benchmarkSegmentKeysMatch,
  extractPcBenchmarkFilterOptions,
  extractTcpBenchmarkFilterOptions,
  extractBenchmarkPortNamesFromApi,
  normalizeBenchmarkSegmentOptions,
  parseBenchmarkBuiltDate,
  resolvePcBenchmarkRows,
  resolveTcpBenchmarkRows,
} from 'src/app/shared/helpers/benchmark-filter-options.util';
import { getTcpBenchmarkBarColors } from 'src/app/shared/helpers/chart-palette.util';
import {
  buildTableFilterCascadeOptions,
  dedupeFilterOptions,
  extractRecordBuildYear,
  extractRecordOperation,
  extractRecordYard,
  formatVesselAgeLabel,
  getVesselAgeYearsFromBuildDate,
  pruneTableFilterSelections,
  TableFilterCascadeState,
} from 'src/app/shared/helpers/port-database-table-filter.util';
import { PcMultiSelectOption } from 'src/app/shared/components/pc-multi-select-filter/pc-multi-select-filter.component';
import {
  buildCpWarrantedCellView,
  buildCpWarrantedTooltip,
  buildDesignCellView,
  buildDesignTooltip,
  buildOptimizationCellView,
  buildOptimizationTooltip,
  getOptimizationLegConsumption,
  getOpActualValue,
  getOpWarrantedValue,
  getOpDesignValue,
  hasAnyActualOpData,
  invalidateRowOptimizationCache,
  ConsumptionDifferenceView,
  PortDatabaseOpKey,
} from 'src/app/shared/helpers/port-database-consumption-cell.util';
import {
  formatFilterOptionsDate,
  isAllFilterOptionsSelected,
  normalizeClientStatusValue,
  normalizeFilterApiOptions,
  normalizeOperationsFilterOptions,
  normalizePortDatabaseClients,
  matchesDatabaseOperationFilter,
  PORT_DATABASE_OPERATIONS,
  PortConsumptionFilterOptionsPayload,
  pruneSelectionsToOptions,
  resolveClientFilterPayload,
  resolveMultiSelectFilterPayload,
  resolveOperationsFilterPayload,
} from 'src/app/shared/helpers/port-database-filter-api.util';
import {
  computeOverallRangeBounds,
  ensureSliderBounds,
  formatOverallRangeDisplay,
  getOverallRangeEndpointClass,
  getOverallRangeError,
  getOverallRangeStep,
  getZeroPositionPercent,
  matchesOverallRangeField,
  normalizeOverallRangeSelection,
  OverallRangeBounds,
  parseOverallRangeValue,
} from 'src/app/shared/helpers/port-database-overall-range.util';
import { DEMO_VESSEL_PC } from 'src/app/shared/helpers/heating-table-dynamic-extract.util';
@Component({
  selector: 'app-port-database',
  templateUrl: './port-database.component.html',
  styleUrls: [
    './port-database.component.scss',
    './port-database-benchmark.scss',
    './port-database-filter.drawer.scss',
  ],
})
export class PortDatabaseComponent implements AfterViewInit, OnDestroy {
  private chartTimeout: any;
  private benchmarkDataLoaded = false;
  private adminUserCache: boolean | null = null;
  /** Payload keys of benchmark chart requests currently in flight. */
  private readonly inFlightChartRequests = new Set<string>();
  filterForm!: FormGroup;
  pcBenchmarkForm!: FormGroup;
  tcpBenchmarkForm!: FormGroup;

  tcpClientOptions: string[] = [];
  selectedTcpClients: string[] = [];
  tcpVesselTypeOptions: string[] = [];
  selectedTcpVesselTypes: string[] = [];
  tcpYardOptions: string[] = [];
  selectedTcpYards: string[] = [];
  /** Vessel age values (years) from TCP `ship_built_to` / built date fields. */
  tcpBuiltAgeOptions: string[] = [];
  tcpBuiltAgeOptionItems: PcMultiSelectOption[] = [];
  selectedTcpBuiltAges: string[] = [];
  tcpVesselNameOptions: string[] = [];
  selectedTcpVesselNames: string[] = [];
  tcpVesselCatalog: Array<{
    vessel_name: string;
    client: string;
    vessel_type: string;
    ship_yard: string;
    date_built: string;
    segment?: string;
  }> = [];
  tcpBenchmarkPool: any[] = [];
  private tcpBenchmarkMasterPool: any[] = [];
  tcpOperationOptions: string[] = [];
  tcpMasterOperationOptions: string[] = [];
  tcpMasterSegmentOptions: Array<{ key: string; value: string[] }> = [];
  pcMasterSegmentOptions: Array<{ key: string; value: string[] }> = [];
  pcClientOptions: string[] = [];
  /** Accumulated PC client names — never replaced by filtered API responses. */
  pcMasterClientOptions: string[] = [];
  selectedPcClients: string[] = [];
  pcVesselNameOptions: string[] = [];
  pcYardOptions: string[] = [];
  selectedPcYards: string[] = [];
  /** Vessel age values (years) derived from `build_year` in latest PC response. */
  pcBuiltAgeOptions: string[] = [];
  pcBuiltAgeOptionItems: PcMultiSelectOption[] = [];
  selectedPcBuiltAges: string[] = [];
  pcBuiltDateMin: string | null = null;
  pcBuiltDateMax: string | null = null;
  /** All vessel names seen from getPorts + benchmark pool — used as catalog fallback. */
  private pcMasterVesselNames: string[] = [];
  pcBenchmarkPool: any[] = [];
  private pcBenchmarkMasterPool: any[] = [];
  /**
   * Latest filtered `pc_bench_marking` response rows.
   * Vessel Name + Yard dropdowns bind only from this set (not the catalog).
   */
  private pcLatestBenchmarkRows: any[] = [];
  tcpPortOptions: string[] = [];
  /** Accumulated TCP port names from tracker/catalog — used when benchmark pool rows lack port fields. */
  private tcpMasterPortOptions: string[] = [];
  selectedTcpPorts: string[] = [];
  tcpBuiltDateMin: string | null = null;
  tcpBuiltDateMax: string | null = null;
  portDropdownOpen: boolean = false;
  pcInlineFilterOpen = false;
  tcpInlineFilterOpen = false;
  isLoadingPcChart = false;
  isLoadingTcpChart = false;
  private readonly benchDebug = true; // temporary: trace benchmark pipeline
  private vesselMetaByName = new Map<
    string,
    {
      vessel_type?: string;
      ship_yard?: string;
      date_built?: string;
      client?: string;
    }
  >();
  subusername: any;
  @ViewChild('statisticsContainer')
  set statisticsContainer(ref: ElementRef<HTMLElement> | undefined) {
    this.bindPageStickyOffset(ref?.nativeElement ?? null);
  }
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChildren(PcMultiSelectFilterComponent)
  multiSelectFilters!: QueryList<PcMultiSelectFilterComponent>;
  /** Table header sticks just below the KPI cards on full-page scroll. */
  @HostBinding('style.--pd-page-sticky-offset.px')
  pageStickyOffsetPx = 152;
  private static readonly BODY_SCROLL_CLASS = 'pc-fleet-view-scroll';
  private statsResizeObserver: ResizeObserver | null = null;
  elements: any;
  itemsPerPage: number = 10;
  pageSizeOptions: number[] = [10];
  currentPage: number = 0;
  conditionOptions = ['Ballast', 'Laden'];
  statusOptions = ['Active', 'Inactive'];
  clientOptions: string[] = [];
  clientStatusOptions: string[] = ['Active', 'Inactive'];
  /** Multi-select Client Status (Active and/or Inactive). */
  selectedClientStatuses: string[] = ['Active'];
  apiClientOptions: string[] = [];
  apiPortTypeOptions: string[] = [];
  apiSegmentOptions: string[] = [];
  apiVoyageStatusOptions: string[] = [];
  isLoadingFilterOptions = false;
  isLoadingClientFilterOptions = false;
  private filterOptionsRequestSeq = 0;
  vesselNameOptions: string[] = [];
  private suppressFilterCascade = false;
  private readonly destroy$ = new Subject<void>();
  private readonly tableFilterApply$ = new Subject<void>();
  private readonly filterOptionsReload$ = new Subject<void>();
  private lastFilterOptionsPayloadKey = '';
  /** PC-only chart refresh stream — never touches the TCP chart/API. */
  private readonly benchmarkChartUpdate$ = new Subject<void>();
  /** TCP-only chart refresh stream — never touches the PC chart/API. */
  private readonly tcpChartUpdate$ = new Subject<void>();
  private tableFilterOperationOptions: string[] = [];
  private tableFilterPortOptions: string[] = [];
  readonly defaultPortRoleFilterOptions = [
    'Load Port',
    'Discharge Port',
    'Via Points',
  ];
  isFilterOpen = false;
  hideToggle = false;
  segmentOptions: string[] = [];
  filteredData: any;
  overConsumptionOptions = ['CP Overall', 'Design Overall'];
  operations = [
    { label: 'Inerting', value: 'Inerting per day' },
    { label: 'Loading', value: 'Loading per day' },
    { label: 'Discharge', value: 'Discharge per day (For non-special area)' },
    { label: 'Idle', value: 'Idle Par day' },
    { label: 'Heating ‐ Increasing', value: 'heating ‐ increasing' },
    { label: 'Heating ‐ Maintaining', value: 'heating ‐ maintaining' },
    { label: 'Manouevering', value: 'Manouevering per day' },
    { label: 'Tank Cleaning', value: 'Tank Cleaning per day' },
    { label: 'Crude Oil Washing', value: 'Crude Oil Washing per day' },
  ];
  pcOperations: any;
  filterOperationOptions: any;
  portOptions: any;
  segments: any[] = [];
  sortMapData: boolean = false;
  isSorting: boolean = false;
  paginatedData: any;
  totalVessels: any;
  username: any;
  dropdownOpen = false;
  selectedShips: string[] = [];
  shipNameSuggestions: any[] = [];
  graph: boolean = false;

  benchmarkSegments: Array<{ key: string; value: string[] }> = [];
  benchmarkPorts: any;
  /** Authoritative port list from benchmark ports API for PC filter dropdown. */
  pcMasterPortOptions: string[] = [];
  /** Port options shown in PC benchmark dropdown (scoped by Client + Segment + Operations). */
  pcScopedPortOptions: string[] = [];
  /** Full operations list — never narrowed by cascade. */
  pcMasterOperationOptions: string[] = [];
  private pcChartRequestSeq = 0;
  operationsOptions: string[] = [];
  bothChart: any;
  chartOptions!: Chart | null;
  tcpChartOptions!: Chart | null;
  tcpData: any;
  pcData: any[] = [];
  selectedSegment: any = 'LR3/Suezmax';

  selectedOperation: any = 'Loading per day';

  clientName: any = 'ALL';
  segmentsDropdown: any[] = [];
  selectedPort: any = 'La Goulette';
  selectedPcSegment: any = 'LR3/Suezmax';
  selectedPcOperation: any = 'discharging';
  ports: any[] = [];
  azsort: boolean = false;
  isAscending: boolean = false;
  portSearchText: string = '';
  filteredPorts: string[] = [];
  portsRes: any;
  selectedPorts: string[] = [];
  selectedPortLabel: string = '';

  isPortInputFocused = false;

  isVesselInputFocused = false;
  vesselSearchText: string = '';
  vessels: { name: string; segment: string; client?: string }[] = [];
  filteredVessels: { name: string; segment: string }[] = [];
  selectedVessels: string[] = [];
  selectedFilterPort: string[] = [];
  allPorts: string[] = [];
  clientScopedPorts: string[] = [];
  clientScopedOperations: string[] = [];
  // vessels: string[] = [];
  // filteredVessels: string[] = this.vessels;
  // selectedVessels: string[] = [];
  vesselDropdownOpen: boolean = false;
  selectedVesselLabel: string = '';
  portRequestFormService: any;
  PortDropdownOpen: boolean = false;
  selectedFilterPortLabel: string = '';
  clientFilteredVessels: any;
  totalVoyages: number = 0;
  totalDays: number = 0;
  totalCPConsumption: number = 0;
  totalActualConsumption: number = 0;
  totalCpOverall: number = 0;
  totalCpOverallMt: number = 0;
  totalDesignOverall: number = 0;
  totalDesignOverallPercent: number = 0;
  isLoadingDatabase = true;
  selectedClient: any = 'ALL';
  operationsLoaded = false;
  /** Monotonic id so only the latest Port API response is applied (no stale overwrite). */
  private pcPortRequestSeq = 0;
  /** Monotonic id so only the latest Segment API response is applied (no stale overwrite). */
  private pcSegmentRequestSeq = 0;
  /** Monotonic id so only the latest Operations API response is applied (no stale overwrite). */
  private pcOperationRequestSeq = 0;
  /** Monotonic id so only the latest vessel-pool response narrows the Vessel dropdown. */
  private pcVesselPoolRequestSeq = 0;
  segmentSubNames: any;
  pcSegments: any = [];
  // Collapsible C/P Overall & Design Overall sections (detail rails + summary).
  cpExpanded = false;
  designExpanded = false;
  brobSectionExpanded = false;
  consumptionSectionExpanded = false;
  voyageDetailsSectionExpanded = false;
  totalOptimizationMT: number = 0;

  // Header-level summary calculations (reactive to table filters).
  totalConsumptionColumnSumMt: number = 0;
  actualConsumptionGroupTotalMt: number = 0;
  warrantedConsumptionGroupTotalMt: number = 0;
  designConsumptionGroupTotalMt: number = 0;
  actualOpSums = { maneuvering: 0, cargoOps: 0, idle: 0 };
  warrantedOpSums = { maneuvering: 0, cargoOps: 0, idle: 0 };
  designOpSums = { maneuvering: 0, cargoOps: 0, idle: 0 };
  optimizationOpSums = { maneuvering: 0, cargoOps: 0, idle: 0 };
  optimizationConsumptionGroupTotalMt: number = 0;
  eventTimeSums = { maneuvering: 0, idle: 0, cargoOps: 0 };
  totalEventTimeDays = 0;

  dummyYears: string[] = [
    '2010',
    '2011',
    '2012',
    '2013',
    '2014',
    '2015',
    '2016',
    '2017',
    '2018',
    '2019',
    '2020',
    '2021',
    '2022',
    '2023',
    '2024',
  ];
  filteredYears: string[] = [...this.dummyYears];
  selectedPcYears: string[] = [];
  yearSearchText: string = '';
  yearDropdownOpen: boolean = false;
  selectedYearLabel: string = '';

  // Database Side Drawer Custom Dropdowns State
  dbOverConsDropdownOpen = false;
  dbShipDropdownOpen = false;
  dbPortDropdownOpen = false;
  dbOperationDropdownOpen = false;

  dbOverConsSearchText = '';
  dbShipSearchText = '';
  dbPortSearchText = '';
  dbOperationSearchText = '';
  isLoadingTable = true;
  selectedOverConsumptions: string[] = [];
  cpOverallDatasetBounds: OverallRangeBounds | null = null;
  cpOverallSliderBounds: OverallRangeBounds | null = null;
  designOverallDatasetBounds: OverallRangeBounds | null = null;
  designOverallSliderBounds: OverallRangeBounds | null = null;
  readonly cpOverallRangeTooltip =
    'Filter voyages by CP Overall performance difference range.';
  readonly designOverallRangeTooltip =
    'Filter voyages by Design Overall performance difference range.';
  selectedSegments: string[] = [];
  selectedClients: string[] = [];
  selectedOperations: string[] = [];
  selectedVoyageStatuses: string[] = [];
  selectedPortRoles: string[] = [];

  get filteredOverConsumptionOptions() {
    return this.overConsumptionOptions.filter((opt) =>
      opt.toLowerCase().includes(this.dbOverConsSearchText.toLowerCase()),
    );
  }

  get filteredDbOperationOptions(): string[] {
    return (this.filterOperationOptions || []).filter((opt: string) =>
      opt.toLowerCase().includes(this.dbOperationSearchText.toLowerCase()),
    );
  }

  get shipFilterOptions(): string[] {
    if (this.username === 'Meraki - Operations') {
      return [...(this.vesselNameOptions || [])];
    }
    if (
      this.tableFilterOperationOptions.length ||
      this.selectedOperations.length
    ) {
      return [...(this.vesselNameOptions || [])];
    }
    if (this.vesselNameOptions.length) {
      return [...this.vesselNameOptions];
    }
    return dedupeFilterOptions(
      this.dbShipSuggestions.map((item: any) => item.vessel_name),
    );
  }

  get portFilterOptions(): string[] {
    if (this.username === 'Meraki - Operations') {
      return [...this.tableFilterPortOptions];
    }
    if (this.tableFilterPortOptions.length) {
      return [...this.tableFilterPortOptions];
    }
    if (this.shouldScopeFilterOptionsToClient()) {
      return [...this.clientScopedPorts];
    }
    return [...(this.allPorts || [])];
  }

  get portTypeFilterOptions(): string[] {
    if (this.username === 'Meraki - Operations') {
      return [...this.apiPortTypeOptions];
    }
    if (this.apiPortTypeOptions.length) {
      return [...this.apiPortTypeOptions];
    }
    return [...this.defaultPortRoleFilterOptions];
  }

  get operationFilterOptions(): string[] {
    const raw =
      this.tableFilterOperationOptions.length
        ? this.tableFilterOperationOptions
        : this.filterOperationOptions || [];
    const normalized = normalizeOperationsFilterOptions(raw);
    if (!normalized.length || normalized.length < 3) {
      return [...PORT_DATABASE_OPERATIONS];
    }
    return normalized;
  }

  get segmentFilterOptions(): string[] {
    return [...this.segmentMultiSelectOptions];
  }

  segmentApiValueMap: { [key: string]: string } = {};
  segmentMultiSelectOptions: string[] = [];

  get segmentDropdownOptions(): string[] {
    return getPortDatabaseTableFilterSegmentOptions();
  }

  get voyageStatusFilterOptions(): string[] {
    if (this.username === 'Meraki - Operations') {
      return [...this.apiVoyageStatusOptions];
    }
    if (this.apiVoyageStatusOptions.length) {
      return [...this.apiVoyageStatusOptions];
    }
    const unique = new Map<string, string>();
    this.getRecordsForClientScope().forEach((item: any) => {
      const status = item?.final_status?.trim();
      if (!status) {
        return;
      }
      const key = status.toLowerCase();
      if (!unique.has(key)) {
        unique.set(key, status);
      }
    });
    return Array.from(unique.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }

  get dbShipSuggestions() {
    const uniqueNames = new Set<string>();
    const list: any[] = [];

    (this.elements || []).forEach((item: any) => {
      const vesselName = item.vessel_name;
      const segmentMatches = matchesAnySegment(
        item.segment,
        this.selectedSegments,
      );
      const clientMatches = matchesAnyClient(item.client, this.selectedClients);

      if (
        vesselName &&
        segmentMatches &&
        clientMatches &&
        !uniqueNames.has(vesselName.toLowerCase())
      ) {
        uniqueNames.add(vesselName.toLowerCase());
        list.push(item);
      }
    });

    return list
      .filter((item) =>
        item.vessel_name
          .toLowerCase()
          .includes(this.dbShipSearchText.toLowerCase()),
      )
      .sort((a: any, b: any) => a.vessel_name.localeCompare(b.vessel_name));
  }

  get dbPortSuggestions() {
    return (this.allPorts || [])
      .filter((port) =>
        port.toLowerCase().includes(this.dbPortSearchText.toLowerCase()),
      )
      .sort((a: string, b: string) =>
        a.toLowerCase().localeCompare(b.toLowerCase()),
      );
  }

  // Toggle Methods
  toggleDbOverConsDropdown() {
    this.dbOverConsDropdownOpen = !this.dbOverConsDropdownOpen;
  }
  toggleDbShipDropdown() {
    this.dbShipDropdownOpen = !this.dbShipDropdownOpen;
  }
  toggleDbPortDropdown() {
    this.dbPortDropdownOpen = !this.dbPortDropdownOpen;
  }
  toggleDbOperationDropdown() {
    this.dbOperationDropdownOpen = !this.dbOperationDropdownOpen;
  }

  // Selection Logic Helper Methods
  isAllOverConsSelected() {
    return (
      this.filteredOverConsumptionOptions.length > 0 &&
      this.filteredOverConsumptionOptions.every((opt) =>
        this.selectedOverConsumptions.includes(opt),
      )
    );
  }
  toggleSelectAllOverCons(event: any) {
    if (event.target.checked) {
      this.filteredOverConsumptionOptions.forEach((opt) => {
        if (!this.selectedOverConsumptions.includes(opt))
          this.selectedOverConsumptions.push(opt);
      });
    } else {
      this.selectedOverConsumptions = this.selectedOverConsumptions.filter(
        (opt) => !this.filteredOverConsumptionOptions.includes(opt),
      );
    }
  }
  clearAllOverCons() {
    this.selectedOverConsumptions = [];
  }
  toggleOverConsSelection(event: any, opt: string) {
    if (event.target.checked) {
      if (!this.selectedOverConsumptions.includes(opt))
        this.selectedOverConsumptions.push(opt);
    } else {
      this.selectedOverConsumptions = this.selectedOverConsumptions.filter(
        (o) => o !== opt,
      );
    }
  }

  isAllOperationsSelected() {
    return (
      this.filteredDbOperationOptions.length > 0 &&
      this.filteredDbOperationOptions.every((opt: string) =>
        this.selectedOperations.includes(opt),
      )
    );
  }
  toggleSelectAllOperations(event: any) {
    if (event.target.checked) {
      this.filteredDbOperationOptions.forEach((opt: string) => {
        if (!this.selectedOperations.includes(opt))
          this.selectedOperations.push(opt);
      });
    } else {
      this.selectedOperations = this.selectedOperations.filter(
        (opt: string) => !this.filteredDbOperationOptions.includes(opt),
      );
    }
  }
  clearAllOperations() {
    this.selectedOperations = [];
  }
  toggleOperationSelection(event: any, opt: string) {
    if (event.target.checked) {
      if (!this.selectedOperations.includes(opt))
        this.selectedOperations.push(opt);
    } else {
      this.selectedOperations = this.selectedOperations.filter(
        (o) => o !== opt,
      );
    }
  }

  isAllShipsSelected() {
    return (
      this.dbShipSuggestions.length > 0 &&
      this.dbShipSuggestions.every((ship) =>
        this.selectedShips.includes(ship.vessel_name),
      )
    );
  }
  toggleSelectAllShips(event: any) {
    if (event.target.checked) {
      this.dbShipSuggestions.forEach((ship) => {
        if (!this.selectedShips.includes(ship.vessel_name))
          this.selectedShips.push(ship.vessel_name);
      });
    } else {
      const suggestNames = this.dbShipSuggestions.map((s) => s.vessel_name);
      this.selectedShips = this.selectedShips.filter(
        (name) => !suggestNames.includes(name),
      );
    }
    this.selectedShipLabel = this.selectedShips.join(', ');
  }
  clearAllShips() {
    this.selectedShips = [];
    this.selectedShipLabel = 'Select Vessel';
  }

  isAllFilterPortsSelected() {
    return (
      this.dbPortSuggestions.length > 0 &&
      this.dbPortSuggestions.every((port) =>
        this.selectedFilterPort.includes(port),
      )
    );
  }
  toggleSelectAllFilterPorts(event: any) {
    if (event.target.checked) {
      this.dbPortSuggestions.forEach((port) => {
        if (!this.selectedFilterPort.includes(port))
          this.selectedFilterPort.push(port);
      });
    } else {
      this.selectedFilterPort = this.selectedFilterPort.filter(
        (port) => !this.dbPortSuggestions.includes(port),
      );
    }
    this.selectedFilterPortLabel = this.selectedFilterPort.join(', ');
  }
  clearAllFilterPorts() {
    this.selectedFilterPort = [];
    this.selectedFilterPortLabel = '';
  }

  constructor(
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private sharedDataService: DashboardService,
    private requestFormService: RequestFormService,
    private prequestFormService: PortRequestFormServiceService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private scLookerFilterCascade: ScLookerFilterCascadeService,
  ) {
    this.username = localStorage.getItem('username');
    this.filterForm = this.fb.group({
      clientStatus: ['Active'],
      from_date: [''],
      to_date: [''],
      overConsumption: [''],
      vesselName: [''],
      client: [''],
      segment: ['ALL'],
      port: [''],
      operation: [''],
      minCpOverall: [null],
      maxCpOverall: [null],
      minDesignOverall: [null],
      maxDesignOverall: [null],
    });

    this.pcBenchmarkForm = this.fb.group({
      ship_built_from: [''],
      ship_built_to: [''],
    });

    this.tcpBenchmarkForm = this.fb.group({});
    this.tcpOperationOptions = this.operations.map((op) => op.value);
    this.tcpMasterOperationOptions = [...this.tcpOperationOptions];
  }

  ngOnInit() {
    // Enable mat-sidenav-content as the page scrollport (same as fleet view)
    // so KPI cards + table header can stick on full-page scroll.
    document.body.classList.add(PortDatabaseComponent.BODY_SCROLL_CLASS);
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.graph = params['view'] === 'graph';
        this.hideToggle = params['source'] === 'graphical-view';

        //  OPTIONAL: SOURCE HANDLE (SAFE)
        const source = params['source'];
        if (source === 'pc') {
          console.log('Opened from PC Graph');
        }
        if (source === 'tcp') {
          console.log('Opened from TCP Graph');
        }

        if (this.graph) {
          this.ensureBenchmarkDataLoaded();
          this.loadBenchmarkCharts();
        }
      });
    // set default values only once when page opens
    this.selectedPcSegment = 'LR3/Suezmax';
    this.selectedPcOperation = 'Cargo Ops -Discharging';
    this.selectedVessels = [];
    // this.selectedPorts = ['La Goulette'];
    // this.selectedPcSegment = 'MR';
    // this.selectedPcOperation = 'discharging';
    this.clientName = 'ALL';
    if (!this.tcpMasterSegmentOptions.length) {
      this.mergeTcpSegmentOptions(['LR3/Suezmax']);
    }
    // PC Segment options come only from the Segment API (loadBenchmarkSegments /
    // loadPcSegmentsForSelectedClient). Do not seed from raw string tokens.

    window.addEventListener('expandChart', (e: any) => {
      const vessel = e.detail.vessel;
      const values = this.pcData
        .filter((d: any) => d.vessel_name === vessel)
        .map((d: any) => Number(d.operation_consumption) || 0);
      // this.openExpandChart(vessel, values);
    });

    this.prequestFormService.getPorts().subscribe((res: any) => {
      if (res.status === 'success') {
        this.portsRes = res.ports;
        const clientFilters: { [key: string]: (item: any) => boolean } = {
          'Eiger Shipping': (item) =>
            item.client &&
            (item.client === 'Northwell' ||
              item.client.toLowerCase() === 'eiger'),
          'Zeronorth ZN': (item) =>
            item.client && item.client.toLowerCase() === 'zeronorth',
          'Elite Shipping': (item) =>
            item.client &&
            (item.client.toLowerCase() === 'elite tankship dmcc' ||
              item.client.toLowerCase() === 'elite'),
          'Bunge Shipping': (item) =>
            item.client && item.client.toLowerCase() === 'bunge',
          'Alcor Shipping': (item) =>
            item.client.toLowerCase() === 'alcor chartering dmcc',
          'EMZoil Shipping': (item: any) =>
            item.client &&
            (item.client.toLowerCase() === 'emz oil' ||
              item.client.toLowerCase() === 'emzoil fzco'),
          'Ethiopian Shipping & Logistics': (item) =>
            item.client.toLowerCase() === 'ethiopian shipping & logistics',
        };
        if (this.username === 'Meraki - Operations') {
          this.portsRes = this.portsRes;
        } else if (clientFilters[this.username]) {
          this.portsRes = this.portsRes.filter(clientFilters[this.username]);
        }
        // Ports logic stays the same
        if (this.portsRes) {
          this.ports = this.portsRes
            .map((p: any) => p.port?.trim())
            .filter((p: string) => p)
            .map((p: string) => this.formatPortName(p));
          this.ports = [...new Set(this.ports)];
          this.selectedPorts = this.ports.includes('La Goulette')
            ? ['La Goulette']
            : [this.ports[0]];
          this.updatePortLabel();
        }

        if (res.vessels) {
          // Convert everything to lowercase once for matching
          const allVessels = res.vessels.map((v: any) => ({
            name: v.vessel_name,
            segment: v.segment?.trim() || null,
            client: v.client?.toLowerCase().trim(),
          }));

          const clientFilters: { [key: string]: (item: any) => boolean } = {
            'Eiger Shipping': (item) =>
              item.client &&
              (item.client === 'Northwell' ||
                item.client.toLowerCase() === 'eiger'),
            'Zeronorth ZN': (item) =>
              item.client && item.client.toLowerCase() === 'zeronorth',
            'Elite Shipping': (item) =>
              item.client &&
              (item.client.toLowerCase() === 'elite tankship dmcc' ||
                item.client.toLowerCase() === 'elite'),
            'Bunge Shipping': (item) =>
              item.client && item.client.toLowerCase() === 'bunge',
            'Alcor Shipping': (item) =>
              item.client &&
              (item.client.toLowerCase() === 'alcor chartering dmcc' ||
                item.client.toLowerCase() === 'alcor chartering'),
            'EMZoil Shipping': (item: any) =>
              item.client &&
              (item.client.toLowerCase() === 'emz oil' ||
                item.client.toLowerCase() === 'emzoil fzco'),
            'Ethiopian Shipping & Logistics': (item) =>
              item.client.toLowerCase() === 'ethiopian shipping & logistics',
          };

          let filtered;

          if (this.username === 'Meraki - Operations') {
            filtered = allVessels; // show everything
          } else if (clientFilters[this.username]) {
            filtered = allVessels.filter(clientFilters[this.username]);
          } else {
            filtered = []; // no match
          }

          // These are the final vessels for UI
          this.vessels = filtered.map((v: any) => ({
            name: v.name,
            segment: v.segment,
            client: v.client,
          }));
          this.syncPcVesselCatalogFromPorts();
          const clientsFromVessels = Array.from(
            new Set(
              filtered
                .map((v: any) => v.client)
                .filter(Boolean)
                .map((c: string) => this.toProperCase(c)),
            ),
          ) as string[];
          this.mergePcClientOptions(clientsFromVessels);
          this.segments = [
            ...new Set(
              filtered
                .map((v: any) => v.segment?.trim().toUpperCase())
                .filter((s: string) => s),
            ),
          ];
          // Do not merge vessel catalog segments into PC Benchmarking dropdown.
          // Dropdown options come only from the Segment API key/value response.
        }

        // Initially filtered vessels by default segment
        this.filterVesselsBySegment();
      }

      if (this.graph) {
        this.logBench('getPorts complete — loading charts');
        this.loadBenchmarkCharts();
      }
    });

    this.loadData();
    if (this.graph) {
      this.ensureBenchmarkDataLoaded();
    }
    this.loadSegments();
    this.setupFilterCascade();
    this.setupLiveFilterUpdates();
    this.syncSegmentSelectionFromForm();
    this.loadFilterOptions();

    setTimeout(() => {
      if (this.graph) {
        this.logBench('init safety-net — loading charts');
        this.loadBenchmarkCharts();
      }
    }, 1500);

    this.requestFormService.themeChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.graph) {
          return;
        }
        if (this.pcData?.length) {
          this.buildPcChartFromData();
        }
        if (this.tcpData?.length) {
          const sortedData = [...this.tcpData].sort((a: any, b: any) =>
            (a.vessel_name || '').localeCompare(b.vessel_name || ''),
          );
          this.applyTcpChartFromData(sortedData);
        }
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    document.body.classList.remove(PortDatabaseComponent.BODY_SCROLL_CLASS);
    this.statsResizeObserver?.disconnect();
    this.statsResizeObserver = null;
    this.destroy$.next();
    this.destroy$.complete();
    this.closePortDropdown();
  }

  /** Align sticky table header with live KPI card stack height. */
  private bindPageStickyOffset(el: HTMLElement | null): void {
    this.statsResizeObserver?.disconnect();
    this.statsResizeObserver = null;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    const update = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      const next = Math.max(height, 120);
      if (next !== this.pageStickyOffsetPx) {
        this.pageStickyOffsetPx = next;
        this.cdr.markForCheck();
      }
    };
    update();
    this.statsResizeObserver = new ResizeObserver(() => update());
    this.statsResizeObserver.observe(el);
  }

  closeFilterDrawer(): void {
    this.isFilterOpen = false;
    this.resetMultiSelectFilterUi();
  }

  isClientFilterEnabled(): boolean {
    return this.getSelectedClientStatuses().length > 0;
  }

  hasClientSelected(): boolean {
    return this.selectedClients.length > 0;
  }

  areDownstreamFiltersEnabled(): boolean {
    // Client Status unlocks cascading filters; options come from filter-options.
    return this.isClientFilterEnabled();
  }

  isClientFieldBlocked(): boolean {
    return !this.isClientFilterEnabled();
  }

  isDownstreamFieldBlocked(): boolean {
    return !this.areDownstreamFiltersEnabled();
  }

  /** Selected Client Status values (Active / Inactive), normalized. */
  private getSelectedClientStatuses(): string[] {
    const allowed = new Set(
      (this.clientStatusOptions || []).map((s) =>
        String(s).trim().toLowerCase(),
      ),
    );
    return (this.selectedClientStatuses || [])
      .map((s) => String(s ?? '').trim())
      .filter((s) => s && allowed.has(s.toLowerCase()));
  }

  /** Payload status: single selection only; both Active+Inactive → "". */
  private resolveClientStatusForPayload(): string {
    const statuses = this.getSelectedClientStatuses();
    if (statuses.length === 1) {
      return normalizeClientStatusValue(statuses[0]);
    }
    return '';
  }

  onClientStatusesChanged(statuses: string[]): void {
    this.selectedClientStatuses = statuses || [];
    this.suppressFilterCascade = true;
    this.filterForm.patchValue(
      {
        clientStatus: this.selectedClientStatuses[0] || '',
      },
      { emitEvent: false },
    );
    this.suppressFilterCascade = false;
    this.onClientStatusChanged();
  }

  onClientsChanged(clients: string[]): void {
    this.selectedClients = clients || [];
    if (this.suppressFilterCascade) {
      return;
    }
    this.clearDownstreamFrom('client');
    if (this.username === 'Meraki - Operations') {
      this.requestFilterOptions(true);
    } else {
      this.rebuildTableFilterCascadeOptions();
    }
    this.scheduleLiveTableFilter();
  }

  onVesselsChanged(vessels: string[]): void {
    this.selectedShips = vessels || [];
    if (this.suppressFilterCascade) {
      return;
    }
    this.clearDownstreamFrom('vessel');
    if (this.shouldUseFilterOptionsApi()) {
      this.requestFilterOptions();
    } else {
      this.rebuildTableFilterCascadeOptions();
    }
    this.scheduleLiveTableFilter();
  }

  onSegmentsChanged(segments: string[]): void {
    this.selectedSegments = segments || [];
    this.suppressFilterCascade = true;
    // Keep legacy form control in sync (single sentinel when multi / empty).
    const formSegment =
      this.selectedSegments.length === 1 ? this.selectedSegments[0] : 'ALL';
    this.filterForm.patchValue({ segment: formSegment }, { emitEvent: false });
    this.suppressFilterCascade = false;

    this.clearDownstreamFrom('segment');
    if (this.shouldUseFilterOptionsApi()) {
      this.requestFilterOptions();
    } else {
      this.rebuildTableFilterCascadeOptions();
    }
    this.scheduleLiveTableFilter();
  }

  /** No-op kept for callers — multi-select owns `selectedSegments` directly. */
  private syncSegmentSelectionFromForm(): void {
    // Intentionally empty: Segment is driven by `selectedSegments` multi-select.
  }

  /** Keep multi-select arrays aligned before Apply / local filtering. */
  private syncTableFilterStateFromUi(): void {
    // Segment + Client are multi-select arrays; nothing to sync from single form fields.
  }

  private resolveSegmentSelection(_formValues?: any): string[] {
    return [...(this.selectedSegments || [])];
  }

  /** Cascade payload string: empty when All / none / multi; otherwise the single pick. */
  private resolveSegmentForFilterPayload(): string {
    const selected = this.sanitizeFilterOptionValues(this.selectedSegments);
    const options = this.sanitizeFilterOptionValues(
      this.segmentMultiSelectOptions,
    );
    if (!selected.length || isAllFilterOptionsSelected(selected, options)) {
      return '';
    }
    if (selected.length === 1) {
      // Find the original key in selectedSegments to lookup the value
      const originalKey = this.selectedSegments.find(
        (s) => this.sanitizeFilterOptionValues([s])[0] === selected[0]
      );
      if (originalKey && this.segmentApiValueMap[originalKey]) {
        return this.segmentApiValueMap[originalKey];
      }
      return selected[0];
    }
    // Multiple concrete segments — cascade API only accepts one string; local filter applies all.
    return '';
  }

  private resolveRecordClient(item: any): string {
    return String(item?.client ?? item?.client_name ?? '').trim();
  }

  onPortRolesChanged(roles: string[]): void {
    this.selectedPortRoles = roles || [];
    if (this.suppressFilterCascade) {
      return;
    }
    if (this.shouldUseFilterOptionsApi()) {
      this.requestFilterOptions();
    }
    this.scheduleLiveTableFilter();
  }

  onPortsChanged(ports: string[]): void {
    this.selectedFilterPort = ports || [];
    this.syncTableFilterPortsToBenchmark(this.selectedFilterPort);
    if (this.suppressFilterCascade) {
      return;
    }
    if (this.shouldUseFilterOptionsApi()) {
      this.requestFilterOptions();
    } else {
      this.rebuildTableFilterCascadeOptions();
    }
    this.scheduleLiveTableFilter();
  }

  private syncTableFilterPortsToBenchmark(ports: string[]): void {
    this.selectedPorts = [...(ports || [])];
    this.selectedFilterPortLabel = this.selectedFilterPort.join(', ');
    this.updatePortLabel();
  }

  private syncBenchmarkPortsToTableFilter(ports: string[]): void {
    this.selectedFilterPort = [...(ports || [])];
    this.selectedFilterPortLabel = this.selectedFilterPort.join(', ');
  }

  onOperationsChanged(operations: string[]): void {
    this.selectedOperations = operations || [];
    if (this.suppressFilterCascade) {
      return;
    }
    this.clearDownstreamFrom('operations');
    if (this.shouldUseFilterOptionsApi()) {
      this.requestFilterOptions();
    } else {
      this.rebuildTableFilterCascadeOptions();
    }
    this.scheduleLiveTableFilter();
  }

  onVoyageStatusesChanged(statuses: string[]): void {
    this.selectedVoyageStatuses = statuses || [];
    if (this.suppressFilterCascade) {
      return;
    }
    if (this.shouldUseFilterOptionsApi()) {
      this.requestFilterOptions();
    }
    this.scheduleLiveTableFilter();
  }

  onOverConsumptionsChanged(selections: string[]): void {
    this.selectedOverConsumptions = selections || [];
    if (this.suppressFilterCascade) {
      return;
    }
    if (this.shouldUseFilterOptionsApi()) {
      this.requestFilterOptions();
    }
    this.scheduleLiveTableFilter();
  }

  private setupFilterCascade(): void {
    this.filterForm
      .get('segment')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.suppressFilterCascade) {
          return;
        }
        this.syncSegmentSelectionFromForm();
        this.clearDownstreamFrom('segment');
        if (this.shouldUseFilterOptionsApi()) {
          this.requestFilterOptions();
        } else {
          this.rebuildTableFilterCascadeOptions();
        }
        this.scheduleLiveTableFilter();
      });

    this.filterForm
      .get('from_date')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.suppressFilterCascade) {
          return;
        }
        if (this.shouldUseFilterOptionsApi()) {
          this.requestFilterOptions();
        }
        this.scheduleLiveTableFilter();
      });

    this.filterForm
      .get('to_date')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.suppressFilterCascade) {
          return;
        }
        if (this.shouldUseFilterOptionsApi()) {
          this.requestFilterOptions();
        }
        this.scheduleLiveTableFilter();
      });

    (
      [
        'minCpOverall',
        'maxCpOverall',
        'minDesignOverall',
        'maxDesignOverall',
      ] as const
    ).forEach((controlName) => {
      this.filterForm
        .get(controlName)
        ?.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe(() => {
          if (this.suppressFilterCascade) {
            return;
          }
          this.scheduleLiveTableFilter();
        });
    });
  }

  private setupLiveFilterUpdates(): void {
    this.tableFilterApply$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!Array.isArray(this.elements) || !this.elements.length) {
          return;
        }
        this.syncTableFilterStateFromUi();
        this.applyLocalFiltering(this.filterForm.getRawValue(), {
          notifyWhenEmpty: false,
        });
        this.cdr.markForCheck();
      });

    this.filterOptionsReload$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(() => {
        this.executeFilterOptionsRequest();
      });

    // PC filter changes redraw ONLY the PC chart (pc_bench_marking).
    this.benchmarkChartUpdate$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.graph) {
          return;
        }
        this.ensureValidPcOperationSelection();
        this.createChart();
        this.cdr.markForCheck();
      });

    // TCP filter changes redraw ONLY the TCP chart (tcp_bench_marking).
    this.tcpChartUpdate$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.graph) {
          return;
        }
        this.createTcpChart();
        this.cdr.markForCheck();
      });
  }

  private scheduleLiveTableFilter(): void {
    // Table filtering now only executes when the user clicks 'Apply Filter' in the drawer
  }

  /** Schedule a PC-only chart refresh (never triggers the TCP chart/API). */
  private scheduleBenchmarkChartUpdate(): void {
    this.benchmarkChartUpdate$.next();
  }

  /** Schedule a TCP-only chart refresh (never triggers the PC chart/API). */
  private scheduleTcpChartUpdate(): void {
    this.tcpChartUpdate$.next();
  }

  private getTableFilterCascadeState(): TableFilterCascadeState {
    return {
      selectedClients: [...this.selectedClients],
      selectedSegments: [...this.selectedSegments],
      selectedOperations: [...this.selectedOperations],
      selectedShips: [...this.selectedShips],
      selectedPorts: [...this.selectedFilterPort],
    };
  }

  private rebuildTableFilterCascadeOptions(): void {
    const records = Array.isArray(this.elements) ? this.elements : [];
    if (!records.length) {
      return;
    }

    const state = this.getTableFilterCascadeState();
    const options = buildTableFilterCascadeOptions(records, state);
    const pruned = pruneTableFilterSelections(state, options);

    this.suppressFilterCascade = true;
    if (this.username === 'Meraki - Operations') {
      // Client options come from the Looker API list — do not prune picks against table rows only.
      this.selectedClients = [...state.selectedClients];
    } else {
      this.selectedClients = pruned.selectedClients;
    }
    this.selectedSegments = pruned.selectedSegments;
    this.selectedOperations = pruned.selectedOperations;
    this.selectedShips = pruned.selectedShips;
    this.selectedFilterPort = pruned.selectedPorts;
    this.suppressFilterCascade = false;

    if (this.username === 'Meraki - Operations') {
      this.apiClientOptions = dedupeFilterOptions([
        ...this.apiClientOptions,
        ...options.clientOptions,
      ]);
    } else {
      this.clientOptions = options.clientOptions;
    }

    this.segmentOptions = options.segmentOptions;
    this.tableFilterOperationOptions = options.operationOptions;
    this.vesselNameOptions = options.vesselOptions;
    this.tableFilterPortOptions = options.portOptions;
    this.clientScopedPorts = options.portOptions;
    this.clientScopedOperations = options.operationOptions;

    this.cdr.markForCheck();
  }

  onClientStatusChanged(): void {
    this.clearDownstreamFrom('clientStatus');
    this.lastFilterOptionsPayloadKey = '';
    this.loadFilterOptions();
    this.scheduleLiveTableFilter();
  }

  private clearDownstreamFrom(
    level:
      | 'clientStatus'
      | 'client'
      | 'segment'
      | 'operations'
      | 'vessel'
      | 'port',
  ): void {
    this.suppressFilterCascade = true;

    if (level === 'clientStatus' || level === 'client') {
      if (level === 'clientStatus') {
        this.selectedClients = [];
        this.apiClientOptions = [];
        this.clearApiDrivenFilterOptions();
      } else {
        this.clearApiDrivenFilterOptions();
      }
      this.filterForm.patchValue({ segment: 'ALL' }, { emitEvent: false });
      this.selectedSegments = [];
      this.selectedOperations = [];
      this.selectedShips = [];
      this.selectedFilterPort = [];
      this.selectedVoyageStatuses = [];
      this.selectedOverConsumptions = [];
      this.vesselNameOptions = [];
      this.tableFilterOperationOptions = [];
      this.tableFilterPortOptions = [];
    } else if (level === 'segment') {
      this.selectedOperations = [];
      this.selectedShips = [];
      this.selectedFilterPort = [];
    } else if (level === 'operations') {
      this.selectedShips = [];
      this.selectedFilterPort = [];
    } else if (level === 'vessel') {
      this.selectedFilterPort = [];
    }

    this.suppressFilterCascade = false;
  }

  loadFilterOptions(): void {
    if (this.username !== 'Meraki - Operations') {
      this.rebuildTableFilterCascadeOptions();
      return;
    }
    this.requestFilterOptions(true);
  }

  private shouldUseFilterOptionsApi(): boolean {
    return this.username === 'Meraki - Operations';
  }

  private sanitizeFilterOptionValues(values: string[]): string[] {
    return (values || []).filter(
      (value) =>
        !!String(value ?? '').trim() &&
        String(value).trim() !== 'No options available',
    );
  }

  private buildPortConsumptionFilterOptionsPayload(): PortConsumptionFilterOptionsPayload {
    this.syncTableFilterStateFromUi();

    return {
      username: String(this.username || ''),
      client_status: this.resolveClientStatusForPayload(),
      // Select All → null; empty → []; partial → selected clients.
      client: resolveClientFilterPayload(
        this.selectedClients,
        this.apiClientOptions,
      ),
      segment: this.resolveSegmentForFilterPayload(),
      vessel_names: resolveMultiSelectFilterPayload(
        this.selectedShips,
        this.shipFilterOptions,
      ),
      port_types: resolveMultiSelectFilterPayload(
        this.selectedPortRoles,
        this.portTypeFilterOptions,
      ),
      port_names: resolveMultiSelectFilterPayload(
        this.selectedFilterPort,
        this.portFilterOptions,
      ),
      operations: resolveOperationsFilterPayload(
        this.selectedOperations,
        this.operationFilterOptions,
      ),
      voyage_statuses: resolveMultiSelectFilterPayload(
        this.selectedVoyageStatuses,
        this.voyageStatusFilterOptions,
      ),
      from_date: formatFilterOptionsDate(
        this.filterForm.get('from_date')?.value,
      ),
      to_date: formatFilterOptionsDate(this.filterForm.get('to_date')?.value),
      over_consumption: resolveMultiSelectFilterPayload(
        this.selectedOverConsumptions,
        this.overConsumptionOptions,
      ),
    };
  }

  private requestFilterOptions(immediate = false): void {
    if (this.username !== 'Meraki - Operations') {
      this.rebuildTableFilterCascadeOptions();
      return;
    }
    if (immediate) {
      this.executeFilterOptionsRequest();
      return;
    }
    this.filterOptionsReload$.next();
  }

  private executeFilterOptionsRequest(): void {
    if (this.username !== 'Meraki - Operations') {
      return;
    }

    if (!this.getSelectedClientStatuses().length) {
      this.apiClientOptions = [];
      this.selectedClients = [];
      this.clearApiDrivenFilterOptions();
      this.isLoadingFilterOptions = false;
      this.isLoadingClientFilterOptions = false;
      this.cdr.markForCheck();
      return;
    }

    const payload = this.buildPortConsumptionFilterOptionsPayload();
    const payloadKey = JSON.stringify(payload);
    if (
      payloadKey === this.lastFilterOptionsPayloadKey &&
      this.filterOptionsRequestSeq > 0
    ) {
      return;
    }
    this.lastFilterOptionsPayloadKey = payloadKey;

    const requestSeq = ++this.filterOptionsRequestSeq;
    this.isLoadingFilterOptions = true;
    this.isLoadingClientFilterOptions = true;

    this.prequestFormService
      .getPortConsumptionFilterOptions(
        payload as unknown as Record<string, unknown>,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (requestSeq !== this.filterOptionsRequestSeq) {
            return;
          }
          const normalized = normalizeFilterApiOptions(response);
          // Only refresh Client dropdown when payload had no client constraint.
          // Cascading with a selected client returns a narrowed `client` list
          // (often 1 item) — never let that wipe the full multi-select options.
          const refreshClients =
            payload.client === null ||
            (Array.isArray(payload.client) && payload.client.length === 0);
          this.applyApiFilterOptions(normalized, {
            updateClients: refreshClients,
          });
          this.isLoadingFilterOptions = false;
          this.isLoadingClientFilterOptions = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          if (requestSeq !== this.filterOptionsRequestSeq) {
            return;
          }
          console.error('Failed to load port consumption filter options', err);
          this.clearDependentApiOptionsKeepSelections();
          this.isLoadingFilterOptions = false;
          this.isLoadingClientFilterOptions = false;
          this.cdr.markForCheck();
        },
      });
  }

  private applyApiFilterOptions(
    options: ReturnType<typeof normalizeFilterApiOptions>,
    config: { updateClients?: boolean } = {},
  ): void {
    this.suppressFilterCascade = true;

    if (config.updateClients !== false && options.clients.length) {
      // First normalize actual API clients
      const normalizedClients = normalizePortDatabaseClients(
        options.clients
      );
      // Mask only for demo login
      if (this.subusername?.toLowerCase() === 'demo') {
        const clientMap = new Map<string, string>();
        let counter = 1;

        this.apiClientOptions = normalizedClients.map((client: any) => {
          const actualClient =
            typeof client === 'string'
              ? client
              : client?.label ??
              client?.name ??
              client?.client ??
              '';

          const key = actualClient.trim().toLowerCase();

          if (!clientMap.has(key)) {
            clientMap.set(key, `Client ${counter++}`);
          }

          if (typeof client === 'string') {
            return clientMap.get(key)!;
          }

          return {
            ...client,
            label: clientMap.get(key),
            original_client: actualClient,
          };
        });
      } else {
        // Normal users see actual client names
        this.apiClientOptions = normalizedClients;
      }
      this.selectedClients = pruneSelectionsToOptions(
        this.selectedClients,
        this.apiClientOptions,
      );
    }

    // Bind every dependent dropdown from filter-options (not the /clients API).
    this.apiSegmentOptions = options.segments;
    this.vesselNameOptions = options.shipNames;
    this.apiPortTypeOptions = options.portTypes;
    this.tableFilterPortOptions = options.portNames;
    this.tableFilterOperationOptions = options.operations;
    this.apiVoyageStatusOptions = options.voyageStatuses;
    this.overConsumptionOptions = options.overConsumption.length
      ? options.overConsumption
      : ['Yes', 'No', 'CP Overall', 'Design Overall'];

    this.selectedOperations = pruneSelectionsToOptions(
      this.selectedOperations,
      options.operations,
    );
    this.selectedShips = pruneSelectionsToOptions(
      this.selectedShips,
      options.shipNames,
    );
    this.selectedFilterPort = pruneSelectionsToOptions(
      this.selectedFilterPort,
      options.portNames,
    );
    this.selectedPortRoles = pruneSelectionsToOptions(
      this.selectedPortRoles,
      options.portTypes,
    );
    this.selectedVoyageStatuses = pruneSelectionsToOptions(
      this.selectedVoyageStatuses,
      options.voyageStatuses,
    );
    this.selectedOverConsumptions = pruneSelectionsToOptions(
      this.selectedOverConsumptions,
      this.overConsumptionOptions,
    );

    this.selectedSegments = pruneSegmentSelectionsToOptions(
      this.selectedSegments,
      this.segmentMultiSelectOptions,
    );
    const formSegment =
      this.selectedSegments.length === 1 ? this.selectedSegments[0] : 'ALL';
    this.filterForm.patchValue({ segment: formSegment }, { emitEvent: false });

    this.clientScopedPorts = [...this.tableFilterPortOptions];
    this.clientScopedOperations = [...this.tableFilterOperationOptions];
    this.suppressFilterCascade = false;
    this.cdr.markForCheck();
    this.scheduleLiveTableFilter();
  }

  private clearDependentApiOptionsKeepSelections(): void {
    this.apiPortTypeOptions = [];
    this.apiSegmentOptions = [];
    this.apiVoyageStatusOptions = [];
    this.tableFilterOperationOptions = [];
    this.tableFilterPortOptions = [];
    this.vesselNameOptions = [];
    this.clientScopedPorts = [];
    this.clientScopedOperations = [];
    this.overConsumptionOptions = ['CP Overall', 'Design Overall'];
  }

  private clearApiDrivenFilterOptions(): void {
    this.apiPortTypeOptions = [];
    this.apiSegmentOptions = [];
    this.apiVoyageStatusOptions = [];
    this.tableFilterOperationOptions = [];
    this.tableFilterPortOptions = [];
    this.vesselNameOptions = [];
    this.clientScopedPorts = [];
    this.clientScopedOperations = [];
    this.selectedOperations = [];
    this.selectedShips = [];
    this.selectedFilterPort = [];
    this.selectedPortRoles = [];
    this.selectedVoyageStatuses = [];
    this.selectedOverConsumptions = [];
    this.overConsumptionOptions = ['CP Overall', 'Design Overall'];
    this.filterForm.patchValue({ segment: 'ALL' }, { emitEvent: false });
    this.selectedSegments = [];
  }

  private applyFilterOptionsFromResponse(options: {
    clientStatusOptions: string[];
    clientOptions: string[];
    segmentOptions: string[];
    vesselNameOptions: string[];
  }): void {
    if (options.clientStatusOptions.length) {
      this.clientStatusOptions = options.clientStatusOptions;
    }
    this.syncClientStatusSelection();

    if (this.isClientFilterEnabled() && options.clientOptions.length) {
      this.apiClientOptions = extractClientOptionsFromRecords([
        ...options.clientOptions.map((client) => ({ client })),
        ...(this.elements || []),
      ]);
    }

    if (this.hasClientSelected()) {
      this.pruneInvalidFilterSelections();
    }

    this.rebuildTableFilterCascadeOptions();
  }

  private shouldScopeFilterOptionsToClient(): boolean {
    return this.username === 'Meraki - Operations' && this.hasClientSelected();
  }

  private getRecordsForClientScope(): any[] {
    const records = this.elements || [];
    if (!this.shouldScopeFilterOptionsToClient()) {
      return records;
    }
    return records.filter((item: any) =>
      matchesAnyClient(item.client, this.selectedClients),
    );
  }

  private extractPortsFromRecords(records: any[]): string[] {
    const uniquePorts = new Map<string, string>();
    records
      .map((item: any) => item.port)
      .filter(Boolean)
      .forEach((port: string) => {
        const lower = port.toLowerCase().trim();
        if (!uniquePorts.has(lower)) {
          uniquePorts.set(lower, port.trim());
        }
      });
    return Array.from(uniquePorts.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }

  private extractOperationsFromRecords(records: any[]): string[] {
    return normalizeOperationsFilterOptions(
      dedupeFilterOptions(
        (records || []).map((item: any) => item.operations).filter(Boolean),
      ),
    );
  }

  private refreshClientDependentFilterOptions(): void {
    this.rebuildTableFilterCascadeOptions();
  }

  private syncClientStatusSelection(): void {
    const options = this.clientStatusOptions || [];
    if (!options.length) {
      this.selectedClientStatuses = [];
      return;
    }

    const allowed = new Set(
      options.map((option) => String(option).trim().toLowerCase()),
    );
    const kept = (this.selectedClientStatuses || []).filter((status) =>
      allowed.has(String(status).trim().toLowerCase()),
    );

    if (kept.length) {
      this.selectedClientStatuses = kept;
    } else {
      const preferred =
        options.find(
          (option) => String(option).trim().toLowerCase() === 'active',
        ) ?? options[0];
      this.selectedClientStatuses = preferred ? [preferred] : [];
    }

    this.suppressFilterCascade = true;
    this.filterForm.patchValue(
      { clientStatus: this.selectedClientStatuses[0] || '' },
      { emitEvent: false },
    );
    this.suppressFilterCascade = false;
  }

  private mergeVesselNameOptions(apiNames: string[]): string[] {
    const merged = new Set<string>();
    apiNames.forEach((name) => {
      const normalized = String(name).trim().toUpperCase();
      if (normalized) {
        merged.add(normalized);
      }
    });
    this.selectedShips.forEach((name) => {
      const normalized = String(name).trim().toUpperCase();
      if (normalized) {
        merged.add(normalized);
      }
    });
    return Array.from(merged).sort((a, b) => a.localeCompare(b));
  }

  private pruneInvalidFilterSelections(): void {
    this.rebuildTableFilterCascadeOptions();
  }

  private matchesPortRoleFilter(item: any): boolean {
    if (!this.selectedPortRoles.length) {
      return true;
    }

    const operation = String(
      item?.operations ?? item?.operation ?? '',
    ).toLowerCase();
    const port = String(item?.port ?? '').trim();
    if (!port) {
      return false;
    }

    return this.selectedPortRoles.some((role) => {
      if (role === 'Load Port') {
        return operation.includes('load');
      }
      if (role === 'Discharge Port') {
        return (
          operation.includes('discharg') || operation.includes('discharge')
        );
      }
      if (role === 'Via Points') {
        return operation.includes('bunker') || operation.includes('transit');
      }
      return false;
    });
  }

  private closePortDropdown(): void {
    this.portDropdownOpen = false;
  }

  /** Dev-only benchmark pipeline logging */
  private logBench(step: string, detail?: unknown): void {
    if (!this.benchDebug) return;
    if (detail !== undefined) {
      console.log(`[Benchmark] ${step}`, detail);
    } else {
      console.log(`[Benchmark] ${step}`);
    }
  }

  private logFilterOptions(label: string, options: unknown): void {
    console.log(label, options);
    this.cdr.markForCheck();
  }

  private populateTcpFilterOptionsFromRows(rows: any[], root?: any): void {
    if (!rows?.length) {
      return;
    }

    this.mergeTcpBenchmarkMasterPool(rows);
    this.buildTcpVesselCatalogFromRows(this.getTcpCascadingPool());

    const opts = extractTcpBenchmarkFilterOptions(rows, root);
    this.tcpClientOptions = [...opts.clients];
    this.tcpVesselNameOptions = [...opts.vessels];
    this.tcpYardOptions = [...opts.yards];
    if (opts.operations.length) {
      this.mergeTcpOperationOptions(opts.operations);
    }
    if (opts.segments.length) {
      this.mergeTcpSegmentOptions(opts.segments);
    }
    const rowPorts = dedupeFilterOptions(
      rows
        .map((record) => String(record?.port_name || record?.port || '').trim())
        .filter(Boolean),
    );
    if (rowPorts.length) {
      this.mergeTcpMasterPortOptions(rowPorts);
    }
    const rootPorts = this.coerceTcpRootPortList(
      root?.['port_name'] ?? root?.['ports'],
    );
    if (rootPorts.length) {
      this.mergeTcpMasterPortOptions(rootPorts);
    }
    this.ensureTcpOperationOptions();
    if (
      this.tcpOperationOptions.length &&
      !this.tcpOperationOptions.includes(this.selectedOperation)
    ) {
      this.selectedOperation = this.tcpOperationOptions[0];
    }

    this.tcpBuiltDateMin = opts.builtDateMin;
    this.tcpBuiltDateMax = opts.builtDateMax;

    this.cdr.markForCheck();
    this.logFilterOptions('TCP Response', {
      rows: rows.length,
      total_record: root?.total_record,
    });
    this.logFilterOptions('Client Options', this.tcpClientOptions);
    this.logFilterOptions('Vessel Options', this.tcpVesselNameOptions);
    this.logFilterOptions(
      'Segment Options',
      this.benchmarkSegments.map((s) => s.key),
    );
    this.logFilterOptions('Operation Options', this.tcpOperationOptions);
    this.logFilterOptions('Yard Options', this.tcpYardOptions);
    this.rebuildTcpCascadingOptions();
  }

  private applyTcpClientFilter(rows: any[]): any[] {
    if (this.isAllTcpClientsSelected()) {
      return rows;
    }
    return rows.filter((record) =>
      matchesAnyClient(record?.client, this.selectedTcpClients),
    );
  }

  private applyTcpVesselFilter(rows: any[]): any[] {
    if (
      !this.selectedTcpVesselNames?.length ||
      this.isAllTcpVesselsSelected()
    ) {
      return rows;
    }
    return rows.filter((record) =>
      matchesSelectedVesselNames(
        record?.vessel_name,
        this.selectedTcpVesselNames,
      ),
    );
  }

  private applyTcpSegmentFilter(rows: any[]): any[] {
    if (!this.selectedSegment) {
      return rows;
    }
    const segmentValues = this.getTcpSegmentFilterValues();
    return rows.filter((record) =>
      matchesAnySegment(
        record?.vessel_class || record?.segment || '',
        segmentValues.length ? segmentValues : [this.selectedSegment],
      ),
    );
  }

  private applyTcpOperationFilter(rows: any[]): any[] {
    if (!this.selectedOperation) {
      return rows;
    }
    return rows.filter(
      (record) =>
        !record?.port_consumption_parameter ||
        record.port_consumption_parameter === this.selectedOperation,
    );
  }

  private applyTcpShipBuiltFilter(rows: any[]): any[] {
    const { ship_built_from, ship_built_to } = this.tcpBenchmarkForm.value;
    if (!ship_built_from && !ship_built_to) {
      return rows;
    }
    return rows.filter((record) =>
      this.matchesTcpShipBuiltRange(record, ship_built_from, ship_built_to),
    );
  }

  private applyTcpBuiltAgeFilter(rows: any[]): any[] {
    if (this.isAllTcpBuiltAgesSelected()) {
      return rows;
    }
    const selectedAges = new Set(this.selectedTcpBuiltAges.map(String));
    return rows.filter((record) => {
      const age = getVesselAgeYearsFromBuildDate(
        extractRecordBuildYear(record),
      );
      if (age === null) {
        return false;
      }
      return selectedAges.has(String(age));
    });
  }

  /** Cascade: Client → Segment → Operations → Vessel → Port → Yard → Age → chart. */
  private getTcpPoolScoped(
    through:
      | 'client'
      | 'segment'
      | 'operations'
      | 'vessel'
      | 'port'
      | 'yard'
      | 'age'
      | 'chart' = 'chart',
  ): any[] {
    let scoped = this.applyTcpClientFilter([...this.getTcpCascadingPool()]);
    if (through === 'client') {
      return scoped;
    }

    const segmentOptionRows = scoped;
    scoped = this.applyTcpSegmentFilter(scoped);
    if (through === 'segment') {
      return segmentOptionRows;
    }

    const operationOptionRows = scoped;
    scoped = this.applyTcpOperationFilter(scoped);
    if (through === 'operations') {
      return operationOptionRows;
    }

    const vesselOptionRows = scoped;
    scoped = this.applyTcpVesselFilter(scoped);
    if (through === 'vessel') {
      return vesselOptionRows;
    }

    const portOptionRows = scoped;
    scoped = this.applyTcpPortFilter(scoped);
    if (through === 'port') {
      return portOptionRows;
    }

    const yardOptionRows = scoped;
    scoped = this.applyTcpYardFilter(scoped);
    if (through === 'yard') {
      return yardOptionRows;
    }

    const ageOptionRows = scoped;
    scoped = this.applyTcpBuiltAgeFilter(scoped);
    if (through === 'age') {
      return ageOptionRows;
    }

    scoped = this.applyTcpShipBuiltFilter(scoped);
    return scoped;
  }

  private applyPcClientFilter(rows: any[]): any[] {
    if (this.isAllPcClientsSelected()) {
      return rows;
    }
    return rows.filter((record) =>
      matchesAnyClient(record?.client, this.selectedPcClients),
    );
  }

  private applyPcVesselFilter(rows: any[]): any[] {
    if (!this.selectedVessels?.length || this.isAllPcVesselsSelected()) {
      return rows;
    }
    return rows.filter((record) =>
      this.selectedVessels.some(
        (name) =>
          name.toLowerCase() ===
          String(record?.vessel_name || '')
            .trim()
            .toLowerCase(),
      ),
    );
  }

  private applyPcSegmentFilter(rows: any[]): any[] {
    if (!this.selectedPcSegment) {
      return rows;
    }
    const segmentValues = this.getPcSegmentFilterValues();
    return rows.filter((record) =>
      matchesAnySegment(
        record?.vessel_class || record?.segment || '',
        segmentValues.length ? segmentValues : [this.selectedPcSegment],
      ),
    );
  }

  private applyPcOperationFilter(rows: any[]): any[] {
    if (!this.selectedPcOperation) {
      return rows;
    }
    const op = String(this.selectedPcOperation).toLowerCase();
    return rows.filter((record) => {
      const recordOp = String(
        record?.operation || record?.operations || '',
      ).toLowerCase();
      return !recordOp || recordOp === op;
    });
  }

  private extractTcpClientsFromPool(): string[] {
    return this.uniqueSorted(
      this.getTcpCascadingPool().map((record) =>
        String(record?.client || '').trim(),
      ),
    );
  }

  private extractPcClientsFromPool(): string[] {
    return this.uniqueSorted(
      this.getPcCascadingPool().map((record) =>
        String(record?.client || '').trim(),
      ),
    );
  }

  private ensureValidPcOperationSelection(): void {
    const op = String(this.selectedPcOperation || '').trim();
    const opts = (
      this.pcMasterOperationOptions.length
        ? this.pcMasterOperationOptions
        : this.operationsOptions || []
    ).filter((item) => item && !/^\d+(\.\d+)?$/.test(String(item).trim()));
    if (opts.length) {
      this.operationsOptions = [...opts];
    }
    if (
      !op ||
      /^\d+(\.\d+)?$/.test(op) ||
      !this.operationsOptions.includes(op)
    ) {
      this.selectedPcOperation =
        this.operationsOptions.find(
          (item) => item === 'Cargo Ops -Discharging',
        ) ||
        this.operationsOptions[0] ||
        'Cargo Ops -Discharging';
    }
  }

  private applyPcPortFilter(rows: any[]): any[] {
    if (!this.selectedPorts?.length || this.isAllPcPortsSelected()) {
      return rows;
    }
    return rows.filter((record) => {
      const port = String(record?.port_name || record?.port || '').trim();
      return this.selectedPorts.some(
        (selected) => selected.toLowerCase() === port.toLowerCase(),
      );
    });
  }

  private applyTcpPortFilter(rows: any[]): any[] {
    if (!this.selectedTcpPorts?.length || this.isAllTcpPortsSelected()) {
      return rows;
    }
    return rows.filter((record) => {
      const port = String(record?.port_name || record?.port || '').trim();
      return this.selectedTcpPorts.some(
        (selected) => selected.toLowerCase() === port.toLowerCase(),
      );
    });
  }

  private applyTcpYardFilter(rows: any[]): any[] {
    if (this.isAllTcpYardsSelected()) {
      return rows;
    }
    return rows.filter((record) => {
      const yard =
        record?.yard ||
        record?.yard_name ||
        record?.ship_yard ||
        this.getVesselMeta(record?.vessel_name)?.ship_yard ||
        '';
      return this.selectedTcpYards.some(
        (y) => y.toLowerCase() === String(yard).toLowerCase(),
      );
    });
  }

  private applyPcYardFilter(rows: any[]): any[] {
    if (this.isAllPcYardsSelected()) {
      return rows;
    }
    return rows.filter((record) => {
      const yard = String(
        record?.yard ||
        record?.yard_name ||
        record?.ship_yard ||
        this.getVesselMeta(record?.vessel_name)?.ship_yard ||
        '',
      ).trim();
      return this.selectedPcYards.some(
        (y) => y.toLowerCase() === yard.toLowerCase(),
      );
    });
  }

  private applyPcBuiltAgeFilter(rows: any[]): any[] {
    if (this.isAllPcBuiltAgesSelected()) {
      return rows;
    }
    const selectedAges = new Set(this.selectedPcBuiltAges.map(String));
    return rows.filter((record) => {
      const age = getVesselAgeYearsFromBuildDate(
        extractRecordBuildYear(record),
      );
      if (age === null) {
        return false;
      }
      return selectedAges.has(String(age));
    });
  }

  private applyPcShipBuiltFilter(rows: any[]): any[] {
    const { ship_built_from, ship_built_to } = this.pcBenchmarkForm.value;
    if (!ship_built_from && !ship_built_to) {
      return rows;
    }
    return rows.filter((record) =>
      this.matchesTcpShipBuiltRange(record, ship_built_from, ship_built_to),
    );
  }

  /** Cascade: Client → Segment → Operations → Vessel → Port → Yard → Age → chart. */
  private getPcPoolScoped(
    through:
      | 'client'
      | 'segment'
      | 'operations'
      | 'vessel'
      | 'port'
      | 'yard'
      | 'age'
      | 'chart' = 'chart',
  ): any[] {
    let scoped = this.applyPcClientFilter([...this.getPcCascadingPool()]);
    if (through === 'client') {
      return scoped;
    }

    const segmentOptionRows = scoped;
    scoped = this.applyPcSegmentFilter(scoped);
    if (through === 'segment') {
      return segmentOptionRows;
    }

    const operationOptionRows = scoped;
    scoped = this.applyPcOperationFilter(scoped);
    if (through === 'operations') {
      return operationOptionRows;
    }

    const vesselOptionRows = scoped;
    scoped = this.applyPcVesselFilter(scoped);
    if (through === 'vessel') {
      return vesselOptionRows;
    }

    const portOptionRows = scoped;
    scoped = this.applyPcPortFilter(scoped);
    if (through === 'port') {
      return portOptionRows;
    }

    const yardOptionRows = scoped;
    scoped = this.applyPcYardFilter(scoped);
    if (through === 'yard') {
      return yardOptionRows;
    }

    const ageOptionRows = scoped;
    scoped = this.applyPcBuiltAgeFilter(scoped);
    if (through === 'age') {
      return ageOptionRows;
    }

    scoped = this.applyPcShipBuiltFilter(scoped);
    return scoped;
  }

  private ensureTcpOperationOptions(): void {
    if (!this.tcpMasterOperationOptions.length) {
      this.tcpMasterOperationOptions = this.operations.map((op) => op.value);
    }
    this.tcpOperationOptions = [...this.tcpMasterOperationOptions];
    if (!this.tcpMasterSegmentOptions.length) {
      this.mergeTcpSegmentOptions(['LR3/Suezmax']);
    }
    if (!this.selectedOperation) {
      this.selectedOperation = this.tcpOperationOptions[0] || 'Loading per day';
    }
  }

  /**
   * PC Benchmarking Segment dropdown source of truth.
   * Only `{ key, value[] }` rows from the Segment API are allowed.
   * Raw string tokens (table/catalog) must never become dropdown options.
   */
  private setPcSegmentOptionsFromApi(segments: unknown[]): void {
    if (!Array.isArray(segments) || !segments.length) {
      return;
    }

    const keyValueRows = segments.filter(
      (segment) =>
        !!segment &&
        typeof segment === 'object' &&
        !Array.isArray(segment) &&
        String((segment as Record<string, unknown>)['key'] ?? '').trim() !== '',
    );
    if (!keyValueRows.length) {
      return;
    }

    // Clear previous options first, then replace with API keys only.
    this.pcMasterSegmentOptions = [];
    this.pcSegments = [];

    const fromApi = normalizeBenchmarkSegmentOptions(keyValueRows);
    if (!fromApi.length) {
      return;
    }

    this.pcMasterSegmentOptions = fromApi;
    this.pcSegments = [...fromApi];
    this.syncPcSegmentSelection();
    this.cdr.markForCheck();
  }

  /** @deprecated Prefer setPcSegmentOptionsFromApi — kept for call-site compatibility. */
  private mergePcSegmentOptions(segments: unknown[]): void {
    this.setPcSegmentOptionsFromApi(segments);
  }

  private mergeTcpSegmentOptions(segments: unknown[]): void {
    if (!segments || !segments.length) {
      return;
    }

    const hasStructuredObjects = segments.some(
      (s: any) => s && typeof s === 'object' && 'key' in s && 'value' in s
    );

    let merged: { key: string; value: string[] }[] = [];

    if (hasStructuredObjects) {
      const existingMap = new Map<string, string[]>();
      this.tcpMasterSegmentOptions.forEach((s) => existingMap.set(s.key, s.value));
      segments.forEach((s: any) => {
        if (s && s.key && s.value) {
          existingMap.set(s.key, Array.isArray(s.value) ? s.value : [s.value]);
        }
      });
      merged = Array.from(existingMap.entries()).map(([key, value]) => ({ key, value }));
    } else {
      const incoming = (segments as string[])
        .map((segment) => String(segment || '').trim())
        .filter(Boolean);
      if (!incoming.length) {
        return;
      }

      const allTokens = [
        ...this.tcpMasterSegmentOptions.flatMap((segment) => [
          segment.key,
          ...segment.value,
        ]),
        ...incoming,
      ];
      merged = normalizeBenchmarkSegmentOptions(allTokens);
    }

    if (!merged.length) {
      return;
    }

    this.tcpMasterSegmentOptions = merged;
    this.syncTcpSegmentDisplayOptions();
    this.cdr.markForCheck();
  }

  pcSegmentOptionCompare = (optionKey: string, selectedKey: string): boolean =>
    benchmarkSegmentKeysMatch(optionKey, selectedKey);

  private syncPcSegmentSelection(): void {
    if (!this.pcSegments?.length) {
      return;
    }
    const match = this.pcSegments.find((segment: { key: string }) =>
      benchmarkSegmentKeysMatch(segment.key, this.selectedPcSegment),
    );
    if (match) {
      this.selectedPcSegment = match.key;
      return;
    }
    const mrMatch = this.pcSegments.find((segment: { key: string }) =>
      benchmarkSegmentKeysMatch(segment.key, 'LR3/Suezmax'),
    );
    if (
      mrMatch &&
      benchmarkSegmentKeysMatch(this.selectedPcSegment || 'MR', 'MR')
    ) {
      this.selectedPcSegment = mrMatch.key;
      return;
    }
    this.selectedPcSegment = this.pcSegments[0].key;
  }

  private syncTcpSegmentDisplayOptions(): void {
    if (!this.tcpMasterSegmentOptions.length) {
      return;
    }
    this.benchmarkSegments = [...this.tcpMasterSegmentOptions];
    this.syncTcpSegmentSelection();
  }

  private syncTcpSegmentSelection(): void {
    if (!this.benchmarkSegments?.length) {
      return;
    }
    const match = this.benchmarkSegments.find((segment: { key: string }) =>
      benchmarkSegmentKeysMatch(segment.key, this.selectedSegment),
    );
    if (match) {
      this.selectedSegment = match.key;
      return;
    }
    const mrMatch = this.benchmarkSegments.find((segment: { key: string }) =>
      benchmarkSegmentKeysMatch(segment.key, 'LR3/Suezmax'),
    );
    if (
      mrMatch &&
      benchmarkSegmentKeysMatch(this.selectedSegment || 'MR', 'MR')
    ) {
      this.selectedSegment = mrMatch.key;
      return;
    }
    this.selectedSegment = this.benchmarkSegments[0].key;
  }

  /** Populate master TCP segment list once — display list stays stable across filter changes. */
  private rebuildTcpSegmentDropdownOptions(): void {
    const tokens: string[] = [
      ...this.tcpMasterSegmentOptions.flatMap((segment) => [
        segment.key,
        ...segment.value,
      ]),
    ];

    if (Array.isArray(this.segmentOptions) && this.segmentOptions.length) {
      tokens.push(...this.segmentOptions);
    }

    if (Array.isArray(this.segments) && this.segments.length) {
      tokens.push(...this.segments.map((segment) => String(segment)));
    }

    const records = Array.isArray(this.elements) ? this.elements : [];
    if (records.length) {
      tokens.push(...extractSegmentOptionsFromRecords(records));
    }

    if (this.getTcpCascadingPool()?.length) {
      tokens.push(
        ...this.getTcpCascadingPool().map((record) =>
          String(record?.vessel_class || record?.segment || ''),
        ),
      );
    }

    const cleaned = dedupeFilterOptions(tokens);
    if (cleaned.length) {
      this.mergeTcpSegmentOptions(cleaned);
      return;
    }

    if (this.tcpMasterSegmentOptions.length) {
      this.syncTcpSegmentDisplayOptions();
      return;
    }

    this.mergeTcpSegmentOptions(['LR3/Suezmax']);
  }

  private mergeTcpOperationOptions(operations: string[]): void {
    const base =
      this.tcpMasterOperationOptions.length > 0
        ? this.tcpMasterOperationOptions
        : this.operations.map((op) => op.value);
    const merged = [...base];
    operations
      .map((op) => String(op || '').trim())
      .filter(Boolean)
      .forEach((op) => {
        if (!merged.includes(op)) {
          merged.push(op);
        }
      });
    this.tcpMasterOperationOptions = merged;
    this.tcpOperationOptions = [...merged];
    if (
      this.tcpOperationOptions.length &&
      !this.tcpOperationOptions.includes(this.selectedOperation)
    ) {
      this.selectedOperation = this.tcpOperationOptions[0];
    }
    this.cdr.markForCheck();
  }

  private loadTcpBenchmarkSegments(): void {
    const payload = { client: this.selectedClient || 'ALL' };
    this.prequestFormService.getSegmentsBnch(payload).subscribe({
      next: (res: any) => {
        if (res?.status === 'success' && res.segments?.length) {
          this.mergeTcpSegmentOptions(res.segments);
        }
      },
      error: () => {
        if (!this.tcpMasterSegmentOptions.length) {
          this.mergeTcpSegmentOptions(['LR3/Suezmax']);
        }
      },
    });
  }

  private tcpBenchmarkRowKey(record: any): string {
    return [
      String(record?.client ?? ''),
      String(record?.vessel_name ?? ''),
      String(record?.yard ?? record?.yard_name ?? ''),
      String(record?.port_consumption_parameter ?? ''),
      String(record?.vessel_class ?? record?.segment ?? ''),
    ].join('|');
  }

  private mergeTcpBenchmarkMasterPool(rows: any[]): void {
    if (!rows?.length) {
      return;
    }
    const byKey = new Map<string, any>();
    for (const row of [...this.tcpBenchmarkMasterPool, ...rows]) {
      byKey.set(this.tcpBenchmarkRowKey(row), row);
    }
    this.tcpBenchmarkMasterPool = Array.from(byKey.values());
    this.tcpBenchmarkPool = this.tcpBenchmarkMasterPool;
  }

  private getTcpCascadingPool(): any[] {
    return this.tcpBenchmarkMasterPool.length
      ? this.tcpBenchmarkMasterPool
      : this.tcpBenchmarkPool;
  }

  private mergePcClientOptions(clients: string[]): void {
    const merged = extractClientOptionsFromRecords(
      [...this.pcMasterClientOptions, ...clients].map((client) => ({ client })),
    );
    this.pcMasterClientOptions = merged;
    this.pcClientOptions = [...merged];
    this.cdr.markForCheck();
  }

  private loadPcBenchmarkClients(): void {
    this.sharedDataService.getClients().subscribe({
      next: (res: any) => {
        const names = (res || [])
          .map((item: any) => String(item?.client_name || '').trim())
          .filter(Boolean);
        if (names.length) {
          this.mergePcClientOptions(names);
        }
      },
      error: () => {
        // Fallback: tracker / vessel-derived clients already merged elsewhere.
      },
    });
  }

  private pcBenchmarkRowKey(record: any): string {
    return [
      String(record?.client ?? ''),
      String(record?.vessel_name ?? ''),
      String(record?.port_name ?? record?.port ?? ''),
      String(record?.time ?? ''),
      String(record?.vessel_id ?? ''),
      String(record?.operation_consumption ?? ''),
    ].join('|');
  }

  private mergePcBenchmarkMasterPool(rows: any[]): void {
    if (!rows?.length) {
      return;
    }
    const byKey = new Map<string, any>();
    for (const row of [...this.pcBenchmarkMasterPool, ...rows]) {
      byKey.set(this.pcBenchmarkRowKey(row), row);
    }
    this.pcBenchmarkMasterPool = Array.from(byKey.values());
    this.pcBenchmarkPool = this.pcBenchmarkMasterPool;
  }

  private getPcCascadingPool(): any[] {
    return this.pcBenchmarkMasterPool.length
      ? this.pcBenchmarkMasterPool
      : this.pcBenchmarkPool;
  }

  private populatePcFilterOptionsFromRows(rows: any[]): void {
    if (!rows?.length) {
      return;
    }

    this.mergePcBenchmarkMasterPool(rows);
    const opts = extractPcBenchmarkFilterOptions(rows);

    if (opts.clients.length) {
      this.mergePcClientOptions(opts.clients);
    }
    // Segment dropdown is bound only to the Segment API — never from chart/table rows.
    if (opts.ports.length) {
      this.mergePcMasterPortOptions(opts.ports);
    }
    if (opts.vessels.length) {
      this.mergePcMasterVesselNames(opts.vessels);
    }

    this.logFilterOptions('PC Response', { rows: rows.length });
    this.logFilterOptions('Client Options', this.pcClientOptions);
    this.logFilterOptions('Vessel Options', opts.vessels);
    this.logFilterOptions('Segment Options', opts.segments);
    this.logFilterOptions('Operation Options', this.operationsOptions);
    this.logFilterOptions('Port Options', opts.ports);

    const builtOpts = extractTcpBenchmarkFilterOptions(rows);
    this.pcBuiltDateMin = builtOpts.builtDateMin;
    this.pcBuiltDateMax = builtOpts.builtDateMax;
    if (builtOpts.builtDateMin && builtOpts.builtDateMax) {
      const { ship_built_from, ship_built_to } = this.pcBenchmarkForm.value;
      if (!ship_built_from && !ship_built_to) {
        this.pcBenchmarkForm.patchValue(
          {
            ship_built_from: builtOpts.builtDateMin,
            ship_built_to: builtOpts.builtDateMax,
          },
          { emitEvent: false },
        );
      }
    }

    this.rebuildPcCascadingOptions();
  }

  private mergePcMasterVesselNames(vesselNames: string[]): void {
    const merged = new Set(
      [...this.pcMasterVesselNames, ...vesselNames]
        .map((name) => String(name || '').trim())
        .filter(Boolean),
    );
    this.pcMasterVesselNames = Array.from(merged).sort((a, b) =>
      a.localeCompare(b),
    );
  }

  private syncPcVesselCatalogFromPorts(): void {
    const names = (this.vessels || [])
      .map((v: any) => String(v?.name || '').trim())
      .filter(Boolean);
    if (names.length) {
      this.mergePcMasterVesselNames(names);
    }
  }

  private rebuildPcVesselNameOptions(): void {
    // Prefer vessels from the latest filtered benchmarking API response.
    if (this.pcLatestBenchmarkRows.length) {
      this.bindPcVesselAndYardOptionsFromLatestRows();
      return;
    }

    const vesselScoped = this.getPcPoolScoped('vessel');
    const poolNames = this.uniqueSorted(
      vesselScoped
        .map((record) => String(record?.vessel_name || ''))
        .filter(Boolean),
    );

    if (poolNames.length) {
      this.pcVesselNameOptions = poolNames;
      this.prunePcVesselSelections();
      return;
    }

    // Fallback only before the first benchmarking response arrives.
    this.pcVesselNameOptions = this.getCatalogVesselNamesForPcSegment();
    this.prunePcVesselSelections();
  }

  /**
   * Bind Vessel Name + Yard + Built Year (age) dropdowns from the latest
   * filtered API rows only. Yard/age options further narrow with selections.
   */
  private bindPcVesselAndYardOptionsFromLatestRows(): void {
    const rows = Array.isArray(this.pcLatestBenchmarkRows)
      ? this.pcLatestBenchmarkRows
      : [];

    const vesselNames = dedupeFilterOptions(
      rows
        .map((record) => String(record?.vessel_name || '').trim())
        .filter(Boolean),
    );
    this.pcVesselNameOptions = vesselNames;
    this.prunePcVesselSelections();

    let yardSource = rows;
    if (this.selectedVessels?.length && !this.isAllPcVesselsSelected()) {
      const selectedKeys = new Set(
        this.selectedVessels.map((name) => name.trim().toLowerCase()),
      );
      yardSource = rows.filter((record) =>
        selectedKeys.has(
          String(record?.vessel_name || '')
            .trim()
            .toLowerCase(),
        ),
      );
    }

    this.pcYardOptions = dedupeFilterOptions(
      yardSource
        .map((record) =>
          String(
            record?.yard_name || record?.yard || record?.ship_yard || '',
          ).trim(),
        )
        .filter(Boolean),
    );
    this.selectedPcYards = this.selectedPcYards.filter((yard) =>
      this.pcYardOptions.some(
        (option) => option.toLowerCase() === yard.toLowerCase(),
      ),
    );

    let ageSource = yardSource;
    if (this.selectedPcYards?.length && !this.isAllPcYardsSelected()) {
      const selectedYards = new Set(
        this.selectedPcYards.map((yard) => yard.trim().toLowerCase()),
      );
      ageSource = yardSource.filter((record) =>
        selectedYards.has(
          String(record?.yard_name || record?.yard || record?.ship_yard || '')
            .trim()
            .toLowerCase(),
        ),
      );
    }

    this.bindPcBuiltAgeOptionsFromRows(ageSource);
  }

  private bindPcBuiltAgeOptionsFromRows(rows: any[]): void {
    const ages = Array.from(
      new Set(
        (rows || [])
          .map((record) =>
            getVesselAgeYearsFromBuildDate(extractRecordBuildYear(record)),
          )
          .filter((age): age is number => age !== null),
      ),
    ).sort((a, b) => a - b);

    this.pcBuiltAgeOptions = ages.map(String);
    this.pcBuiltAgeOptionItems = ages.map((age) => ({
      value: String(age),
      label: formatVesselAgeLabel(age),
    }));
    this.selectedPcBuiltAges = this.selectedPcBuiltAges.filter((age) =>
      this.pcBuiltAgeOptions.includes(String(age)),
    );
  }

  private vesselMatchesSelectedPcSegment(
    vesselSegment: string | null | undefined,
  ): boolean {
    if (!this.selectedPcSegment) {
      return true;
    }
    const segmentValues = this.getPcSegmentFilterValues();
    return matchesAnySegment(
      vesselSegment || '',
      segmentValues.length ? segmentValues : [this.selectedPcSegment],
    );
  }

  private getCatalogVesselNamesForPcSegment(): string[] {
    const catalog = this.uniqueSorted(
      (this.vessels || [])
        .filter((v: any) => {
          if (
            !this.isAllPcClientsSelected() &&
            !matchesAnyClient(v.client, this.selectedPcClients)
          ) {
            return false;
          }
          return this.vesselMatchesSelectedPcSegment(v.segment);
        })
        .map((v: any) => String(v?.name || '').trim())
        .filter(Boolean),
    );

    const pool = this.getPcCascadingPool();
    if (!pool?.length) {
      return catalog;
    }

    const allowed = new Set(
      this.getPcPoolScoped('operations')
        .map((record) =>
          String(record?.vessel_name || '')
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),
    );
    if (!allowed.size) {
      return catalog;
    }

    return catalog.filter((name) => allowed.has(name.toLowerCase()));
  }

  private prefetchPcBenchmarkPoolForVessels(): void {
    // Narrow the Vessel dropdown to Client + Segment + Operation. This uses the
    // benchmarking dataset API (NOT the Port API), so it is safe to run before a
    // Vessel is selected. Port filtering is deliberately excluded here.
    const operation =
      this.selectedPcOperation ||
      this.operationsOptions[0] ||
      'Cargo Ops -Discharging';
    const json: Record<string, unknown> = {
      segment: this.getPcSegmentApiPayload(),
      operation,
      client: this.getPcClientPayload(),
    };

    const requestSeq = ++this.pcVesselPoolRequestSeq;

    this.prequestFormService.getPcBenchmarking(json).subscribe({
      next: (data: any) => {
        if (requestSeq !== this.pcVesselPoolRequestSeq) {
          return;
        }
        const rows = resolvePcBenchmarkRows(data);
        if (rows.length) {
          this.pcLatestBenchmarkRows = rows;
          this.mergePcBenchmarkMasterPool(rows);
          const vesselNames = rows
            .map((row) => String(row?.vessel_name || '').trim())
            .filter(Boolean);
          if (vesselNames.length) {
            this.mergePcMasterVesselNames(vesselNames);
          }
          this.bindPcVesselAndYardOptionsFromLatestRows();
        } else {
          this.pcLatestBenchmarkRows = [];
          this.pcVesselNameOptions = [];
          this.pcYardOptions = [];
          this.pcBuiltAgeOptions = [];
          this.pcBuiltAgeOptionItems = [];
        }
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestSeq !== this.pcVesselPoolRequestSeq) {
          return;
        }
        this.rebuildPcVesselNameOptions();
      },
    });
  }

  private rebuildPcCascadingOptions(): void {
    if (this.pcMasterClientOptions.length) {
      this.pcClientOptions = [...this.pcMasterClientOptions];
    } else if (this.getPcCascadingPool()?.length) {
      this.mergePcClientOptions(this.extractPcClientsFromPool());
    }

    this.syncPcSegmentDisplayOptions();
    this.syncPcOperationDisplayOptions();

    // Vessel + Yard + Built Age must follow the latest filtered benchmarking response.
    if (this.pcLatestBenchmarkRows.length) {
      this.bindPcVesselAndYardOptionsFromLatestRows();
    } else {
      this.syncPcVesselCatalogFromPorts();
      this.rebuildPcVesselNameOptions();

      const yardScoped = this.getPcCascadingPool()?.length
        ? this.getPcPoolScoped('yard')
        : [];
      this.pcYardOptions = dedupeFilterOptions(
        yardScoped
          .map((record) =>
            extractRecordYard(record, this.getVesselMeta(record?.vessel_name)),
          )
          .filter(Boolean),
      );
      this.selectedPcYards = this.selectedPcYards.filter((yard) =>
        this.pcYardOptions.includes(yard),
      );
      this.pcBuiltAgeOptions = [];
      this.pcBuiltAgeOptionItems = [];
      this.selectedPcBuiltAges = [];
    }

    this.rebuildPcScopedPortOptions();
    this.prunePcPortSelections();

    this.filterVesselsBySegment();
    this.cdr.markForCheck();
  }

  private syncPcSegmentDisplayOptions(): void {
    // Dropdown shows API keys only — never rebuild from raw table/catalog tokens.
    if (!this.pcMasterSegmentOptions.length) {
      this.pcSegments = [];
      return;
    }
    this.pcSegments = [...this.pcMasterSegmentOptions];
    this.syncPcSegmentSelection();
  }

  /** Distinct Segment tokens available for the currently selected Client(s). */
  private getAvailableSegmentTokensForPcClient(): string[] {
    const tokens: string[] = [];

    (this.vessels || []).forEach((vessel: any) => {
      if (
        this.isAllPcClientsSelected() ||
        matchesAnyClient(vessel?.client, this.selectedPcClients)
      ) {
        const seg = String(vessel?.segment || '').trim();
        if (seg) {
          tokens.push(seg);
        }
      }
    });

    this.applyPcClientFilter([...this.getPcCascadingPool()]).forEach(
      (record: any) => {
        const seg = String(
          record?.vessel_class || record?.segment || '',
        ).trim();
        if (seg) {
          tokens.push(seg);
        }
      },
    );

    return [...new Set(tokens)];
  }

  private syncPcOperationDisplayOptions(): void {
    if (!this.pcMasterOperationOptions.length) {
      return;
    }
    this.operationsOptions = [...this.pcMasterOperationOptions];
    this.ensureValidPcOperationSelection();
  }

  private rebuildPcScopedPortOptions(): void {
    // Intentionally a no-op. Port options must come ONLY from the Port API
    // (benchmarkPort → applyPcBenchmarkPortsApiResponse) after Operations.
  }

  /** Clear all Port dropdown state whenever a parent (Client/Segment/Operation) changes. */
  private clearPcPortOptions(): void {
    this.pcScopedPortOptions = [];
    this.benchmarkPorts = [];
    this.filteredPorts = [];
    this.selectedPorts = [];
    this.portSearchText = '';
    this.updatePortLabel();
    // Stale Vessel/Yard options from the previous filter response must not linger.
    this.pcLatestBenchmarkRows = [];
    this.pcVesselNameOptions = [];
    this.pcYardOptions = [];
    this.pcBuiltAgeOptions = [];
    this.pcBuiltAgeOptionItems = [];
    this.selectedPcBuiltAges = [];
  }

  private prunePcPortSelections(): void {
    const options = this.pcScopedPortOptions.length
      ? this.pcScopedPortOptions
      : this.pcPortOptions;
    if (!options.length) {
      this.updatePortLabel();
      return;
    }

    this.selectedPorts = (this.selectedPorts || []).filter((port) =>
      options.some((option) => option.toLowerCase() === port.toLowerCase()),
    );
    this.updatePortLabel();
  }

  private prunePcVesselSelections(): void {
    const options = this.pcVesselNameOptions || [];
    if (!options.length) {
      return;
    }
    this.selectedVessels = (this.selectedVessels || []).filter((name) =>
      options.some(
        (option) => option.toLowerCase() === String(name).toLowerCase(),
      ),
    );
    this.selectedVesselLabel = this.selectedVessels.join(', ');
  }

  /** Keep PC Segment dropdown bound only to the Segment API response. */
  private rebuildPcSegmentDropdownOptions(): void {
    this.syncPcSegmentDisplayOptions();
  }

  loadSegments() {
    this.prequestFormService.getBenchmarkSegments({ client: 'ALL' }).subscribe((res: any) => {
      if (res.status === 'success' && res.segments) {
        this.segmentsDropdown = res.segments;
        this.segmentMultiSelectOptions = res.segments.map((s: any) => s.key);
        this.segmentApiValueMap = {};
        res.segments.forEach((s: any) => {
          this.segmentApiValueMap[s.key] = s.value;
        });
        this.cdr.markForCheck();
      }
    });
  }
  updatePortLabel() {
    this.selectedPortLabel = this.selectedPorts.join(', ');
  }

  /** True UTC fields (e.g. report sent). */
  formatUTCDate(dateValue: any): string {
    return formatPcUtcDateTime(dateValue);
  }

  /** Arrival / Departure — API stores port LT (often labeled GMT); convert to UTC. */
  formatPortLocalAsUTC(dateValue: any, portName?: string | null): string {
    return formatPcPortLocalAsUtc(dateValue, portName);
  }

  toggleCP(event?: Event): void {
    event?.stopPropagation();
    this.cpExpanded = !this.cpExpanded;
    this.logDetailRailDebug('toggleCP');
  }

  toggleDesign(event?: Event): void {
    event?.stopPropagation();
    this.designExpanded = !this.designExpanded;
    this.logDetailRailDebug('toggleDesign');
  }

  /** Sub-columns for C/P Overall detail rail */
  readonly cpOverallDetailColumns = [
    { key: 'cp_warranted_hsfo', label: 'HSFO', group: 'warranted' },
    { key: 'cp_warranted_vlsfo', label: 'VLSFO', group: 'warranted' },
    { key: 'cp_warranted_lsgo', label: 'LSMGO', group: 'warranted' },
    { key: 'cp_loss_hsfo', label: 'HSFO', group: 'loss' },
    { key: 'cp_loss_vlsfo', label: 'VLSFO', group: 'loss' },
    { key: 'cp_loss_lsgo', label: 'LSMGO', group: 'loss' },
  ] as const;

  /** Sub-columns for Design Overall detail rail */
  readonly designOverallDetailColumns = [
    { key: 'design_cons_hsfo', label: 'HSFO', group: 'consumption' },
    { key: 'design_cons_vlsfo', label: 'VLSFO', group: 'consumption' },
    { key: 'design_cons_lsgo', label: 'LSMGO', group: 'consumption' },
    { key: 'design_loss_hsfo', label: 'HSFO', group: 'loss' },
    { key: 'design_loss_vlsfo', label: 'VLSFO', group: 'loss' },
    { key: 'design_loss_lsgo', label: 'LSMGO', group: 'loss' },
  ] as const;

  getCpOverallDetailValue(
    element: any,
    columnKey: string,
  ): number | null | undefined {
    switch (columnKey) {
      case 'cp_warranted_hsfo':
        return element?.cp_warranted_consumptions_mt
          ?.cp_warranted_consumptions_mt_hsfo;
      case 'cp_warranted_vlsfo':
        return element?.cp_warranted_consumptions_mt
          ?.cp_warranted_consumptions_mt_vlsfo;
      case 'cp_warranted_lsgo':
        return element?.cp_warranted_consumptions_mt
          ?.cp_warranted_consumptions_mt_lsgo;
      case 'cp_loss_hsfo':
        return element?.cp_warrantedloss_or_gain
          ?.cp_warrantedloss_or_gain_mt__hsfo;
      case 'cp_loss_vlsfo':
        return element?.cp_warrantedloss_or_gain
          ?.cp_warrantedloss_or_gain_mt__vlsfo;
      case 'cp_loss_lsgo':
        return element?.cp_warrantedloss_or_gain
          ?.cp_warrantedloss_or_gain_mt__lsgo;
      default:
        return undefined;
    }
  }

  getDesignOverallDetailValue(
    element: any,
    columnKey: string,
  ): number | null | undefined {
    switch (columnKey) {
      case 'design_cons_hsfo':
        return element?.technical_design_consumptions_mt
          ?.technical_design_consumptions_mt_hsfo;
      case 'design_cons_vlsfo':
        return element?.technical_design_consumptions_mt
          ?.technical_design_consumptions_mt_vlsfo;
      case 'design_cons_lsgo':
        return element?.technical_design_consumptions_mt
          ?.technical_design_consumptions_mt_lsgo;
      case 'design_loss_hsfo':
        return element?.technical_designloss_or_gain_mt
          ?.technical_designloss_or_gain_mt_hsfo;
      case 'design_loss_vlsfo':
        return element?.technical_designloss_or_gain_mt
          ?.technical_designloss_or_gain_mt_vlsfo;
      case 'design_loss_lsgo':
        return element?.technical_designloss_or_gain_mt
          ?.technical_designloss_or_gain_mt_lsgo;
      default:
        return undefined;
    }
  }

  getCpLossCellColor(element: any, columnKey: string): string | null {
    const value = this.getCpOverallDetailValue(element, columnKey);
    if (value == null || columnKey.indexOf('loss') === -1) {
      return null;
    }
    if (value < 0) {
      return '#ff9090';
    }
    if (value > 0) {
      return '#a8d296';
    }
    return null;
  }

  getDesignLossCellColor(element: any, columnKey: string): string | null {
    const value = this.getDesignOverallDetailValue(element, columnKey);
    if (value == null || columnKey.indexOf('loss') === -1) {
      return null;
    }
    if (value < 0) {
      return '#ff9090';
    }
    if (value > 0) {
      return '#a8d296';
    }
    return null;
  }

  isCpOverallDividerColumn(columnKey: string): boolean {
    return columnKey === 'cp_loss_hsfo';
  }

  isDesignOverallDividerColumn(columnKey: string): boolean {
    return columnKey === 'design_cons_hsfo' || columnKey === 'design_loss_hsfo';
  }

  private logDetailRailDebug(context: string): void {
    if (!isDevMode()) {
      return;
    }
    const sample = this.paginatedData?.[0];
    console.group(`[Port DB] ${context}`);
    console.log('Collapse state', {
      cpExpanded: this.cpExpanded,
      designExpanded: this.designExpanded,
      brobSectionExpanded: this.brobSectionExpanded,
      consumptionSectionExpanded: this.consumptionSectionExpanded,
      voyageDetailsSectionExpanded: this.voyageDetailsSectionExpanded,
      showSubHeaderRow: this.showSubHeaderRow,
      headerRowSpan: this.headerRowSpan,
    });
    console.log('C/P Overall sub-columns', this.cpOverallDetailColumns);
    console.log('Design Overall sub-columns', this.designOverallDetailColumns);
    if (sample) {
      console.log('Sample row API fields', {
        cp_warranted_consumptions_mt: sample.cp_warranted_consumptions_mt,
        cp_warrantedloss_or_gain: sample.cp_warrantedloss_or_gain,
        technical_design_consumptions_mt:
          sample.technical_design_consumptions_mt,
        technical_designloss_or_gain_mt: sample.technical_designloss_or_gain_mt,
      });
    }
    console.groupEnd();
  }

  toggleBrobSection(event?: Event): void {
    event?.stopPropagation();
    this.brobSectionExpanded = !this.brobSectionExpanded;
  }

  toggleConsumptionSection(event?: Event): void {
    event?.stopPropagation();
    this.consumptionSectionExpanded = !this.consumptionSectionExpanded;
  }

  toggleVoyageDetailsSection(event?: Event): void {
    event?.stopPropagation();
    this.voyageDetailsSectionExpanded = !this.voyageDetailsSectionExpanded;
    this.logDetailRailDebug('toggleVoyageDetailsSection');
  }

  formatMetricCell(value: number | null | undefined): string {
    if (value === null || value === undefined || value === 0) {
      return '--';
    }
    return String(value);
  }

  get databaseTooltipClass(): string {
    return this.isDarkMode()
      ? 'pd-database-tooltip'
      : 'pd-database-tooltip pd-database-tooltip--light';
  }

  getCpWarrantedCellView(
    element: any,
    op: PortDatabaseOpKey,
  ): ConsumptionDifferenceView {
    return buildCpWarrantedCellView(element, op);
  }

  getDesignCellView(
    element: any,
    op: PortDatabaseOpKey,
  ): ConsumptionDifferenceView {
    return buildDesignCellView(element, op);
  }

  getCpWarrantedCellTooltip(element: any, op: PortDatabaseOpKey): string {
    return buildCpWarrantedTooltip(element, op);
  }

  getDesignCellTooltip(element: any, op: PortDatabaseOpKey): string {
    return buildDesignTooltip(element, op);
  }

  getOptimizationCellView(
    element: any,
    op: PortDatabaseOpKey,
  ): ConsumptionDifferenceView {
    return buildOptimizationCellView(element, op);
  }

  getOptimizationCellTooltip(element: any, op: PortDatabaseOpKey): string {
    return buildOptimizationTooltip(element, op);
  }

  getOptimizationLegConsumption(
    element: any,
    op: PortDatabaseOpKey,
  ): number | null {
    return getOptimizationLegConsumption(element, op);
  }

  getOpWarrantedValue(
    element: any,
    op: PortDatabaseOpKey,
  ): number | null {
    return getOpWarrantedValue(element, op);
  }

  getOpDesignValue(
    element: any,
    op: PortDatabaseOpKey,
  ): number | null {
    return getOpDesignValue(element, op);
  }

  hasAnyActualOpData(element: any): boolean {
    return hasAnyActualOpData(element);
  }

  private optimizationSaveTimers = new WeakMap<any, ReturnType<typeof setTimeout>>();

  isSavingOptimizationConsumption(element: any): boolean {
    return !!element?._optimizationConsumptionSaving;
  }

  isRowVesselMasked(row?: any): boolean {
    if (this.subusername?.toLowerCase() === 'demo') {
      return true;
    }
    const imo = String(row?.vessel_imo ?? '').trim();
    if (imo === 'XXXXXXX' || imo.toUpperCase().includes('X')) {
      return true;
    }
    const vesselName = String(row?.vessel_name ?? row?.vesselName ?? '').trim();
    if (vesselName === 'Vessel 1' || vesselName === 'Vessel 2') {
      return true;
    }
    return false;
  }

  ensureBunkerObject(element: any): void {
    if (!element) {
      return;
    }
    if (!element.bunker || typeof element.bunker !== 'object') {
      element.bunker = {};
    }
  }

  setOptimizationLegConsumption(
    element: any,
    keyOrOp: PortDatabaseOpKey | 'optimization_man' | 'optimization_cargo_ops' | 'optimization_idle',
    value: number | string | null,
  ): void {
    if (!element) {
      return;
    }
    this.ensureBunkerObject(element);
    const parsed = value === '' || value == null ? null : Number(value);
    const next = Number.isFinite(parsed as number) ? parsed : null;

    const bunkerKey: 'optimization_man' | 'optimization_cargo_ops' | 'optimization_idle' =
      keyOrOp === 'maneuvering' || keyOrOp === 'optimization_man'
        ? 'optimization_man'
        : keyOrOp === 'cargoOps' || keyOrOp === 'optimization_cargo_ops'
          ? 'optimization_cargo_ops'
          : 'optimization_idle';

    element.bunker[bunkerKey] = next;

    if (!element.optimization_consumption_mt || typeof element.optimization_consumption_mt !== 'object') {
      element.optimization_consumption_mt = {};
    }

    if (bunkerKey === 'optimization_man') {
      element.optimization_man = next;
      element.optimization_maneuvering = next;
      element.optimization_manoeuvring = next;
      element.optimization_consumption_manoeuvring_mt = next;
      element.optimization_consumption_mt.manoeuvring = next;
      element.optimization_consumption_mt.maneuvering = next;
    } else if (bunkerKey === 'optimization_cargo_ops') {
      element.optimization_cargo_ops = next;
      element.optimization_consumption_cargo_ops_mt = next;
      element.optimization_consumption_mt.cargo_ops = next;
    } else {
      element.optimization_idle = next;
      element.optimization_consumption_idle_mt = next;
      element.optimization_consumption_mt.idle = next;
    }

    invalidateRowOptimizationCache(element);
    element._optimizationConsumptionDirty = true;
    this.count(this.filteredData);
  }

  formatOptInputValue(value: number | string | null | undefined): number | string {
    if (value == null || value === '' || !Number.isFinite(Number(value))) {
      return '';
    }
    return Number(value);
  }

  private formatOptimizationApiDateTime(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return '';
      }
      return this.toOptimizationApiIsoLocal(value);
    }

    const raw = String(value).trim();
    if (!raw) {
      return '';
    }

    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}T${isoMatch[2]}`;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    // The API serialises naive DB timestamps as "Wed, 14 Oct 2026 10:00:00 GMT".
    // Read those back in UTC; local getters would shift them by the browser
    // offset (e.g. +5:30) and the backend would not find the port call.
    if (/(GMT|UTC|Z)$/i.test(raw)) {
      return parsed.toISOString().slice(0, 19);
    }
    return this.toOptimizationApiIsoLocal(parsed);
  }

  private toOptimizationApiIsoLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  saveOptimizationConsumption(row: any, force = false): void {
    if (!row) {
      return;
    }
    if (this.isRowVesselMasked(row)) {
      if (force) {
        this.toastr.warning('Saving is disabled for masked vessels.');
      }
      return;
    }
    if (!force && !row._optimizationConsumptionDirty) {
      return;
    }
    this.ensureBunkerObject(row);

    const existing = this.optimizationSaveTimers.get(row);
    if (existing) {
      clearTimeout(existing);
    }

    const runSave = () => {
      this.optimizationSaveTimers.delete(row);
      if (this.isRowVesselMasked(row)) {
        if (force) {
          this.toastr.warning('Saving is disabled for masked vessels.');
        }
        return;
      }
      if (!force && !row._optimizationConsumptionDirty) {
        return;
      }

      const rawImo = row?.vessel_imo || row?.original_vessel_imo || '';
      if (this.isRowVesselMasked({ vessel_imo: rawImo })) {
        if (force) {
          this.toastr.warning('Saving is disabled for masked vessels.');
        }
        return;
      }
      const vesselImo = String(rawImo).trim().replace(/[^0-9]/g, '');
      const arrival = this.formatOptimizationApiDateTime(row?.arrival_date_and_time);
      const departure = this.formatOptimizationApiDateTime(row?.departure_date_and_time);

      if (!vesselImo) {
        this.toastr.error('Cannot save: vessel IMO is missing.');
        return;
      }
      if (!arrival || !departure) {
        this.toastr.error('Cannot save: arrival or departure date/time is missing.');
        return;
      }

      const manVal = this.getOptimizationLegConsumption(row, 'maneuvering');
      const cargoVal = this.getOptimizationLegConsumption(row, 'cargoOps');
      const idleVal = this.getOptimizationLegConsumption(row, 'idle');

      const payload: Record<string, any> = {
        unique_id: row?.unique_id,
        vessel_imo: vesselImo,
        arrival_date_and_time: arrival,
        departure_date_and_time: departure,
        optimization_consumption_mt: {
          manoeuvring: manVal,
          cargo_ops: cargoVal,
          idle: idleVal,
        },
      };

      row._optimizationConsumptionSaving = true;
      this.prequestFormService
        .updateVesselViewOptimizationConsumption(payload)
        .subscribe({
          next: (res: any) => {
            row._optimizationConsumptionDirty = false;
            row._optimizationConsumptionSaving = false;

            if (!row.optimization_consumption_mt || typeof row.optimization_consumption_mt !== 'object') {
              row.optimization_consumption_mt = {};
            }
            row.optimization_consumption_mt.manoeuvring = manVal;
            row.optimization_consumption_mt.cargo_ops = cargoVal;
            row.optimization_consumption_mt.idle = idleVal;

            invalidateRowOptimizationCache(row);
            this.prequestFormService.clearPortPerformanceFrontendCaches();

            const message = res?.message || 'Optimization Consumption saved';
            this.toastr.success(message);
            this.count(this.filteredData);
            this.cdr.markForCheck();
          },
          error: (err: any) => {
            row._optimizationConsumptionSaving = false;
            this.toastr.error(err?.error?.message || err?.error?.error || 'Failed to save Optimization Consumption');
            this.cdr.markForCheck();
          },
        });
    };

    if (force) {
      runSave();
      return;
    }

    const timer = setTimeout(runSave, 350);
    this.optimizationSaveTimers.set(row, timer);
  }

  formatOptimizationMetricCell(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === 0 || value === '0' || value === '') {
      return '--';
    }
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : '--';
  }

  /** Ops column share of its group total (header aggregate). */
  formatOpSharePercent(
    partMt: number | null | undefined,
    groupTotalMt: number,
  ): string {
    if (!groupTotalMt) {
      return '0%';
    }
    const part = this.toFiniteNumber(partMt);
    return `${Math.round((part / groupTotalMt) * 100)}%`;
  }

  /**
   * Top group share of combined Actual + C/P + Design totals
   * (e.g. Actual ≈ 32% when it is ~1/3 of the three group sums).
   */
  formatConsumptionGroupSharePercent(): string {
    if (!this.actualConsumptionGroupTotalMt) {
      return '0%';
    }

    const combined =
      ((this.warrantedConsumptionGroupTotalMt -
        this.actualConsumptionGroupTotalMt) /
        this.actualConsumptionGroupTotalMt) *
      100;

    // Rounds 12.6 -> "13%" or 12.4 -> "12%"
    return `${Math.round(combined)}%`;
  }
  formatDesignGroupSharePercent(): string {
    if (!this.actualConsumptionGroupTotalMt) {
      return '0%';
    }

    const combined =
      ((this.designConsumptionGroupTotalMt -
        this.actualConsumptionGroupTotalMt) /
        this.actualConsumptionGroupTotalMt) *
      100;

    // Rounds 12.6 -> "13%" or 12.4 -> "12%"
    return `${Math.round(combined)}%`;
  }

  formatOptimizationGroupSharePercent(): string {
    if (!this.actualConsumptionGroupTotalMt) {
      return '0%';
    }

    const combined =
      ((this.optimizationConsumptionGroupTotalMt -
        this.actualConsumptionGroupTotalMt) /
        this.actualConsumptionGroupTotalMt) *
      100;

    return `${Math.round(combined)}%`;
  }

  /** Event Time column share of total event time (header aggregate). */
  formatEventTimeSharePercent(partDays: number | null | undefined): string {
    if (!this.totalEventTimeDays) {
      return '0%';
    }
    const part = this.toFiniteNumber(partDays);
    return `${Math.round((part / this.totalEventTimeDays) * 100)}%`;
  }

  get showSubHeaderRow(): boolean {
    // Ops Data is always visible and requires the sub-header row.
    return true;
  }

  get headerRowSpan(): number {
    return this.showSubHeaderRow ? 2 : 1;
  }

  get hasPaginatedRows(): boolean {
    return (this.paginatedData?.length ?? 0) > 0;
  }

  get mainHeaderStickyHeightPx(): number {
    return 68;
  }

  get subHeaderStickyHeightPx(): number {
    return 36;
  }

  get detailRailHeaderOffsetPx(): number {
    return this.showSubHeaderRow
      ? this.mainHeaderStickyHeightPx + this.subHeaderStickyHeightPx
      : this.mainHeaderStickyHeightPx;
  }

  getDetailRailSectionSummary(
    key: 'voyageDetails' | 'cpmetrics' | 'designkpi' | 'brob' | 'consumption',
  ): string {
    switch (key) {
      case 'voyageDetails':
        return (this.filteredData?.length ?? 0).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      case 'cpmetrics':
        return this.formatDetailRailNumeric(this.totalCpOverallMt);
      case 'designkpi':
        return this.formatDetailRailNumeric(this.totalDesignOverall);
      case 'brob':
        return this.formatDetailRailNumeric(this.getBrobSectionSumMt());
      case 'consumption':
        return this.formatDetailRailNumeric(this.totalConsumptionColumnSumMt);
      default:
        return '--';
    }
  }

  getDetailRailSectionUnit(
    key: 'voyageDetails' | 'cpmetrics' | 'designkpi' | 'brob' | 'consumption',
  ): string {
    switch (key) {
      case 'voyageDetails':
        return 'RECORDS';
      case 'cpmetrics':
      case 'designkpi':
      case 'brob':
      case 'consumption':
        return 'MT';
      default:
        return '';
    }
  }

  getDetailRailSummaryLine(
    key: 'voyageDetails' | 'cpmetrics' | 'designkpi' | 'brob' | 'consumption',
  ): string {
    const section = this.detailRailSections.find((item) => item.key === key);
    const title = section?.fullTitle ?? '';
    const value = this.getDetailRailSectionSummary(key);
    const unit = this.getDetailRailSectionUnit(key);
    return `${title} • ${value} • ${unit}`;
  }

  readonly detailRailSections: Array<{
    key: 'voyageDetails' | 'cpmetrics' | 'designkpi' | 'brob' | 'consumption';
    line1: string;
    line2: string;
    fullTitle: string;
    cssClass: string;
    expandedRowSpan: 'full' | 'main';
  }> = [
      {
        key: 'voyageDetails',
        line1: 'Voyage',
        line2: 'Details',
        fullTitle: 'Voyage Details',
        cssClass: 'voyagedetails',
        expandedRowSpan: 'main',
      },
      {
        key: 'cpmetrics',
        line1: 'C/P',
        line2: 'Overall',
        fullTitle: 'C/P Overall',
        cssClass: 'cpmetrics',
        expandedRowSpan: 'main',
      },
      {
        key: 'designkpi',
        line1: 'Design',
        line2: 'Overall',
        fullTitle: 'Design Overall',
        cssClass: 'designkpi',
        expandedRowSpan: 'main',
      },
      {
        key: 'brob',
        line1: 'Bunker',
        line2: 'ROB',
        fullTitle: 'Bunker ROB',
        cssClass: 'brob',
        expandedRowSpan: 'main',
      },
      {
        key: 'consumption',
        line1: 'Fuel',
        line2: 'Data',
        fullTitle: 'Fuel Data',
        cssClass: 'consumption',
        expandedRowSpan: 'main',
      },
    ];

  isDetailRailExpanded(
    key: 'voyageDetails' | 'cpmetrics' | 'designkpi' | 'brob' | 'consumption',
  ): boolean {
    switch (key) {
      case 'voyageDetails':
        return this.voyageDetailsSectionExpanded;
      case 'cpmetrics':
        return this.cpExpanded;
      case 'designkpi':
        return this.designExpanded;
      case 'brob':
        return this.brobSectionExpanded;
      case 'consumption':
        return this.consumptionSectionExpanded;
      default:
        return false;
    }
  }

  toggleDetailRail(
    key: 'voyageDetails' | 'cpmetrics' | 'designkpi' | 'brob' | 'consumption',
    event?: Event,
  ): void {
    switch (key) {
      case 'voyageDetails':
        this.toggleVoyageDetailsSection(event);
        break;
      case 'cpmetrics':
        this.toggleCP(event);
        break;
      case 'designkpi':
        this.toggleDesign(event);
        break;
      case 'brob':
        this.toggleBrobSection(event);
        break;
      case 'consumption':
        this.toggleConsumptionSection(event);
        break;
    }
  }

  getDetailRailSummaryColor(
    key: 'voyageDetails' | 'cpmetrics' | 'designkpi' | 'brob' | 'consumption',
  ): string | null {
    switch (key) {
      case 'cpmetrics':
        return this.getColor(this.totalCpOverallMt);
      case 'designkpi':
        return this.getColor(this.totalDesignOverall);
      case 'consumption':
        return this.getColor(this.totalConsumptionColumnSumMt);
      case 'brob':
        return '#93c5fd';
      default:
        return null;
    }
  }

  private formatDetailRailNumeric(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }
    // Match the top database stat cards (`number:'1.0-0'`) — whole numbers only.
    return Math.round(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  getBrobSectionSumMt(): number {
    const source = this.filteredData ?? this.elements ?? [];
    return source.reduce((sum: number, item: any) => {
      const arrival = item?.brob_arrival_mt ?? {};
      const departure = item?.brob_departure_mt ?? {};
      const received = item?.bunker_received ?? {};
      return (
        sum +
        this.toFiniteNumber(arrival.brob_arrival_mt_hsfo) +
        this.toFiniteNumber(arrival.brob_arrival_mt_vlsfo) +
        this.toFiniteNumber(arrival.brob_arrival_mt_lsgo) +
        this.toFiniteNumber(departure.brob_departure_mt_hsfo) +
        this.toFiniteNumber(departure.brob_departure_mt_vlsfo) +
        this.toFiniteNumber(departure.brob_departure_mt_lsgo) +
        this.toFiniteNumber(received.bunker_received_hsfo) +
        this.toFiniteNumber(received.bunker_received_vlsfo) +
        this.toFiniteNumber(received.bunker_received_lsgo)
      );
    }, 0);
  }

  get allDetailSectionsExpanded(): boolean {
    return (
      this.cpExpanded &&
      this.designExpanded &&
      this.brobSectionExpanded &&
      this.consumptionSectionExpanded &&
      this.voyageDetailsSectionExpanded
    );
  }

  toggleAllDetailSections(event?: Event): void {
    event?.stopPropagation();
    const expand = !this.allDetailSectionsExpanded;
    this.cpExpanded = expand;
    this.designExpanded = expand;
    this.brobSectionExpanded = expand;
    this.consumptionSectionExpanded = expand;
    this.voyageDetailsSectionExpanded = expand;
    this.logDetailRailDebug('toggleAllDetailSections');
  }

  triggerChartUpdate() {
    clearTimeout(this.chartTimeout);

    this.chartTimeout = setTimeout(() => {
      this.createChart();
    }, 300);
  }
  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    const target = event.target as Node;
    const el = event.target as HTMLElement | null;

    if (
      el?.closest('.pc-ms-filter') ||
      el?.closest('.pc-ms-filter__panel--overlay')
    ) {
      return;
    }

    if (this.portDropdownOpen) {
      this.closePortDropdown();
      this.updatePortLabel();
    }

    const portDropdown = document.querySelector('.excel-dropdown-port');
    const vesselDropdown = document.querySelector('.excel-dropdown-vessel');

    if (portDropdown && !portDropdown.contains(target)) {
      this.closePortDropdown();
      this.updatePortLabel();
    }

    if (vesselDropdown && !vesselDropdown.contains(target)) {
      this.vesselDropdownOpen = false;
    }

    const yearDropdown = document.querySelector('.excel-dropdown-year');
    if (yearDropdown && !yearDropdown.contains(target)) {
      this.yearDropdownOpen = false;
    }

    // DB Side Drawer Dropdowns outside click handling
    const dbOverCons = document.querySelector('.db-overcons-container');
    if (dbOverCons && !dbOverCons.contains(target)) {
      this.dbOverConsDropdownOpen = false;
    }

    const dbShip = document.querySelector('.db-ship-container');
    if (dbShip && !dbShip.contains(target)) {
      this.dbShipDropdownOpen = false;
    }

    const dbPort = document.querySelector('.db-port-container');
    if (dbPort && !dbPort.contains(target)) {
      this.dbPortDropdownOpen = false;
    }

    const dbOperation = document.querySelector('.db-operation-container');
    if (dbOperation && !dbOperation.contains(target)) {
      this.dbOperationDropdownOpen = false;
    }
  }
  selectAllPorts(): void {
    this.selectedPorts = [...this.filteredPorts];
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.updatePortLabel();
    this.rebuildPcCascadingOptions();
  }

  clearAllPorts(): void {
    this.selectedPorts = [];
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.updatePortLabel();
    this.scheduleBenchmarkChartUpdate();
  }

  onOperationChange() {
    if (!this.selectedPcOperation) return;

    // Operation changed → clear downstream selections + stale options.
    this.selectedVessels = [];
    this.selectedVesselLabel = '';
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.clearPcPortOptions();
    this.rebuildPcCascadingOptions();
    // Dependency step 3: Operation → Ports API (Client + Segment + Operation),
    // then Vessel Names for the same scope.
    this.benchmarkPort();
    this.prefetchPcBenchmarkPoolForVessels();
    this.scheduleBenchmarkChartUpdate();

    if (
      this.selectedPcOperation.toLowerCase() === 'loading' ||
      this.selectedPcOperation === 'Cargo Ops -Loading'
    ) {
      this.selectedOperation = 'Loading per day';
    } else if (
      this.selectedPcOperation.toLowerCase() === 'discharging' ||
      this.selectedPcOperation.includes('Disch') ||
      this.selectedPcOperation === 'discharging'
    ) {
      this.selectedOperation = 'Discharge per day (For non-special area)';
    } else if (this.selectedPcOperation.toLowerCase() === 'idle') {
      this.selectedOperation = 'Idle Par day';
    }
  }
  filterVessels() {
    const data = this.vessels;

    this.filteredVessels = data.filter((v: any) => {
      const segmentMatch = this.vesselMatchesSelectedPcSegment(v.segment);

      const portMatch = !this.selectedPorts || v.port === this.selectedPorts;

      return segmentMatch && portMatch;
    });
  }
  loadBenchmarkSegments() {
    let payload = {
      client: this.selectedClient,
    };
    this.prequestFormService.getbenchMarkSegments(payload).subscribe((data) => {
      // this.benchmarkSegments = data.segments;
      this.segmentSubNames = data.segments;
    });
    this.prequestFormService.getSegmentsBnch(payload).subscribe({
      next: (res: any) => {
        if (res.status === 'success' && res.segments?.length) {
          this.mergeTcpSegmentOptions(res.segments);
        }
      },
      error: () => {
        this.toastr.error('Failed to load segments');
      },
    });

    this.prequestFormService.getBenchmarkSegments(payload).subscribe({
      next: (res: any) => {
        if (res.status === 'success' && res.segments?.length) {
          this.setPcSegmentOptionsFromApi(res.segments);
          if (this.graph) {
            this.loadBenchmarkCharts();
          }
        }
      },
      error: () => {
        this.toastr.error('Failed to load segments');
      },
    });
  }
  // benchmarkPort() {

  //   let payload = {
  //     segment: [this.selectedPcSegment],
  //     client: this.selectedClient,
  //     operation: this.selectedPcOperation,
  //   };

  //   this.prequestFormService.getbenchMarkPorts(payload).subscribe((data: any) => {


  //     this.benchmarkPorts = Object.keys(data.ports).map(p => p.trim());
  //     // ⭐ dropdown update
  //     this.filteredPorts = [...this.benchmarkPorts];

  //   });

  // }
  toProperCase(value: string): string {
    if (!value) return '';
    value = value.toLowerCase().trim();
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  formatPortName(name: string): string {
    return name
      .replace(/[_/,]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  onSegmentChange(): void {
    this.filterVesselsBySegment();
  }

  /** TCP benchmark filters — uses selectedSegment (not PC segment). */
  onTcpSegmentChange(): void {
    this.syncTcpSegmentSelection();
    this.selectedTcpVesselNames = [];
    this.selectedTcpPorts = [];
    this.selectedTcpYards = [];
    this.selectedTcpBuiltAges = [];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpOperationChange(): void {
    this.selectedTcpVesselNames = [];
    this.selectedTcpPorts = [];
    this.selectedTcpYards = [];
    this.selectedTcpBuiltAges = [];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpBenchmarkFilterChange(): void {
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpClientSelectionChange(): void {
    this.selectedTcpVesselNames = [];
    this.selectedTcpPorts = [];
    this.selectedTcpYards = [];
    this.selectedTcpBuiltAges = [];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpVesselSelectionChange(): void {
    this.selectedTcpPorts = [];
    this.selectedTcpYards = [];
    this.selectedTcpBuiltAges = [];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpPortSelectionChange(): void {
    this.selectedTcpYards = [];
    this.selectedTcpBuiltAges = [];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  private loadBenchmarkCharts(): void {
    this.ensureValidPcOperationSelection();
    this.logBench('loadBenchmarkCharts', {
      graph: this.graph,
      ports: this.getPcPortPayload(),
      operation: this.selectedPcOperation,
      tcpSegment: this.selectedSegment,
      tcpOperation: this.selectedOperation,
    });
    this.createTcpChart();
    this.createChart();
  }

  togglePcInlineFilters(): void {
    this.pcInlineFilterOpen = !this.pcInlineFilterOpen;
    this.tcpInlineFilterOpen = false;
    if (this.pcInlineFilterOpen) {
      if (this.clientOptions.length) {
        this.mergePcClientOptions(this.clientOptions);
      }
      if (this.getPcCascadingPool().length) {
        const opts = extractPcBenchmarkFilterOptions(this.getPcCascadingPool());
        this.mergePcClientOptions(opts.clients);
      }
      if (!this.pcMasterSegmentOptions.length) {
        this.loadBenchmarkSegments();
      }
      this.rebuildPcSegmentDropdownOptions();
      this.rebuildPcCascadingOptions();
      this.cdr.markForCheck();
    } else {
      this.closePortDropdown();
    }
  }

  toggleTcpInlineFilters(): void {
    this.tcpInlineFilterOpen = !this.tcpInlineFilterOpen;
    this.pcInlineFilterOpen = false;
    if (this.tcpInlineFilterOpen) {
      if (!this.tcpMasterSegmentOptions.length) {
        this.loadTcpBenchmarkSegments();
      }
      this.rebuildTcpSegmentDropdownOptions();
      this.ensureTcpOperationOptions();
      this.rebuildTcpCascadingOptions();
      this.cdr.markForCheck();
    }
  }

  closePcFilterDrawer(): void {
    this.pcInlineFilterOpen = false;
    this.closePortDropdown();
  }

  closeTcpFilterDrawer(): void {
    this.tcpInlineFilterOpen = false;
  }

  closeBenchmarkFilterDrawers(): void {
    this.closePcFilterDrawer();
    this.closeTcpFilterDrawer();
  }

  applyPcBenchmarkFilters(): void {
    this.ensureValidPcOperationSelection();
    this.syncPcBenchmarkClientFromFilters();
    this.logBench('applyPcBenchmarkFilters');
    // The Port API is gated behind Vessel selection, so Apply only (re)draws the
    // chart with the current filters. No ports are fetched here.
    this.createChart();
    this.closePcFilterDrawer();
  }

  applyTcpBenchmarkFilters(): void {
    this.logBench('applyTcpBenchmarkFilters');
    this.createTcpChart();
    this.closeTcpFilterDrawer();
  }

  resetPcChartDisplay(): void {
    this.isSorting = false;
    this.sortMapData = false;
    this.azsort = false;
    this.isAscending = false;
    if (this.pcData?.length) {
      this.buildPcChartFromData();
    }
  }

  resetTcpChartDisplay(): void {
    this.isSorting = false;
    this.sortMapData = false;
    this.azsort = false;
    this.isAscending = false;
    if (this.tcpData?.length) {
      const sortedData = [...this.tcpData].sort((a: any, b: any) =>
        (a.vessel_name || '').localeCompare(b.vessel_name || ''),
      );
      this.applyTcpChartFromData(sortedData);
    }
  }

  clearPcBenchmarkFilters(): void {
    this.resetPcBenchmarkFiltersToDefaults({ reloadChart: true });
    this.cdr.markForCheck();
  }

  private isAllPcClientsSelected(): boolean {
    const opts = this.pcClientOptions || [];
    const selected = this.selectedPcClients || [];
    if (!opts.length || !selected.length) {
      return true;
    }
    return opts.every((client) => selected.includes(client));
  }

  get pcPortOptions(): string[] {
    // Ports come exclusively from the Port API (triggered after Operations).
    // No pool/master fallback so stale ports never leak into the dropdown.
    return [...this.pcScopedPortOptions];
  }

  private mergePcMasterPortOptions(ports: string[]): void {
    const merged = extractBenchmarkPortNamesFromApi([
      ...this.pcMasterPortOptions,
      ...ports,
    ]);
    if (!merged.length) {
      return;
    }
    this.pcMasterPortOptions = merged;
  }

  private applyPcBenchmarkPortsApiResponse(data: any): void {
    const portNames = extractBenchmarkPortNamesFromApi(data?.ports);
    if (!portNames.length) {
      // No ports for this Client + Segment + Operation (+ Vessel) combination —
      // clear stale options and invalid selections rather than keeping old data.
      this.pcScopedPortOptions = [];
      this.benchmarkPorts = [];
      this.filteredPorts = [];
      this.selectedPorts = [];
      this.updatePortLabel();
      return;
    }
    this.mergePcMasterPortOptions(portNames);
    this.pcScopedPortOptions = [...portNames];
    this.benchmarkPorts = [...portNames];
    this.filteredPorts = [...portNames];
    this.prunePcPortSelections();
  }

  private getPcClientPayload(): string | string[] {
    if (this.isAllPcClientsSelected()) {
      return 'ALL';
    }
    if (this.selectedPcClients.length === 1) {
      return this.selectedPcClients[0];
    }
    return this.selectedPcClients;
  }

  onPcClientsChanged(clients: string[]): void {
    this.selectedPcClients = [...clients];
    this.syncPcBenchmarkClientFromFilters();
    // Per-toggle: just re-scope the visible options. The Segment API call and the
    // rest of the cascade run on dropdown close (onPcClientSelectionChange).
    this.rebuildPcCascadingOptions();
    this.scheduleBenchmarkChartUpdate();
  }

  onPcVesselsChanged(vessels: string[]): void {
    this.selectedVessels = [...vessels];
    this.closePortDropdown();
    // Do not clear Port options here — Ports load after Operations (before Vessel).
    // On dropdown close we may re-fetch to refine; selections are pruned then.
    this.rebuildPcCascadingOptions();
    this.scheduleBenchmarkChartUpdate();
  }

  onPcPortsChanged(ports: string[]): void {
    this.selectedPorts = [...(ports || [])];
    this.syncBenchmarkPortsToTableFilter(this.selectedPorts);
    this.updatePortLabel();
    this.scheduleBenchmarkChartUpdate();
    if (!this.suppressFilterCascade) {
      this.scheduleLiveTableFilter();
    }
  }

  onPcPortSelectionChange(): void {
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.updatePortLabel();
    this.scheduleBenchmarkChartUpdate();
  }

  onPcClientSelectionChange(): void {
    this.syncPcBenchmarkClientFromFilters();
    // Client changed → clear ALL downstream selections + stale options.
    this.selectedVessels = [];
    this.selectedVesselLabel = '';
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.clearPcPortOptions();
    this.rebuildPcCascadingOptions();
    // Dependency step 1: Client → Segment API → then Operations → Ports → Vessels.
    this.loadPcSegmentsForSelectedClient();
    this.scheduleBenchmarkChartUpdate();
  }

  onPcVesselSelectionChange(): void {
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.updatePortLabel();
    this.rebuildPcCascadingOptions();
    // Vessel selected → re-fetch Ports narrowed by
    // Client + Segment + Operation + Vessel (optional refine after Ops).
    this.benchmarkPort();
    this.scheduleBenchmarkChartUpdate();
  }

  onPcYardsChanged(yards: string[]): void {
    this.selectedPcYards = [...yards];
    this.selectedPcBuiltAges = [];
    this.rebuildPcCascadingOptions();
    this.scheduleBenchmarkChartUpdate();
  }

  onPcBuiltAgesChanged(ages: string[]): void {
    this.selectedPcBuiltAges = [...(ages || [])].map(String);
    this.rebuildPcCascadingOptions();
    this.scheduleBenchmarkChartUpdate();
  }

  onTcpClientsChanged(clients: string[]): void {
    this.selectedTcpClients = [...clients];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpVesselsChanged(vessels: string[]): void {
    this.selectedTcpVesselNames = [...vessels];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpPortsChanged(ports: string[]): void {
    this.selectedTcpPorts = [...ports];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpYardsChanged(yards: string[]): void {
    this.selectedTcpYards = [...yards];
    this.selectedTcpBuiltAges = [];
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  onTcpBuiltAgesChanged(ages: string[]): void {
    this.selectedTcpBuiltAges = [...(ages || [])].map(String);
    this.rebuildTcpCascadingOptions();
    this.scheduleTcpChartUpdate();
  }

  private isAllPcVesselsSelected(): boolean {
    const opts = this.pcVesselNameOptions || [];
    const selected = this.selectedVessels || [];
    if (!opts.length || !selected.length) {
      return false;
    }
    return opts.every((name) =>
      selected.some(
        (value) => value.toLowerCase() === String(name).toLowerCase(),
      ),
    );
  }

  private isAllPcPortsSelected(): boolean {
    const opts = this.pcPortOptions || [];
    const selected = this.selectedPorts || [];
    if (!opts.length || !selected.length) {
      return false;
    }
    return opts.every((port) =>
      selected.some(
        (value) => value.toLowerCase() === String(port).toLowerCase(),
      ),
    );
  }

  /**
   * Resolve the selected PC Benchmarking segment option.
   * UI stores/displays `key`; API payloads must use the full `value` array.
   */
  private getSelectedPcSegmentOption(): {
    key: string;
    value: string[];
  } | null {
    if (!this.selectedPcSegment) {
      return null;
    }
    const pools = [this.pcSegments, this.pcMasterSegmentOptions];
    for (const pool of pools) {
      const match = (pool || []).find((segment: { key: string }) =>
        benchmarkSegmentKeysMatch(segment.key, this.selectedPcSegment),
      );
      if (match) {
        return match;
      }
    }
    return null;
  }

  private getSegmentPayloadArray(segment: string): string[] {
    if (!segment) return [];

    const normalizedSelected = segment.replace(/\s+/g, '').toLowerCase();

    // 1. Try segmentApiValueMap
    const mapKey = Object.keys(this.segmentApiValueMap || {}).find(
      (k) => k.replace(/\s+/g, '').toLowerCase() === normalizedSelected
    );
    if (mapKey && this.segmentApiValueMap[mapKey]) {
      const val = this.segmentApiValueMap[mapKey];
      return Array.isArray(val) ? [...val] : [val];
    }

    // 2. Try PC Master Segment Options
    const pcSeg = this.pcMasterSegmentOptions.find(
      (s) => s.key.replace(/\s+/g, '').toLowerCase() === normalizedSelected
    );
    if (pcSeg?.value?.length) {
      return [...pcSeg.value];
    }

    // 3. Fallbacks for known segments that require all aliases
    if (normalizedSelected === 'lr3/suezmax' || normalizedSelected === 'suezmax') {
      return ["LR3/Suezmax", "Lr3/Suezmax", "SUEZMAX"];
    }

    // 4. Default: remove spaces around slashes to avoid "LR3 / Suezmax" bug
    return [segment.replace(/\s*\/\s*/g, '/')];
  }

  /** API payload: always send the full array of aliases to match user request. */
  private getPcSegmentApiPayload(): string[] {
    return this.getSegmentPayloadArray(this.selectedPcSegment);
  }

  /**
   * PC chart note (left of toolbar):
   * MR / MR2 family → 10,000 m³; all other segments → 100,000 m³.
   */
  get pcCargoNormalizationVolume(): string {
    const segment = String(this.selectedPcSegment ?? '').trim();
    if (segment && benchmarkSegmentKeysMatch(segment, 'MR')) {
      return '10,000 m³';
    }
    return '100,000 m³';
  }

  get pcCargoNormalizationLabel(): string {
    return `Cargo normalized for ${this.pcCargoNormalizationVolume}`;
  }

  /** PC chart Y-axis: Consumption/{segment multiplier}/24Hrs */
  get pcConsumptionYAxisTitle(): string {
    return `Consumption/${this.pcCargoNormalizationVolume}/24Hrs`;
  }

  /** Local row matching: matches against both mapped value and display key. */
  private getPcSegmentFilterValues(): string[] {
    const payload = this.getPcSegmentApiPayload();
    if (this.selectedPcSegment && !payload.includes(this.selectedPcSegment)) {
      return [...payload, this.selectedPcSegment];
    }
    return payload;
  }

  private getPcPortPayload(): string[] {
    const available = this.pcPortOptions;
    if (!this.selectedPorts?.length) {
      return [...available];
    }
    return [...this.selectedPorts];
  }

  private getPcVesselPayload(): string[] {
    const available = this.pcVesselNameOptions || [];
    if (!this.selectedVessels?.length) {
      return [...available];
    }
    return [...this.selectedVessels];
  }

  private syncPcBenchmarkClientFromFilters(): void {
    const clientPayload = this.getPcClientPayload();
    if (clientPayload === 'ALL') {
      this.selectedClient = 'ALL';
      return;
    }
    if (Array.isArray(clientPayload)) {
      this.selectedClient =
        clientPayload.length === 1 ? clientPayload[0] : 'ALL';
      return;
    }
    this.selectedClient = clientPayload;
  }

  private buildPcBenchmarkApiPayload(): Record<string, unknown> {
    const operation =
      this.selectedPcOperation ||
      this.operationsOptions[0] ||
      'Cargo Ops -Discharging';
    return {
      port_name: this.getPcPortPayload(),
      vessel_name: this.getPcVesselPayload(),
      segment: this.getPcSegmentApiPayload(),
      operation,
      client: this.getPcClientPayload(),
    };
  }

  /** API payload: always send the full array of aliases to match user request. */
  private getTcpSegmentApiPayload(): string[] {
    return this.getSegmentPayloadArray(this.selectedSegment);
  }

  /** Local row matching: includes segment aliases for the selected key. */
  private getTcpSegmentFilterValues(): string[] {
    const selectedSeg = this.benchmarkSegments?.find((segment) =>
      benchmarkSegmentKeysMatch(segment.key, this.selectedSegment),
    );
    if (selectedSeg?.value?.length) {
      return [...new Set(selectedSeg.value as string[])];
    }
    return this.selectedSegment ? [this.selectedSegment] : [];
  }

  private getTcpPortPayload(): string[] {
    const available = this.tcpPortOptions || [];
    if (!this.selectedTcpPorts?.length) {
      return [...available];
    }
    return [...this.selectedTcpPorts];
  }

  private getTcpVesselPayload(): string[] {
    const available = this.tcpVesselNameOptions || [];
    if (!this.selectedTcpVesselNames?.length) {
      return [...available];
    }
    return [...this.selectedTcpVesselNames];
  }

  private getTcpYardPayload(): string[] {
    const options = this.tcpYardOptions || [];
    if (this.isAllTcpYardsSelected()) {
      return [...options];
    }
    return [...(this.selectedTcpYards || [])];
  }

  private isAllTcpClientsSelected(): boolean {
    const opts = this.tcpClientOptions || [];
    const selected = this.selectedTcpClients || [];
    if (!opts.length || !selected.length) {
      return true;
    }
    return opts.every((client) => selected.includes(client));
  }

  private isAllTcpVesselsSelected(): boolean {
    const opts = this.tcpVesselNameOptions || [];
    const selected = this.selectedTcpVesselNames || [];
    if (!opts.length || !selected.length) {
      return false;
    }
    return opts.every((name) =>
      selected.some(
        (value) => value.toLowerCase() === String(name).toLowerCase(),
      ),
    );
  }

  private isAllTcpYardsSelected(): boolean {
    const opts = this.tcpYardOptions || [];
    const selected = this.selectedTcpYards || [];
    if (!opts.length || !selected.length) {
      return true;
    }
    return opts.every((yard) => selected.includes(yard));
  }

  private isAllTcpPortsSelected(): boolean {
    const opts = this.tcpPortOptions || [];
    const selected = this.selectedTcpPorts || [];
    if (!opts.length || !selected.length) {
      return false;
    }
    return opts.every((port) =>
      selected.some(
        (value) => value.toLowerCase() === String(port).toLowerCase(),
      ),
    );
  }

  private isAllPcYardsSelected(): boolean {
    const opts = this.pcYardOptions || [];
    const selected = this.selectedPcYards || [];
    if (!opts.length || !selected.length) {
      return true;
    }
    return opts.every((yard) => selected.includes(yard));
  }

  private isAllPcBuiltAgesSelected(): boolean {
    const opts = this.pcBuiltAgeOptions || [];
    const selected = this.selectedPcBuiltAges || [];
    if (!opts.length || !selected.length) {
      return true;
    }
    return opts.every((age) => selected.includes(String(age)));
  }

  private isAllTcpBuiltAgesSelected(): boolean {
    const opts = this.tcpBuiltAgeOptions || [];
    const selected = this.selectedTcpBuiltAges || [];
    if (!opts.length || !selected.length) {
      return true;
    }
    return opts.every((age) => selected.includes(String(age)));
  }

  private getTcpCatalogForSelectedClients() {
    if (this.isAllTcpClientsSelected()) {
      return this.tcpVesselCatalog;
    }
    return this.tcpVesselCatalog.filter((entry) =>
      matchesAnyClient(entry.client, this.selectedTcpClients),
    );
  }

  private refreshTcpVesselNameOptions(): void {
    const scoped = this.getTcpCatalogForSelectedClients();
    this.tcpVesselNameOptions = Array.from(
      new Set(scoped.map((entry) => entry.vessel_name).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
    this.selectedTcpVesselNames = this.selectedTcpVesselNames.filter((name) =>
      this.tcpVesselNameOptions.includes(name),
    );
  }

  private applySingleVesselTcpDefaults(vesselName: string): void {
    const entry = this.tcpVesselCatalog.find(
      (item) => item.vessel_name === vesselName,
    );
    if (!entry) {
      return;
    }

    if (entry.ship_yard) {
      this.selectedTcpYards = [entry.ship_yard];
    }
    if (entry.segment) {
      const segment = this.benchmarkSegments?.find((item: { key: string }) =>
        benchmarkSegmentKeysMatch(item.key, entry.segment || ''),
      );
      if (segment?.key) {
        this.selectedSegment = segment.key;
      }
    }
  }

  private refreshTcpDependentOptions(): void {
    this.rebuildTcpCascadingOptions();
  }

  private coerceStringList(value: unknown): string[] {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string') {
            return item.trim();
          }
          if (item && typeof item === 'object') {
            const record = item as Record<string, unknown>;
            return String(
              record['client'] ??
              record['client_name'] ??
              record['vessel_name'] ??
              record['name'] ??
              record['value'] ??
              '',
            ).trim();
          }
          return String(item ?? '').trim();
        })
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      return value.trim() ? [value.trim()] : [];
    }
    return [];
  }

  private uniqueSorted(values: string[]): string[] {
    return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
      a.localeCompare(b),
    );
  }

  private uniqueSortedVesselNames(values: string[]): string[] {
    const byKey = new Map<string, string>();
    for (const value of values) {
      const trimmed = String(value || '').trim();
      if (!trimmed) {
        continue;
      }
      const key = trimmed.toLowerCase();
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, trimmed);
        continue;
      }
      if (
        existing === existing.toUpperCase() &&
        trimmed !== trimmed.toUpperCase()
      ) {
        byKey.set(key, trimmed);
      }
    }
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
  }

  private buildTcpVesselCatalogFromRows(rows: any[]): void {
    const catalog = new Map<string, (typeof this.tcpVesselCatalog)[number]>();
    rows.forEach((record) => {
      const vessel_name = String(record?.vessel_name || '').trim();
      if (!vessel_name) {
        return;
      }
      const key = `${vessel_name}::${String(record?.client || '').toLowerCase()}`;
      catalog.set(key, {
        vessel_name,
        client: String(record?.client || '').trim(),
        vessel_type: String(
          record?.vessel_type || record?.type_of_vessel || '',
        ).trim(),
        ship_yard: String(
          record?.yard || record?.ship_yard || record?.yard_name || '',
        ).trim(),
        date_built: String(
          record?.ship_built_from ||
          record?.date_built ||
          record?.year_built ||
          '',
        ).trim(),
        segment: String(record?.vessel_class || record?.segment || '').trim(),
      });
    });
    this.tcpVesselCatalog = Array.from(catalog.values());
    this.tcpVesselCatalog.forEach((entry) => {
      this.vesselMetaByName.set(entry.vessel_name.toLowerCase(), {
        vessel_type: entry.vessel_type,
        ship_yard: entry.ship_yard,
        date_built: entry.date_built,
        client: entry.client,
      });
    });
  }

  private getTcpPoolScopedBySelections(
    includeSegment = true,
    includeOperation = true,
  ): any[] {
    if (includeSegment && includeOperation) {
      return this.getTcpPoolScoped('chart');
    }
    let scoped = this.applyTcpClientFilter([...this.getTcpCascadingPool()]);
    scoped = this.applyTcpVesselFilter(scoped);
    if (includeSegment) {
      scoped = this.applyTcpSegmentFilter(scoped);
    }
    if (includeOperation) {
      scoped = this.applyTcpOperationFilter(scoped);
    }
    return scoped;
  }

  private getCatalogVesselNamesForTcpSegment(): string[] {
    const fromTracker = this.uniqueSorted(
      (this.vessels || [])
        .filter((v: any) => {
          if (
            !this.isAllTcpClientsSelected() &&
            !matchesAnyClient(v.client, this.selectedTcpClients)
          ) {
            return false;
          }
          return this.vesselMatchesSelectedTcpSegment(v.segment);
        })
        .map((v: any) => String(v?.name || '').trim())
        .filter(Boolean),
    );

    const fromCatalog = this.uniqueSorted(
      (this.tcpVesselCatalog || [])
        .filter((entry) => {
          if (
            !this.isAllTcpClientsSelected() &&
            !matchesAnyClient(entry.client, this.selectedTcpClients)
          ) {
            return false;
          }
          return this.vesselMatchesSelectedTcpSegment(entry.segment);
        })
        .map((entry) => entry.vessel_name)
        .filter(Boolean),
    );

    return this.uniqueSorted([...fromTracker, ...fromCatalog]);
  }

  private vesselMatchesSelectedTcpSegment(
    vesselSegment: string | null | undefined,
  ): boolean {
    if (!this.selectedSegment) {
      return true;
    }
    const segmentValues = this.getTcpSegmentFilterValues();
    return matchesAnySegment(
      vesselSegment || '',
      segmentValues.length ? segmentValues : [this.selectedSegment],
    );
  }

  private rebuildTcpScopedPortOptions(portScoped: any[] = []): void {
    const poolPorts = dedupeFilterOptions(
      (portScoped || [])
        .map((record) => String(record?.port_name || record?.port || '').trim())
        .filter(Boolean),
    );
    const catalogPorts = this.getCatalogPortsForTcpFilters();
    const merged = dedupeFilterOptions([
      ...poolPorts,
      ...catalogPorts,
      ...this.tcpMasterPortOptions,
    ]);
    this.tcpPortOptions = merged;
    this.pruneTcpPortSelections();
  }

  private mergeTcpMasterPortOptions(ports: string[]): void {
    const merged = dedupeFilterOptions([
      ...this.tcpMasterPortOptions,
      ...ports,
    ]);
    if (!merged.length) {
      return;
    }
    this.tcpMasterPortOptions = merged;
  }

  private pruneTcpPortSelections(): void {
    const options = this.tcpPortOptions || [];
    if (!options.length) {
      return;
    }
    this.selectedTcpPorts = (this.selectedTcpPorts || []).filter((port) =>
      options.some(
        (option) => option.toLowerCase() === String(port).toLowerCase(),
      ),
    );
  }

  /** Ports from tracker records scoped by current TCP benchmark filters. */
  private getCatalogPortsForTcpFilters(): string[] {
    let records = Array.isArray(this.elements) ? [...this.elements] : [];
    if (!records.length) {
      return [];
    }

    if (!this.isAllTcpClientsSelected()) {
      records = records.filter((record) =>
        matchesAnyClient(record?.client, this.selectedTcpClients),
      );
    }

    if (this.selectedSegment) {
      const segmentValues = this.getTcpSegmentFilterValues();
      records = records.filter((record) =>
        matchesAnySegment(
          record?.segment || record?.vessel_class || '',
          segmentValues.length ? segmentValues : [this.selectedSegment],
        ),
      );
    }

    if (
      this.selectedTcpVesselNames?.length &&
      !this.isAllTcpVesselsSelected()
    ) {
      records = records.filter((record) =>
        matchesSelectedVesselNames(
          record?.vessel_name,
          this.selectedTcpVesselNames,
        ),
      );
    }

    return dedupeFilterOptions(
      records
        .map((record) => String(record?.port || record?.port_name || '').trim())
        .filter(Boolean),
    );
  }

  private coerceTcpRootPortList(value: unknown): string[] {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return dedupeFilterOptions(
        value.map((item) => {
          if (typeof item === 'string') {
            return item.trim();
          }
          if (item && typeof item === 'object') {
            const record = item as Record<string, unknown>;
            return String(
              record['port_name'] ??
              record['port'] ??
              record['name'] ??
              record['value'] ??
              '',
            ).trim();
          }
          return String(item ?? '').trim();
        }),
      );
    }
    if (typeof value === 'string') {
      return value.trim() ? [value.trim()] : [];
    }
    return [];
  }

  private rebuildTcpCascadingOptions(): void {
    if (!this.getTcpCascadingPool()?.length) {
      this.ensureTcpOperationOptions();
      return;
    }

    const allClients = extractClientOptionsFromRecords(
      this.getTcpCascadingPool().map((record) => ({
        client: record?.client,
      })),
    );
    if (allClients.length) {
      this.tcpClientOptions = allClients;
    }

    this.syncTcpSegmentDisplayOptions();

    const operationsScoped = this.getTcpPoolScoped('operations');
    const operationValues = dedupeFilterOptions(
      operationsScoped
        .map((record) =>
          String(record?.port_consumption_parameter || '').trim(),
        )
        .filter(Boolean),
    );
    if (operationValues.length) {
      this.mergeTcpOperationOptions(operationValues);
    }
    this.ensureTcpOperationOptions();

    const vesselScoped = this.getTcpPoolScoped('vessel');
    let vesselNames = this.uniqueSortedVesselNames(
      vesselScoped
        .map((record) => String(record?.vessel_name || ''))
        .filter(Boolean),
    );
    if (!vesselNames.length) {
      vesselNames = this.getCatalogVesselNamesForTcpSegment();
    }
    this.tcpVesselNameOptions = vesselNames;
    this.selectedTcpVesselNames = this.selectedTcpVesselNames.filter((name) =>
      this.tcpVesselNameOptions.includes(name),
    );

    const portScoped = this.getTcpPoolScoped('port');
    this.rebuildTcpScopedPortOptions(portScoped);

    const yardScoped = this.getTcpPoolScoped('yard');
    this.tcpYardOptions = dedupeFilterOptions(
      yardScoped
        .map((record) =>
          extractRecordYard(record, this.getVesselMeta(record?.vessel_name)),
        )
        .filter(Boolean),
    );
    this.selectedTcpYards = this.selectedTcpYards.filter((yard) =>
      this.tcpYardOptions.includes(yard),
    );

    const ageScoped = this.getTcpPoolScoped('age');
    this.bindTcpBuiltAgeOptionsFromRows(ageScoped);

    this.logFilterOptions('TCP Cascading Options', {
      clients: this.tcpClientOptions.length,
      vessels: this.tcpVesselNameOptions.length,
      segments: this.benchmarkSegments.length,
      ports: this.tcpPortOptions.length,
      operations: this.tcpOperationOptions.length,
      yards: this.tcpYardOptions.length,
      builtAges: this.tcpBuiltAgeOptions.length,
    });
    this.cdr.markForCheck();
  }

  private bindTcpBuiltAgeOptionsFromRows(rows: any[]): void {
    const ages = Array.from(
      new Set(
        (rows || [])
          .map((record) =>
            getVesselAgeYearsFromBuildDate(extractRecordBuildYear(record)),
          )
          .filter((age): age is number => age !== null),
      ),
    ).sort((a, b) => a - b);

    this.tcpBuiltAgeOptions = ages.map(String);
    this.tcpBuiltAgeOptionItems = ages.map((age) => ({
      value: String(age),
      label: formatVesselAgeLabel(age),
    }));
    this.selectedTcpBuiltAges = this.selectedTcpBuiltAges.filter((age) =>
      this.tcpBuiltAgeOptions.includes(String(age)),
    );
  }

  private ingestTcpBenchmarkResponse(data: any, refreshChart = true): void {
    const rows = this.resolveUsernameScopedTcpData(data);
    console.log('TCP Response', data);
    this.populateTcpFilterOptionsFromRows(rows, data);

    if (refreshChart && rows.length) {
      const filtered = this.filterTcpRecords(rows);
      this.tcpData = filtered.length ? filtered : rows;
      const sortedData = [...this.tcpData].sort((a: any, b: any) =>
        (a.vessel_name || '').localeCompare(b.vessel_name || ''),
      );
      this.applyTcpChartFromData(sortedData);
      this.logFilterOptions('TCP Chart Data', {
        labels: sortedData.length,
        chartReady: !!this.tcpChartOptions,
      });
    }
  }

  loadTcpBenchmarkFilters(): void {
    this.logBench('loadTcpBenchmarkFilters');
    this.createTcpChart();
  }

  clearAllTcpBenchmarkFilters(): void {
    this.logBench('clearAllTcpBenchmarkFilters');
    this.resetTcpBenchmarkFiltersToDefaults({ reloadChart: true });
    this.cdr.markForCheck();
  }

  clearTcpBenchmarkFilters(): void {
    this.clearAllTcpBenchmarkFilters();
  }

  private getVesselMeta(vesselName: string) {
    return this.vesselMetaByName.get(
      String(vesselName || '')
        .trim()
        .toLowerCase(),
    );
  }

  private resolveTcpShipBuiltDate(record: any): Date | null {
    return (
      parseBenchmarkBuiltDate(record?.ship_built_from) ??
      parseBenchmarkBuiltDate(record?.ship_built_to) ??
      parseBenchmarkBuiltDate(record?.date_built ?? record?.year_built)
    );
  }

  private matchesTcpShipBuiltRange(
    record: any,
    from?: string | null,
    to?: string | null,
  ): boolean {
    if (!from && !to) return true;
    const builtDate = this.resolveTcpShipBuiltDate(record);
    if (!builtDate) return false;

    if (from) {
      const fromDate = new Date(from);
      fromDate.setHours(0, 0, 0, 0);
      if (!Number.isNaN(fromDate.getTime()) && builtDate < fromDate)
        return false;
    }
    if (to) {
      const toDate = new Date(to);
      if (!Number.isNaN(toDate.getTime())) {
        toDate.setHours(23, 59, 59, 999);
        if (builtDate > toDate) return false;
      }
    }
    return true;
  }

  private resolveUsernameScopedTcpData(data: any): any[] {
    let rows = resolveTcpBenchmarkRows(data);
    rows = this.filterDemoVesselsOnly(rows);

    const clientFilters: { [key: string]: (item: any) => boolean } = {
      'Eiger Shipping': (item) =>
        item.client &&
        (item.client === 'Northwell' || item.client.toLowerCase() === 'eiger'),
      'Zeronorth ZN': (item) =>
        item.client && item.client.toLowerCase() === 'zeronorth',
      'Elite Shipping': (item) =>
        item.client && item.client.toLowerCase() === 'elite tankship dmcc',
      'Bunge Shipping': (item) =>
        item.client && item.client.toLowerCase() === 'bunge',
      'Alcor Shipping': (item) =>
        item.client?.toLowerCase() === 'alcor chartering dmcc',
      'EMZoil Shipping': (item) =>
        item.client && (item.client.toLowerCase() === 'emz oil' || item.client.toLowerCase() === 'emzoil fzco'),
      'Ethiopian Shipping & Logistics': (item) =>
        item.client?.toLowerCase() === 'ethiopian shipping & logistics',
    };

    if (this.username === 'Meraki - Operations') {
      return rows;
    }
    if (clientFilters[this.username]) {
      return rows.filter(clientFilters[this.username]);
    }
    return rows;
  }

  private buildTcpApiPayload(): Record<string, unknown> {
    const operation =
      this.selectedOperation ||
      this.tcpOperationOptions[0] ||
      this.operations[1]?.value ||
      'Loading per day';
    const payload: Record<string, unknown> = {
      vessel_class: this.getTcpSegmentApiPayload(),
      port_consumption_parameter: operation,
      vessel_name: this.getTcpVesselPayload(),
      port_name: this.getTcpPortPayload(),
      yard: this.getTcpYardPayload(),
    };
    if (!this.isAllTcpClientsSelected()) {
      payload['client'] = this.selectedTcpClients;
    }
    return payload;
  }

  private applyPcChartRowFilters(rows: any[]): any[] {
    let scoped = this.applyPcClientFilter(rows);
    scoped = this.applyPcSegmentFilter(scoped);
    scoped = this.applyPcVesselFilter(scoped);
    scoped = this.applyPcPortFilter(scoped);
    scoped = this.applyPcYardFilter(scoped);
    scoped = this.applyPcOperationFilter(scoped);
    return this.applyPcShipBuiltFilter(scoped);
  }

  private resolveTcpRecordDate(record: any): Date | null {
    const raw =
      record?.report_date ??
      record?.consumption_date ??
      record?.arrival_date ??
      record?.departure_date ??
      record?.data?.date ??
      record?.data?.report_date ??
      null;
    if (!raw) {
      const meta = this.getVesselMeta(record?.vessel_name);
      return parseBenchmarkBuiltDate(
        meta?.date_built || record?.date_built || record?.year_built,
      );
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private filterTcpRecords(records: any[]): any[] {
    const scopedKeys = new Set(
      this.getTcpPoolScoped('chart').map(
        (record) =>
          `${record?.vessel_name}::${record?.vessel_imo ?? ''}::${record?.client ?? ''}`,
      ),
    );
    return records.filter((record) =>
      scopedKeys.has(
        `${record?.vessel_name}::${record?.vessel_imo ?? ''}::${record?.client ?? ''}`,
      ),
    );
  }

  onSegmentPcChange() {
    this.syncPcSegmentSelection();
    this.syncPcBenchmarkClientFromFilters();
    // Segment changed → clear downstream selections + stale options.
    this.selectedVessels = [];
    this.selectedVesselLabel = '';
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.clearPcPortOptions();
    this.rebuildPcCascadingOptions();
    // Dependency step 2: Segment → Operations API → then Ports (after Ops) → Vessels.
    this.benchmarkOperations();
    this.scheduleBenchmarkChartUpdate();
  }

  private roundMt(value: number, decimals = 2): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  private formatBenchmarkTooltip(
    title: string,
    consumption: number,
    avg: number,
    rows: { label: string; value: string }[] = [],
  ): string {
    const theme = this.getBenchmarkChartTheme();
    const detailRows = rows
      .map(
        (r) =>
          `<div style="display:flex;justify-content:space-between;gap:12px;margin-top:4px;">` +
          `<span style="color:${theme.tooltipMuted};">${r.label}</span>` +
          `<b style="color:${theme.tooltipText};">${r.value}</b></div>`,
      )
      .join('');
    return (
      `<div style="min-width:160px;padding:2px 0;">` +
      `<div style="font-size:13px;font-weight:600;color:${theme.tooltipTitle};margin-bottom:6px;">${title}</div>` +
      `<div style="display:flex;justify-content:space-between;gap:12px;">` +
      `<span style="color:${theme.tooltipMuted};">Consumption</span>` +
      `<b style="color:#5b8fc9;">${consumption.toFixed(2)} MT</b></div>` +
      detailRows +
      `<div style="display:flex;justify-content:space-between;gap:12px;margin-top:6px;padding-top:6px;border-top:1px solid ${theme.tooltipBorder};">` +
      `<span style="color:${theme.tooltipMuted};">Average</span>` +
      `<b style="color:#fbbf24;">${avg} MT</b></div></div>`
    );
  }

  private sortChartSeries(
    categories: string[],
    values: number[],
  ): { categories: string[]; values: number[] } {
    const pairs = categories.map((c, i) => ({ c, v: values[i] }));
    if (this.isSorting && this.sortMapData) {
      pairs.sort((a, b) => b.v - a.v);
    } else if (this.isSorting && !this.sortMapData) {
      pairs.sort((a, b) => a.v - b.v);
    } else if (this.azsort && this.isAscending) {
      pairs.sort((a, b) => a.c.localeCompare(b.c));
    } else if (this.azsort && !this.isAscending) {
      pairs.sort((a, b) => b.c.localeCompare(a.c));
    }
    return {
      categories: pairs.map((p) => p.c),
      values: pairs.map((p) => p.v),
    };
  }

  /** Bar styling aligned with Supramax laden performance charts */
  private readonly benchBarFill = '#2e4d78';
  private readonly benchBarHover = '#3a5f8c';
  private readonly benchBarBorder = '#ffffff';
  private readonly benchAvgLine = '#e8913a';

  private getBenchmarkChartTheme() {
    const isDark = this.isDarkMode();
    return {
      plotBackground: isDark ? '#152238' : '#ffffff',
      titleColor: isDark ? '#ffffff' : '#0f172a',
      labelColor: isDark ? '#ffffff' : '#475569',
      axisLineColor: isDark ? '#334155' : '#cbd5e1',
      gridLineColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
      legendColor: isDark ? '#ffffff' : '#475569',
      legendHoverColor: isDark ? '#e2e8f0' : '#0f172a',
      tooltipBg: isDark ? '#0f172a' : '#ffffff',
      tooltipBorder: isDark ? '#475569' : '#e2e8f0',
      tooltipText: isDark ? '#f1f5f9' : '#1e293b',
      tooltipMuted: isDark ? '#94a3b8' : '#64748b',
      tooltipTitle: isDark ? '#f8fafc' : '#0f172a',
      defaultBarBorder: isDark ? '#ffffff' : '#e2e8f0',
    };
  }

  private buildBenchmarkColumnChart(config: {
    title: string;
    categories: string[];
    values: number[];
    xAxisTitle: string;
    yAxisTitle?: string;
    tooltipFormatter?: (ctx: any, avg: number) => string;
    xAxisPlotLines?: any[];
    barColors?: { fill: string; hover: string; border: string };
  }): Chart {
    const yAxisTitle = config.yAxisTitle || this.pcConsumptionYAxisTitle;
    const avg =
      config.values.length > 0
        ? config.values.reduce((a, b) => a + b, 0) / config.values.length
        : 0;
    const avgRounded = this.roundMt(avg);
    const tooltipFn = config.tooltipFormatter;
    const barFill = config.barColors?.fill ?? this.benchBarFill;
    const barHover = config.barColors?.hover ?? this.benchBarHover;
    const barBorder =
      config.barColors?.border ??
      this.getBenchmarkChartTheme().defaultBarBorder;
    const avgLine = this.benchAvgLine;
    const theme = this.getBenchmarkChartTheme();
    const maskedCategories = config.categories.map((name: string, index: number) => {

      if (this.subusername?.toLowerCase() !== 'demo') {
        return name;
      }

      // If categories are generated in the same order as DEMO_VESSEL_PC
      if (index === 0) {
        return 'Vessel 1';
      }

      if (index === 1) {
        return 'Vessel 2';
      }

      return `Vessel ${index + 1}`;
    });

    return new Chart({
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        plotBackgroundColor: theme.plotBackground,
        plotBorderWidth: 0,
        spacingTop: 8,
        spacingBottom: 16,
        spacingLeft: 14,
        spacingRight: 12,
        marginBottom: 88,
        marginTop: 4,
        marginLeft: 72,
        style: { fontFamily: 'Poppins, sans-serif' },
      },
      title: {
        text: config.title,
        style: { fontSize: '13px', fontWeight: '600', color: theme.titleColor },
      },
      xAxis: {
        categories: maskedCategories,
        title: {
          text: config.xAxisTitle,
          style: {
            color: theme.labelColor,
            fontSize: '12px',
            fontWeight: '600',
            textOutline: 'none',
          },
        },
        labels: {
          rotation: -45,
          autoRotation: [-45, -60, -90],
          step: 1,
          style: {
            fontSize: '11px',
            fontWeight: '500',
            color: theme.labelColor,
            textOutline: 'none',
          },
        },
        lineColor: theme.axisLineColor,
        tickColor: theme.axisLineColor,
        plotLines: config.xAxisPlotLines,
      },
      yAxis: {
        min: 0,
        title: {
          text: yAxisTitle,
          margin: 18,
          style: {
            color: theme.labelColor,
            fontSize: '12px',
            fontWeight: '700',
            textOutline: 'none',
            whiteSpace: 'nowrap',
          },
        },
        labels: {
          style: {
            color: theme.labelColor,
            fontSize: '11px',
            fontWeight: '500',
            textOutline: 'none',
          },
        },
        gridLineColor: theme.gridLineColor,
        plotLines: [
          {
            value: avgRounded,
            color: this.benchAvgLine,
            dashStyle: 'Dash',
            width: 2,
            zIndex: 5,
            label: {
              text: `Average: ${avgRounded} MT`,
              align: 'right',
              style: {
                color: this.benchAvgLine,
                fontWeight: '600',
                fontSize: '10px',
              },
            },
          },
        ],
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        margin: 8,
        padding: 4,
        itemMarginTop: 2,
        itemStyle: {
          color: theme.legendColor,
          fontWeight: '500',
          fontSize: '10px',
        },
        itemHoverStyle: { color: theme.legendHoverColor },
      },
      tooltip: {
        useHTML: true,
        shared: false,
        outside: true,
        backgroundColor: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
        borderRadius: 8,
        shadow: true,
        style: { color: theme.tooltipText, fontSize: '12px' },
        formatter: tooltipFn
          ? function (this: any) {
            return tooltipFn(this, avgRounded);
          }
          : function (this: any) {
            const y = Number(this.y) || 0;
            return (
              `<div style="min-width:140px;">` +
              `<b style="color:${theme.tooltipTitle};">${this.x}</b><br/>` +
              `<span style="color:${theme.tooltipMuted};">Consumption</span> ` +
              `<b style="color:${barFill};">${y.toFixed(2)} MT</b><br/>` +
              `<span style="color:${theme.tooltipMuted};">Average</span> ` +
              `<b style="color:${avgLine};">${avgRounded} MT</b></div>`
            );
          },
      },
      plotOptions: {
        column: {
          borderRadius: 6,
          maxPointWidth: 48,
          color: barFill,
          borderColor: barBorder,
          borderWidth: 1,
          dataLabels: {
            enabled: false,
          },
          states: {
            hover: {
              color: barHover,
              borderColor: barBorder,
              borderWidth: 1,
              brightness: 0,
            },
          },
        },
        series: {
          animation: { duration: 400 },
        },
      },
      series: [
        {
          name: 'Consumption',
          type: 'column',
          data: config.values,
          color: barFill,
          borderColor: barBorder,
          borderWidth: 1,
        },
        {
          name: 'Average',
          type: 'line',
          color: this.benchAvgLine,
          dashStyle: 'Dash',
          marker: { enabled: false },
          enableMouseTracking: false,
          showInLegend: true,
          data: [],
          events: {
            legendItemClick: () => false,
          },
        },
      ],
      credits: { enabled: false },
    });
  }

  /** Renders PC chart from cached pcData (grouping + sort + tooltips). */
  private buildPcChartFromData(): void {
    if (!this.pcData?.length) {
      console.log('PC chart skipped — empty pcData');
      console.log('Dataset', []);
      console.log('Labels', []);
      this.chartOptions = null;
      return;
    }

    const grouped: Record<
      string,
      {
        consumption: number;
        time: number | null;
        port: string;
        operation: string;
      }[]
    > = {};

    this.pcData.forEach((d: any) => {
      const vessel = d.vessel_name?.trim();
      if (!vessel) return;
      const consumption = Number(d.operation_consumption) || 0;
      const time =
        d.time !== null && d.time !== undefined ? Number(d.time) : null;
      if (!grouped[vessel]) grouped[vessel] = [];
      const visits = grouped[vessel];
      const portLabel = this.buildPortVisitLabel(d, visits as any);
      visits.push({
        consumption,
        time,
        port: portLabel,
        operation: portLabel,
      });
    });

    const isSingleVessel =
      this.selectedVessels.length === 1 ||
      (this.selectedVessels.length > 1 &&
        Object.keys(grouped).length === 1 &&
        this.selectedVessels.some(
          (name) =>
            name.toLowerCase() === Object.keys(grouped)[0]?.toLowerCase(),
        ));
    let categories: string[] = [];
    let values: number[] = [];
    let vesselKey: string | undefined;

    if (isSingleVessel) {
      const selected = this.selectedVessels[0]?.toLowerCase();
      vesselKey =
        Object.keys(grouped).find((v) => v.toLowerCase() === selected) ||
        Object.keys(grouped)[0];
      const ops = vesselKey ? grouped[vesselKey] : [];
      ops.forEach((item) => {
        categories.push(item.port || item.operation);
        values.push(Number(item.consumption.toFixed(3)));
      });
    } else {
      Object.keys(grouped).forEach((v) => {
        const arr = grouped[v];
        const total = arr.reduce((s, i) => s + i.consumption, 0);
        const avg = arr.length ? total / arr.length : 0;
        categories.push(v);
        values.push(Number(avg.toFixed(3)));
      });
    }

    if (!values.length) {
      this.logBench('PC chart skipped — no values after grouping');
      console.log('Labels', categories);
      console.log('Dataset', values);
      this.chartOptions = null;
      return;
    }

    const sorted = this.sortChartSeries(categories, values);
    categories = sorted.categories;
    values = sorted.values;
    console.log('PC chart labels', categories);
    console.log('PC chart dataset', values);

    const groupedRef = grouped;
    const vesselKeyRef = vesselKey;
    const singleVesselRef = isSingleVessel;

    this.chartOptions = null;
    this.chartOptions = this.buildBenchmarkColumnChart({
      title: 'Benchmarking for PC',
      categories,
      values,
      xAxisTitle: singleVesselRef ? 'Port visits' : 'Vessel names',
      yAxisTitle: this.pcConsumptionYAxisTitle,
      tooltipFormatter: (ctx, avg) => {
        const y = Number(ctx.y) || 0;
        if (singleVesselRef) {
          const ops = vesselKeyRef ? groupedRef[vesselKeyRef] || [] : [];
          const label = String(ctx.x);
          const item = ops.find((o) => (o.port || o.operation) === label);
          if (!item) {
            return this.formatBenchmarkTooltip(label, y, avg);
          }
          return this.formatBenchmarkTooltip(String(ctx.x), y, avg, [
            {
              label: 'Duration',
              value: item.time != null ? `${item.time.toFixed(1)} days` : 'N/A',
            },
          ]);
        }
        const vessel = String(ctx.x);
        const ops = groupedRef[vessel] || [];
        const rows = ops.map((item) => ({
          label: item.port || item.operation,
          value: `${item.consumption.toFixed(2)} MT${item.time != null ? ` · ${item.time.toFixed(1)} d` : ''
            }`,
        }));
        return this.formatBenchmarkTooltip(vessel, y, avg, rows);
      },
    });
  }

  /**
   * Dependency step 1: Client selected → call the Segment API with the current
   * Client, populate the Segment dropdown from the response, then cascade to
   * Operations. Guarded by a sequence id so a stale response can't overwrite a
   * newer one. Never triggers the Port API.
   */
  private loadPcSegmentsForSelectedClient(): void {
    const payload = { client: this.getPcClientPayload() };
    const requestSeq = ++this.pcSegmentRequestSeq;
    console.log('[PC][Segment API] request', payload);

    this.prequestFormService.getBenchmarkSegments(payload).subscribe({
      next: (res: any) => {
        if (requestSeq !== this.pcSegmentRequestSeq) {
          return;
        }
        console.log('[PC][Segment API] response', res?.segments);
        if (res?.status === 'success' && res.segments?.length) {
          this.setPcSegmentOptionsFromApi(res.segments);
        }
        // Segment ready → cascade to Operations (then Vessels).
        this.benchmarkOperations();
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestSeq !== this.pcSegmentRequestSeq) {
          return;
        }
        console.error('Failed to load benchmark segments');
        this.benchmarkOperations();
      },
    });
  }

  benchmarkOperations() {
    // Dependent chain: Operations are re-fetched (and replaced) for the current
    // Client + Segment so the list narrows instead of accumulating stale values.
    const segmentPayload = this.getPcSegmentApiPayload();

    let payload = {
      segment: segmentPayload,
      client: this.getPcClientPayload(),
    };

    // Ignore out-of-order (stale) Operations responses so only the latest wins.
    const requestSeq = ++this.pcOperationRequestSeq;
    console.log('[PC][Operations API] request', payload);

    this.prequestFormService.getbenchMarkOperations(payload).subscribe({
      next: (data) => {
        if (requestSeq !== this.pcOperationRequestSeq) {
          return;
        }
        console.log('[PC][Operations API] response', data?.operations);
        const loaded = (data.operations || []).filter(
          (item: string) => item && !/^\d+(\.\d+)?$/.test(String(item).trim()),
        );

        if (loaded.length) {
          this.pcMasterOperationOptions = dedupeFilterOptions(loaded);
          this.operationsOptions = [...this.pcMasterOperationOptions];
          if (
            !this.selectedPcOperation ||
            !this.operationsOptions.includes(this.selectedPcOperation)
          ) {
            this.selectedPcOperation =
              this.operationsOptions.find(
                (item) => item === 'Cargo Ops -Discharging',
              ) || this.operationsOptions[0];
          }
        }

        this.operationsLoaded = true;
        // Operations ready → Ports API + Vessel dropdown for
        // Client + Segment + Operation (matches filter drawer order).
        this.rebuildPcCascadingOptions();
        this.benchmarkPort();
        this.prefetchPcBenchmarkPoolForVessels();
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestSeq !== this.pcOperationRequestSeq) {
          return;
        }
        console.error('Failed to load benchmark operations');
        this.operationsLoaded = true;
        if (!this.pcMasterOperationOptions.length) {
          this.pcMasterOperationOptions = [
            'Cargo Ops -Discharging',
            'Cargo Ops -Loading',
            'Idle',
          ];
          this.operationsOptions = [...this.pcMasterOperationOptions];
          this.selectedPcOperation = this.operationsOptions[0];
        }
        this.rebuildPcCascadingOptions();
        this.benchmarkPort();
        this.prefetchPcBenchmarkPoolForVessels();
      },
    });
  }
  benchmarkPort() {
    if (!this.selectedPcOperation) {
      this.rebuildPcCascadingOptions();
      return;
    }

    const payload: any = {
      segment: this.getPcSegmentApiPayload(),
      client: this.getPcClientPayload(),
      operation: this.selectedPcOperation,
    };

    // Optional vessel narrow: if Vessel(s) are already selected, include them so
    // benchmarking_ports can refine. Primary trigger is after Operations.
    const vesselNames = this.selectedVessels?.length
      ? [...this.selectedVessels]
      : [];
    if (vesselNames.length) {
      payload.vessel_name = vesselNames;
    }

    // Ignore out-of-order (stale) responses so only the latest one wins.
    const requestSeq = ++this.pcPortRequestSeq;
    console.log('[PC][Port API] request', payload);

    this.prequestFormService.getbenchMarkPorts(payload).subscribe({
      next: (data: any) => {
        if (requestSeq !== this.pcPortRequestSeq) {
          return;
        }
        console.log('[PC][Port API] response ports', data?.ports);
        // Apply ONLY the fresh Port API result. rebuildPcScopedPortOptions is a
        // no-op now, so nothing else can overwrite these ports.
        this.applyPcBenchmarkPortsApiResponse(data);
        // Ports for this vessel are ready → refresh the graph with the latest
        // filter state (uses the vessel's ports until specific ports are picked).
        this.scheduleBenchmarkChartUpdate();
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestSeq !== this.pcPortRequestSeq) {
          return;
        }
        console.error('Failed to load benchmark ports');
        this.rebuildPcCascadingOptions();
      },
    });
  }
  filterVesselsBySegment(): void {
    let data = this.clientFilteredVessels || this.vessels;

    if (!this.isAllPcClientsSelected()) {
      if (this.pcBenchmarkPool?.length) {
        const allowed = new Set(
          this.getPcPoolScoped('client').map((r) => r.vessel_name),
        );
        data = data.filter((v: any) => allowed.has(v.name));
      } else {
        data = data.filter((v: any) =>
          matchesAnyClient(v.client, this.selectedPcClients),
        );
      }
    }

    this.filteredVessels = data.filter((v: any) => {
      const segmentMatch = this.vesselMatchesSelectedPcSegment(v.segment);

      const portMatch =
        !this.selectedPorts?.length || this.selectedPorts.includes(v.port);

      return segmentMatch && portMatch;
    });
  }
  onVesselInputBlur(): void {
    this.isVesselInputFocused = false;
  }

  filterPorts(): void {
    const searchTerm = this.portSearchText?.toLowerCase();
    if (searchTerm) {
      this.filteredPorts = this.benchmarkPorts.filter((port: any) =>
        port.toLowerCase().includes(searchTerm),
      );
    } else {
      this.filteredPorts = this.benchmarkPorts;
    }
  }

  onPortInputFocus(input: HTMLInputElement): void {
    this.portSearchText = '';
    this.filterPorts();
    this.isPortInputFocused = true;
    this.portDropdownOpen = true;
  }

  onPortInputBlur(): void {
    this.isPortInputFocused = false;
  }

  onPortInputChange(event: any) {
    const value = event.target.value.toLowerCase();

    this.portSearchText = value;

    this.filteredPorts = this.benchmarkPorts.filter((p: string) =>
      p.toLowerCase().includes(value),
    );
  }

  // updatePortLabel(): void {
  //   if (this.selectedPorts.length > 0) {
  //     this.selectedPortLabel = this.selectedPorts.join(', ');
  //   } else {
  //     this.selectedPortLabel = '';
  //   }
  // }
  onPortItemClick(event: MouseEvent, port: string): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.selectedPorts.includes(port)) {
      this.selectedPorts = this.selectedPorts.filter((p) => p !== port);
    } else {
      this.selectedPorts = [...this.selectedPorts, port];
    }

    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.updatePortLabel();
    setTimeout(() => this.rebuildPcCascadingOptions(), 0);
    this.cdr.detectChanges();
  }

  onPortCheckboxChange(event: any, port: string) {
    const isChecked = event.target.checked;

    if (isChecked) {
      if (!this.selectedPorts.includes(port)) {
        this.selectedPorts.push(port);
      }
    } else {
      this.selectedPorts = this.selectedPorts.filter((p) => p !== port);
    }

    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.updatePortLabel();
    this.rebuildPcCascadingOptions();
  }

  onPcDateRangeChange(): void {
    // Benchmark charts update on Apply only.
  }

  // --- Year Filter Methods ---
  onYearInputFocus(): void {
    this.yearSearchText = '';
    this.filteredYears = [...this.dummyYears];
    this.yearDropdownOpen = true;
  }

  onYearInputChange(event: any): void {
    this.yearSearchText = event.target.value.toLowerCase();
    this.filteredYears = this.dummyYears.filter((y) =>
      y.toLowerCase().includes(this.yearSearchText),
    );
  }

  onYearInputBlur(): void {
    setTimeout(() => {
      this.yearDropdownOpen = false;
      this.updateYearLabel();
    }, 150);
  }

  onYearCheckboxChange(event: any, year: string): void {
    const isChecked = event.target.checked;
    if (isChecked && !this.selectedPcYears.includes(year)) {
      this.selectedPcYears.push(year);
    } else if (!isChecked) {
      this.selectedPcYears = this.selectedPcYears.filter((y) => y !== year);
    }
    this.updateYearLabel();
  }

  selectAllYears(): void {
    this.selectedPcYears = [...this.filteredYears];
    this.updateYearLabel();
  }

  clearAllYears(): void {
    this.selectedPcYears = [];
    this.updateYearLabel();
  }

  updateYearLabel() {
    this.selectedYearLabel = this.selectedPcYears.join(', ');
  }

  /** Port name from benchmarking record (not vessel name). */
  private resolveRecordPort(record: any): string {
    const port = String(record?.port ?? record?.port_name ?? '').trim();
    return port || 'Unknown Port';
  }

  /** Label for a port visit within a vessel group (e.g. "La Goulette", "La Goulette 2"). */
  private buildPortVisitLabel(
    record: any,
    visitsForVessel: { basePort: string }[],
  ): string {
    const basePort = this.resolveRecordPort(record);
    const visitNum =
      visitsForVessel.filter((v) => v.basePort === basePort).length + 1;
    return visitNum === 1 ? basePort : `${basePort} ${visitNum}`;
  }

  expandMainChart() {
    if (!this.pcData || this.pcData.length === 0) {
      return;
    }

    // Group by vessel; label each bar by port name
    const grouped: {
      [key: string]: { value: number; portLabel: string; basePort: string }[];
    } = {};
    this.pcData.forEach((d: any) => {
      const vessel = d.vessel_name;
      const val = Number(d.operation_consumption) || 0;
      if (!grouped[vessel]) {
        grouped[vessel] = [];
      }
      const visits = grouped[vessel];
      const portLabel = this.buildPortVisitLabel(d, visits);
      const basePort = this.resolveRecordPort(d);
      visits.push({ value: val, portLabel, basePort });
    });

    // Categories + data
    const categories: string[] = [];
    const dataPoints: number[] = [];
    const plotLines: any[] = [];

    let index = 0;
    Object.keys(grouped).forEach((vessel) => {
      const arr = grouped[vessel];

      arr.forEach((entry) => {
        categories.push(entry.portLabel);
        dataPoints.push(Number(entry.value.toFixed(3)));
        index++;
      });

      // separator line after each vessel group
      if (index < categories.length || index === categories.length) {
        plotLines.push({
          color: '#999',
          width: 1,
          value: index - 0.5, // vertical line between groups
          zIndex: 5,
          dashStyle: 'ShortDash',
        });
      }
    });

    // overall average
    const allVals = Object.values(grouped)
      .flat()
      .map((e) => e.value);
    const overallTotal = allVals.reduce((s, v) => s + v, 0);
    const overallAvg =
      allVals.length > 0 ? (overallTotal / allVals.length).toFixed(3) : 0;

    // Chart
    const detailedExpandedChart = this.buildBenchmarkColumnChart({
      title: 'Expanded Detail View (All Operations)',
      categories,
      values: dataPoints,
      xAxisTitle: 'Port visits',
      yAxisTitle: this.pcConsumptionYAxisTitle,
      xAxisPlotLines: plotLines,
      tooltipFormatter: (ctx, avg) =>
        this.formatBenchmarkTooltip(String(ctx.x), Number(ctx.y) || 0, avg),
    });

    const dialogRef = this.dialog.open(ExpandChartComponent, {
      width: '93%',
      height: '65%',
      data: {
        vesselName: 'All Filtered Vessels (Details)',
        chart: detailedExpandedChart,
      },
      autoFocus: false,
    });

    dialogRef.afterOpened().subscribe(() => {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('highchartsReflowRequest'));
      }, 50);
    });
  }
  // createChart() {
  //   const json = {
  //     port_name: this.selectedPorts.map((port) => port?.toLowerCase()),
  //     vessel_name: this.selectedVessels.map((vessel) => vessel?.toLowerCase()),
  //     segment: this.selectedPcSegment,
  //     operation: this.selectedPcOperation,
  //     client: this.clientName,
  //   };

  //   this.benchmarkOperations();
  //   this.benchmarkPort();
  //   this.prequestFormService.getPcBenchmarking(json).subscribe((data) => {
  //     const clientFilters: { [key: string]: (item: any) => boolean } = {
  //       'Eiger Shipping': (item) =>
  //         item.client &&
  //         (item.client === 'Northwell' ||
  //           item.client.toLowerCase() === 'eiger'),
  //       'Zeronorth ZN': (item) =>
  //         item.client && item.client.toLowerCase() === 'zeronorth',
  //       'Elite Shipping': (item) =>
  //         item.client && item.client.toLowerCase() === 'elite tankship dmcc',
  //       'Bunge Shipping': (item) =>
  //         item.client && item.client.toLowerCase() === 'bunge',
  //       'Alcor Shipping': (item) =>
  //         item.client.toLowerCase() === 'alcor chartering dmcc',
  //       'EMZoil Shipping': (item) =>
  //         item.client && item.client.toLowerCase() === 'emz oil',
  //       'Ethiopian Shipping & Logistics': (item) =>
  //         item.client.toLowerCase() === 'ethiopian shipping & logistics',
  //     };

  //     if (this.username === 'Meraki - Operations') {
  //       data = data;
  //     } else if (clientFilters[this.username]) {
  //       data = data.filter(clientFilters[this.username]);
  //     }
  //     // filter by client
  //     this.pcData = data.filter(
  //       (d: any) =>
  //         !this.clientName ||
  //         d.client?.trim().toLowerCase() ===
  //         this.clientName.trim().toLowerCase(),
  //     );

  //     // ✅ group by vessel_name and include both consumption + time
  //     const grouped: {
  //       [key: string]: { consumption: number; time: number }[];
  //     } = {};

  //     this.pcData.forEach((d: any) => {
  //       const vessel = d.vessel_name;
  //       const consumption = Number(d?.operation_consumption) || 0;
  //       const time = Number(d?.time) || 0;

  //       if (!grouped[vessel]) {
  //         grouped[vessel] = [];
  //       }
  //       grouped[vessel].push({ consumption, time });
  //     });

  //     // ✅ categories & averages
  //     const vesselNames = Object.keys(grouped).sort();
  //     const avgValues = vesselNames.map((v) => {
  //       const arr = grouped[v];
  //       const total = arr.reduce((sum, item) => sum + item.consumption, 0);
  //       return arr.length > 0 ? total / arr.length : 0;
  //     });

  //     // ✅ overall average
  //     const overallTotal = avgValues.reduce((s, v) => s + v, 0);
  //     const overallAvg =
  //       avgValues.length > 0 ? overallTotal / avgValues.length : 0;

  //     // ✅ Chart
  //     this.chartOptions = new Chart({
  //       chart: {
  //         type: 'column',
  //         backgroundColor: '#ecf3f9',
  //       },
  //       title: { text: 'Benchmarking for PC' },
  //       xAxis: {
  //         categories: vesselNames,
  //         title: { text: 'Vessel Names' },
  //         labels: {
  //           rotation: -90,
  //           style: { fontSize: '12px', fontFamily: 'Poppins, sans-serif' },
  //         },
  //       },
  //       yAxis: {
  //         min: 0,
  //         title: { text: 'Consumption (MT)' },
  //         plotLines: [
  //           {
  //             value: overallAvg,
  //             color: 'red',
  //             dashStyle: 'Dash',
  //             width: 2,
  //             zIndex: 5,
  //             label: {
  //               text: `Avg: ${overallAvg} MT`,
  //               style: { color: 'red', fontWeight: 'bold' },
  //             },
  //           },
  //         ],
  //       },
  //       tooltip: {
  //         useHTML: true,
  //         formatter: function () {
  //           const vessel = (this.x as string) ?? '';
  //           const allVals = grouped[vessel] ?? [];

  //           let rows = allVals
  //             .map(
  //               (item: any, idx: number) => `
  //               <tr>
  //                 <td style="padding:0 8px;text-align:left;">Ops ${idx + 1}</td>
  //                 <td style="padding:0 8px;text-align:right;">
  //                   <b>${item.consumption.toFixed(2)} MT</b>
  //                 </td>
  //                 <td style="padding:0 8px;text-align:right;color:gray;">
  //                   (${item.time.toFixed(2)} hrs)
  //                 </td>
  //               </tr>`,
  //             )
  //             .join('');

  //           const avgVal =
  //             typeof this.y === 'number' ? this.y.toFixed(2) : '0.00';

  //           return `
  //           <b>${vessel}</b><br/>
  //           <table style="border-collapse:collapse;">
  //             <tr>
  //               <td style="padding:0 8px;text-align:left;color:${this.color}">Avg</td>
  //               <td style="padding:0 8px;text-align:right;">
  //                 <b>${avgVal} MT</b>
  //               </td>
  //             </tr>
  //             ${rows}
  //           </table>
  //           <a style="color:blue;cursor:pointer"
  //             onclick="window.dispatchEvent(new CustomEvent('expandChart', {detail:{vessel:'${vessel}'}}))">
  //             🔍 Expand
  //           </a>
  //         `;
  //         },
  //       },

  //       plotOptions: {
  //         column: { dataLabels: { enabled: false } },
  //       },
  //       series: [
  //         {
  //           name: 'Avg Consumption',
  //           type: 'column',
  //           data: avgValues.map((val) => Number(val.toFixed(3))),
  //           color: '#001f3f',
  //         },
  //         {
  //           name: 'Average',
  //           type: 'line',
  //           color: 'red',
  //           dashStyle: 'Dash',
  //           marker: { enabled: false },
  //           data: new Array(vesselNames.length).fill(overallAvg),
  //           events: { legendItemClick: () => false },
  //         },
  //       ],
  //       credits: { enabled: false },
  //     });
  //   });
  // }
  createChart() {
    this.ensureValidPcOperationSelection();
    // Chart uses current filter state. Ports are loaded by the Port API after
    // Operations; if none are ready yet, executePcChartRequest skips cleanly.
    this.executePcChartRequest(this.buildPcBenchmarkApiPayload());
  }

  private executePcChartRequest(json: Record<string, unknown>): void {
    // Init fires loadBenchmarkCharts from several places (route params, the
    // ports response, ngAfterViewInit, a timed safety net). Skip a request whose
    // identical twin is still in flight so those collapse into one call.
    const requestKey = `pc:${JSON.stringify(json)}`;
    if (this.inFlightChartRequests.has(requestKey)) {
      return;
    }
    this.inFlightChartRequests.add(requestKey);

    const requestSeq = ++this.pcChartRequestSeq;
    this.logBench('PC API request', json);
    this.isLoadingPcChart = true;
    this.prequestFormService.getPcBenchmarking(json).subscribe({
      next: (data: any) => {
        this.inFlightChartRequests.delete(requestKey);
        if (requestSeq !== this.pcChartRequestSeq) {
          return;
        }
        this.isLoadingPcChart = false;
        let rows = resolvePcBenchmarkRows(data);

        // 🔴 FILTER FOR DEMO USER ONLY 🔴
        rows = this.filterDemoVesselsOnly(rows);

        let clientFiltered = rows;
        if (!this.isAllPcClientsSelected()) {
          clientFiltered = rows.filter((d: any) =>
            matchesAnyClient(d.client, this.selectedPcClients),
          );
        } else if (this.clientName && this.clientName !== 'ALL') {
          clientFiltered = rows.filter(
            (d: any) =>
              d.client?.toLowerCase() === this.clientName.toLowerCase(),
          );
        }

        this.pcData = this.applyPcChartRowFilters(
          clientFiltered.length ? clientFiltered : rows,
        );
        if (!this.pcData.length) {
          this.pcData = clientFiltered.length ? clientFiltered : rows;
        }

        // Vessel Name + Yard dropdowns = this filtered response only.
        this.pcLatestBenchmarkRows = rows;
        this.bindPcVesselAndYardOptionsFromLatestRows();

        this.buildPcChartFromData();

        if (rows.length) {
          this.mergePcBenchmarkMasterPool(rows);
          const opts = extractPcBenchmarkFilterOptions(rows);
          if (opts.clients.length) {
            this.mergePcClientOptions(opts.clients);
          }
          if (opts.ports.length) {
            this.mergePcMasterPortOptions(opts.ports);
          }
          if (opts.vessels.length) {
            this.mergePcMasterVesselNames(opts.vessels);
          }
        }

        this.cdr.markForCheck();
      },
      error: (err) => {
        this.inFlightChartRequests.delete(requestKey);
        if (requestSeq !== this.pcChartRequestSeq) {
          return;
        }
        console.error('PC Benchmarking failed', err);
        this.isLoadingPcChart = false;
        this.chartOptions = null;
      },
    });
  }

  private getPcChartPorts(): string[] {
    return this.getPcPortPayload();
  }
  // clearSelections(): void {
  //   // clear port selections
  //   this.selectedPorts = [];
  //   this.portSearchText = '';
  //   this.selectedPortLabel = '';
  //   this.filteredPorts = [];

  //   // clear vessel selections
  //   this.selectedVessels = [];
  //   this.vesselSearchText = '';
  //   this.selectedVesselLabel = '';
  //   this.filteredVessels = [];

  //   // reset dropdowns
  //   this.portDropdownOpen = false;
  //   this.vesselDropdownOpen = false;

  //   // clear chart
  //   this.chartOptions = new Chart({
  //     chart: {
  //       type: 'column',
  //       backgroundColor: '#ecf3f9',
  //     },
  //     title: { text: 'Benchmarking for PC' },
  //     series: [],
  //     credits: { enabled: false },
  //   });
  // }
  private resetMultiSelectFilterUi(): void {
    this.multiSelectFilters?.forEach((filter) => filter.resetDropdownUi());
  }

  private resetBenchmarkChartSortState(): void {
    this.isSorting = false;
    this.sortMapData = false;
    this.azsort = false;
    this.isAscending = false;
  }

  private resetDefaultPcPorts(): void {
    this.selectedPorts = [];
    this.selectedPortLabel = '';
    this.prunePcPortSelections();
  }

  private resetPcBenchmarkFiltersToDefaults(options?: {
    reloadChart?: boolean;
  }): void {
    const reloadChart = options?.reloadChart ?? true;

    this.selectedPcSegment = 'LR3/Suezmax';
    this.selectedPcOperation = 'Cargo Ops -Discharging';
    this.selectedPcClients = [];
    this.selectedClient = 'ALL';
    this.clientName = 'ALL';
    this.selectedVessels = [];
    this.selectedPcYards = [];
    this.selectedPcBuiltAges = [];
    this.selectedPcYears = [];
    this.pcLatestBenchmarkRows = [];
    this.pcVesselNameOptions = [];
    this.pcYardOptions = [];
    this.pcBuiltAgeOptions = [];
    this.pcBuiltAgeOptionItems = [];
    this.updateYearLabel();

    this.portSearchText = '';
    this.vesselSearchText = '';
    this.selectedVesselLabel = '';
    this.closePortDropdown();

    this.pcBenchmarkForm.patchValue(
      { ship_built_from: '', ship_built_to: '' },
      { emitEvent: false },
    );

    this.resetDefaultPcPorts();
    this.ensureValidPcOperationSelection();
    this.filterVesselsBySegment();
    this.operationsLoaded = false;
    this.benchmarkOperations();
    this.rebuildPcCascadingOptions();
    this.syncPcSegmentSelection();
    this.resetMultiSelectFilterUi();

    if (reloadChart) {
      this.resetBenchmarkChartSortState();
      if (this.graph) {
        this.createChart();
      } else {
        this.triggerChartUpdate();
      }
    }
  }

  private resetTcpBenchmarkFiltersToDefaults(options?: {
    reloadChart?: boolean;
  }): void {
    const reloadChart = options?.reloadChart ?? true;

    this.selectedTcpClients = [];
    this.selectedTcpVesselNames = [];
    this.selectedTcpPorts = [];
    this.selectedTcpYards = [];
    this.selectedTcpBuiltAges = [];
    this.tcpBuiltAgeOptions = [];
    this.tcpBuiltAgeOptionItems = [];
    this.selectedSegment = 'LR3/Suezmax';
    this.selectedOperation =
      this.tcpOperationOptions[0] ||
      this.operations?.[1]?.value ||
      'Loading per day';
    this.ensureTcpOperationOptions();
    this.rebuildTcpCascadingOptions();
    this.syncTcpSegmentSelection();
    this.resetMultiSelectFilterUi();

    if (reloadChart) {
      this.resetBenchmarkChartSortState();
      this.createTcpChart();
    }
  }
  private applyTcpChartFromData(sortedData: any[]): void {
    if (!sortedData?.length) {
      this.logBench('TCP chart skipped — no rows');
      this.tcpChartOptions = null;
      return;
    }
    let categories = sortedData.map((d: any) => d.vessel_name);
    let values = sortedData.map((d: any) => {
      const val = d?.data?.total;
      return val === null || val === undefined || val === '' ? 0 : Number(val);
    });
    const sorted = this.sortChartSeries(categories, values);
    categories = sorted.categories;
    values = sorted.values;
    const dataByVessel = new Map(
      sortedData.map((d: any) => [d.vessel_name, d]),
    );


    this.tcpChartOptions = this.buildBenchmarkColumnChart({
      title: 'Benchmarking for TCP',
      categories,
      values,
      xAxisTitle: 'Vessel names',
      yAxisTitle: 'Consumption (MT/24 Hrs)',
      barColors: getTcpBenchmarkBarColors(this.isDarkMode()),
      tooltipFormatter: (ctx, avg) => {
        const y = Number(ctx.y) || 0;
        const record = dataByVessel.get(String(ctx.x));
        const rows: { label: string; value: string }[] = [
          { label: 'Segment', value: this.selectedSegment || '—' },
          { label: 'Operation', value: this.selectedOperation || '—' },
        ];
        if (record?.client) {
          rows.push({ label: 'Client', value: record.client });
        }
        return this.formatBenchmarkTooltip(String(ctx.x), y, avg, rows);
      },
    });
    this.logBench('TCP chart built', {
      labels: categories.length,
      chartReady: !!this.tcpChartOptions,
    });
  }

  createTcpChart() {
    const json = this.buildTcpApiPayload();

    // Same init fan-out as the PC chart — collapse identical concurrent calls.
    const requestKey = `tcp:${JSON.stringify(json)}`;
    if (this.inFlightChartRequests.has(requestKey)) {
      return;
    }
    this.inFlightChartRequests.add(requestKey);

    this.logBench('TCP API request', json);

    this.isLoadingTcpChart = true;
    this.prequestFormService.getTcpBenchmarking(json).subscribe({
      next: (data) => {
        this.inFlightChartRequests.delete(requestKey);
        this.isLoadingTcpChart = false;
        this.ingestTcpBenchmarkResponse(data, true);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.inFlightChartRequests.delete(requestKey);
        console.error('TCP Benchmarking failed', err);
        this.logBench('TCP API error', err);
        this.isLoadingTcpChart = false;
        this.tcpChartOptions = null;
      },
    });
  }
  selectedShipLabel: string = 'Select a ship';
  filterShipNames(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    const segment = this.filterForm.get('segment')?.value?.trim().toLowerCase();
    const selectedClient = this.filterForm
      .get('client')
      ?.value?.trim()
      .toLowerCase();
    const uniqueNames = new Set<string>();

    this.shipNameSuggestions = this.elements.filter((item: any) => {
      const vesselName = item.vessel_name?.toLowerCase();
      const itemSegment = item.segment?.toLowerCase();
      const itemClient = item.client?.toLowerCase();

      const segmentMatches = !segment || itemSegment === segment;
      const clientMatches = !selectedClient || itemClient === selectedClient;
      const nameMatches = vesselName.startsWith(inputValue);

      if (
        segmentMatches &&
        clientMatches &&
        nameMatches &&
        !uniqueNames.has(vesselName)
      ) {
        uniqueNames.add(vesselName);
        return true;
      }
      return false;
    });

    this.dropdownOpen = this.shipNameSuggestions.length > 0;
  }

  onFocusShipInput(): void {
    const segment = this.filterForm.get('segment')?.value?.trim().toLowerCase();
    const selectedClient = this.filterForm
      .get('client')
      ?.value?.trim()
      .toLowerCase();
    const uniqueNames = new Set<string>();

    const filtered = this.elements.filter((item: any) => {
      const vesselName = item.vessel_name?.toLowerCase();
      const itemSegment = item.segment?.toLowerCase();
      const itemClient = item.client?.toLowerCase(); // Ensure your data has client_name

      const segmentMatches = !segment || itemSegment === segment;
      const clientMatches = !selectedClient || itemClient === selectedClient;

      if (segmentMatches && clientMatches && !uniqueNames.has(vesselName)) {
        uniqueNames.add(vesselName);
        return true;
      }
      return false;
    });

    this.shipNameSuggestions = filtered.sort((a: any, b: any) =>
      a.vessel_name.localeCompare(b.vessel_name),
    );

    this.dropdownOpen = this.shipNameSuggestions.length > 0;
  }
  onBlurShipInput(): void {
    // Delay closing to allow checkbox click
    setTimeout(() => {
      this.dropdownOpen = false;
    }, 150);
  }
  onShipCheckboxChange(event: Event, shipName: string): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      if (!this.selectedShips.includes(shipName)) {
        this.selectedShips.push(shipName);
      }
    } else {
      this.selectedShips = this.selectedShips.filter(
        (name: string) => name !== shipName,
      );
    }

    this.selectedShipLabel = this.selectedShips.join(', ');
  }
  filterPortNames(event: Event): void {
    const input = (event.target as HTMLInputElement).value.toLowerCase().trim();
    this.portOptions = this.allPorts.filter((port) =>
      port.toLowerCase().includes(input),
    );
  }

  // 🔹 Opens dropdown and sorts alphabetically (case-insensitive)
  onFocusPortInput(): void {
    this.portOptions.sort((a: string, b: string) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
    this.PortDropdownOpen = this.portOptions.length > 0;
  }

  // 🔹 Closes dropdown after small delay (allows checkbox click)
  onBlurPortInput(): void {
    setTimeout(() => {
      this.PortDropdownOpen = false;
    }, 150);
  }

  // 🔹 Checkbox selection logic (multi-select)
  onFilterPortCheckboxChange(event: Event, portName: string): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      if (!this.selectedFilterPort.includes(portName)) {
        this.selectedFilterPort.push(portName);
      }
    } else {
      this.selectedFilterPort = this.selectedFilterPort.filter(
        (name) => name !== portName,
      );
    }

    this.selectedFilterPortLabel = this.selectedFilterPort.join(', ');
  }
  private getDemoVesselName(imo: string, originalName?: string): string {
    if (this.subusername?.toLowerCase() !== 'demo') {
      return originalName || '';
    }

    return String(imo) === '9399480' ? 'Vessel 1' : 'Vessel 2';
  }
  loadData() {
    this.username = localStorage.getItem('username');
    this.subusername = localStorage.getItem('loginPic') ?? '';
    this.isLoadingDatabase = true;
    this.isLoadingTable = true;
    this.prequestFormService.getTrackerData().subscribe((data) => {
      let records = applyUsernameClientFilter(data || [], this.username);

      // Demo user: show only demo vessels and mask vessel details
      if (this.subusername?.toLowerCase() === 'demo') {
        records = records
          .filter((item: any) =>
            DEMO_VESSEL_PC.includes(
              String(
                item.vessel_imo ??
                item.imo ??
                item.vessel_id
              )
            )
          )
          .map((item: any) => {
            const originalImo = String(
              item.vessel_imo ??
              item.imo ??
              item.vessel_id
            );

            return {
              ...item,

              // Keep original values for API/navigation
              original_vessel_imo: originalImo,
              original_vessel_name: item.vessel_name,

              // Mask values displayed in Fleet View
              vessel_imo: 'XXXXXXX',
              vessel_name:
                originalImo === '9399480'
                  ? 'Vessel 1'
                  : 'Vessel 2',
            };
          });
      }
      this.elements = records;
      this.filteredData = records;
      this.updateOverallRangeBoundsFromDataset();
      this.resetOverallRangeFiltersToDefaults();
      this.count(records);
      // this.getSegments(data);
      this.updatePageSizeOptions(this.filteredData.length || 0);
      this.paginateData();
      this.clientOptions = extractClientOptionsFromRecords(this.elements);
      this.segmentOptions = extractSegmentOptionsFromRecords(this.elements);
      this.mergePcClientOptions(this.clientOptions);
      this.mergeTcpMasterPortOptions(
        this.extractPortsFromRecords(this.elements),
      );
      this.rebuildPcSegmentDropdownOptions();
      this.rebuildTcpSegmentDropdownOptions();
      // this.clientOptions = Array.from(
      //   new Set(
      //     data
      //       .map((item: any) => {
      //         if (!item.client) return null;

      //         const c = item.client.trim().toLowerCase();

      //         // Treat Eiger as Northwell
      //         if (c === 'eiger' || c === 'eiger shipping') {
      //           return 'northwell';
      //         }

      //         return c;
      //       })
      //       .filter(Boolean),
      //   ),
      // ).map((c: any) => c.charAt(0).toUpperCase() + c.slice(1));

      const uniquePorts = new Map<string, string>();
      records
        .map((item: any) => item.port)
        .filter(Boolean)
        .forEach((port: string) => {
          const lower = port.toLowerCase().trim();
          if (!uniquePorts.has(lower)) {
            uniquePorts.set(lower, port.trim());
          }
        });
      this.allPorts = Array.from(uniquePorts.values());
      this.portOptions = [...this.allPorts];
      const uniqueOperations = new Map<string, string>();
      records
        .map((item: any) => item.operations)
        .filter(Boolean)
        .forEach((op: string) => {
          const lower = op.toLowerCase().trim();
          if (!uniqueOperations.has(lower)) {
            uniqueOperations.set(lower, op.trim());
          }
        });
      this.filterOperationOptions = Array.from(uniqueOperations.values());
      this.rebuildTableFilterCascadeOptions();
      this.isLoadingDatabase = false;
      this.isLoadingTable = false;
      this.scheduleLiveTableFilter();
    });
  }
  toTitleCase(name: string): string {
    return name
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  normalizeClient(name: string): string {
    return name
      ?.toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/tankship|shipping|dmcc|ltd|llc|pvt|private|limited/gi, '')
      .trim();
  }

  formatSegment(value: string): string {
    if (!value) return '';

    const trimmed = value.trim().toLowerCase().replace(/\s+/g, ' ');
    const match = trimmed.match(/^([a-z0-9]+)\s*(\(([^)]+)\))?$/i);

    if (match) {
      const main = match[1].toUpperCase(); // e.g., 'LR1', 'MR'
      const bracket = match[3]
        ? ` (${match[3].charAt(0).toUpperCase()}${match[3].slice(1)})`
        : '';
      return main + bracket;
    }

    // Fallback: title case
    return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }

  getSegments(data: any) {
    const segmentsSet = new Map<string, string>();

    data.forEach((item: any) => {
      const original = item.segment?.trim();
      if (!original) return;

      // Normalize: lowercase, collapse extra spaces
      const normalized = original.replace(/\s+/g, ' ').toLowerCase();

      if (!segmentsSet.has(normalized)) {
        // Match pattern like "lr1 (aframax)"
        const match = normalized.match(/^([a-z0-9]+)\s*(\(([^)]+)\))?$/i);

        if (match) {
          const main = match[1].toUpperCase(); // e.g., 'LR1', 'MR'
          const bracket = match[3]
            ? ` (${match[3].charAt(0).toUpperCase()}${match[3].slice(1)})`
            : '';
          segmentsSet.set(normalized, main + bracket);
        } else {
          // Fallback: just title case the whole thing
          const fallback = original
            .toLowerCase()
            .replace(/\b\w/g, (c: string) => c.toUpperCase());
          segmentsSet.set(normalized, fallback);
        }
      }
    });
    // this.segmentOptions = Array.from(segmentsSet.values()).map((seg: string) => ({
    //   label: seg,
    //   value: seg
    // }));
    // this.segmentOptions = Array.from(segmentsSet.values());
  }
  // vessel_name

  ngAfterViewInit() {
    this.sort.sortChange.subscribe((sort: Sort) => {
      this.sortData(sort);
    });
    setTimeout(() => {
      if (this.graph) {
        this.logBench('ngAfterViewInit — loading charts');
        this.loadBenchmarkCharts();
      }
    }, 0);
  }
  matchSegment(itemSegment: string, selectedSegment: string): boolean {
    return matchSegmentFilter(itemSegment, selectedSegment);
  }
  sortData(sort: Sort) {
    const data = this.paginatedData.slice();
    if (!sort.active || sort.direction === '') {
      this.paginatedData = data;
      return;
    }

    if (sort.active === 'optimizationConsumption') {
      this.paginatedData = data.sort((a: any, b: any) => {
        const isAsc = sort.direction === 'asc';
        const aVal =
          (this.getOptimizationLegConsumption(a, 'maneuvering') ?? 0) +
          (this.getOptimizationLegConsumption(a, 'cargoOps') ?? 0) +
          (this.getOptimizationLegConsumption(a, 'idle') ?? 0);
        const bVal =
          (this.getOptimizationLegConsumption(b, 'maneuvering') ?? 0) +
          (this.getOptimizationLegConsumption(b, 'cargoOps') ?? 0) +
          (this.getOptimizationLegConsumption(b, 'idle') ?? 0);
        return (aVal - bVal) * (isAsc ? 1 : -1);
      });
      return;
    }

    this.paginatedData = data.sort((a: any, b: any) => {
      const isAsc = sort.direction === 'asc';
      return this.compare(a, b, sort.active, isAsc);
    });
  }

  compare(a: any, b: any, sortActive: string, isAsc: boolean) {
    if (a[sortActive] === null || a[sortActive] === undefined) {
      return isAsc ? -1 : 1;
    }
    if (b[sortActive] === null || b[sortActive] === undefined) {
      return isAsc ? 1 : -1;
    }

    if (
      [
        'report_sent_date',
        'arrival_date_and_time',
        'departure_date_and_time',
      ].includes(sortActive)
    ) {
      const aDate = new Date(a[sortActive]);
      const bDate = new Date(b[sortActive]);
      if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
        return (aDate.getTime() - bDate.getTime()) * (isAsc ? 1 : -1);
      }
    }

    // Handle numeric values
    if (
      typeof a[sortActive] === 'number' &&
      typeof b[sortActive] === 'number'
    ) {
      return (a[sortActive] - b[sortActive]) * (isAsc ? 1 : -1);
    }

    // Parse strings as numbers if applicable
    const aNum = parseFloat(a[sortActive]);
    const bNum = parseFloat(b[sortActive]);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return (aNum < bNum ? -1 : 1) * (isAsc ? 1 : -1);
    }

    // Handle string sorting
    if (
      typeof a[sortActive] === 'string' &&
      typeof b[sortActive] === 'string'
    ) {
      return a[sortActive].localeCompare(b[sortActive]) * (isAsc ? 1 : -1);
    }

    // Fallback for other types
    return (a[sortActive] < b[sortActive] ? -1 : 1) * (isAsc ? 1 : -1);
  }
  paginateData() {
    const dataSource = this.filteredData ?? this.elements;
    const startIndex = this.currentPage * this.itemsPerPage;
    this.paginatedData = dataSource.slice(
      startIndex,
      startIndex + this.itemsPerPage,
    );
  }

  // paginateData() {
  //   const startIndex = this.currentPage * this.itemsPerPage;
  //   this.paginatedData = this.elements?.slice(
  //     startIndex,
  //     startIndex + this.itemsPerPage
  //   );
  // }

  count(data: any) {
    const source = data ?? [];
    const summary = calculateDatabaseSummary(source);

    this.totalVoyages = summary.totalPortCalls;
    this.totalDays = summary.totalDays;
    this.totalCpOverallMt = summary.totalCpOverallMt;
    this.totalDesignOverall = summary.totalDesignOverallMt;
    this.totalVessels = summary.totalVoyages;

    this.totalOptimizationMT = source.reduce((sum: number, item: any) => {
      const cargoDiff =
        (item.optimization_cargo_ops || 0) -
        (item.actual_consumptions_mt_maneuvering_cargo_idle
          ?.cargo_ops_actual_consumption_mt || 0);
      const idleDiff =
        (item.optimization_idle || 0) -
        (item.actual_consumptions_mt_maneuvering_cargo_idle
          ?.idle_actual_consumption_mt || 0);
      return sum + cargoDiff + idleDiff;
    }, 0);

    // ---- Header summary: Total Consumption (MT) sum + Remaining per operation ----
    // These summaries must NOT depend on paginated rows; they depend on the full filtered dataset.
    this.totalConsumptionColumnSumMt = source.reduce(
      (sum: number, item: any) => {
        return sum + this.getTotalConsumptionMtNumeric(item);
      },
      0,
    );

    const actualOpSums = source.reduce(
      (
        acc: { maneuvering: number; cargoOps: number; idle: number },
        item: any,
      ) => {
        acc.maneuvering += this.toFiniteNumber(
          item.actual_consumptions_mt_maneuvering_cargo_idle
            ?.maneuvering_actual_consumption_mt,
        );
        acc.cargoOps += this.toFiniteNumber(
          item.actual_consumptions_mt_maneuvering_cargo_idle
            ?.cargo_ops_actual_consumption_mt,
        );
        acc.idle += this.toFiniteNumber(
          item.actual_consumptions_mt_maneuvering_cargo_idle
            ?.idle_actual_consumption_mt,
        );
        return acc;
      },
      { maneuvering: 0, cargoOps: 0, idle: 0 },
    );

    const warrantedOpSums = source.reduce(
      (
        acc: { maneuvering: number; cargoOps: number; idle: number },
        item: any,
      ) => {
        if (!hasAnyActualOpData(item)) {
          return acc;
        }
        acc.maneuvering += this.toFiniteNumber(
          this.getOpWarrantedValue(item, 'maneuvering'),
        );
        acc.cargoOps += this.toFiniteNumber(
          this.getOpWarrantedValue(item, 'cargoOps'),
        );
        acc.idle += this.toFiniteNumber(
          this.getOpWarrantedValue(item, 'idle'),
        );
        return acc;
      },
      { maneuvering: 0, cargoOps: 0, idle: 0 },
    );

    const designOpSums = source.reduce(
      (
        acc: { maneuvering: number; cargoOps: number; idle: number },
        item: any,
      ) => {
        if (!hasAnyActualOpData(item)) {
          return acc;
        }
        acc.maneuvering += this.toFiniteNumber(
          this.getOpDesignValue(item, 'maneuvering'),
        );
        acc.cargoOps += this.toFiniteNumber(
          this.getOpDesignValue(item, 'cargoOps'),
        );
        acc.idle += this.toFiniteNumber(
          this.getOpDesignValue(item, 'idle'),
        );
        return acc;
      },
      { maneuvering: 0, cargoOps: 0, idle: 0 },
    );

    const optimizationOpSums = source.reduce(
      (
        acc: { maneuvering: number; cargoOps: number; idle: number },
        item: any,
      ) => {
        acc.maneuvering += this.toFiniteNumber(
          this.getOptimizationLegConsumption(item, 'maneuvering'),
        );
        acc.cargoOps += this.toFiniteNumber(
          this.getOptimizationLegConsumption(item, 'cargoOps'),
        );
        acc.idle += this.toFiniteNumber(
          this.getOptimizationLegConsumption(item, 'idle'),
        );
        return acc;
      },
      { maneuvering: 0, cargoOps: 0, idle: 0 },
    );

    this.actualOpSums = { ...actualOpSums };
    this.warrantedOpSums = { ...warrantedOpSums };
    this.designOpSums = { ...designOpSums };
    this.optimizationOpSums = { ...optimizationOpSums };

    this.actualConsumptionGroupTotalMt =
      actualOpSums.maneuvering + actualOpSums.cargoOps + actualOpSums.idle;
    this.warrantedConsumptionGroupTotalMt =
      warrantedOpSums.maneuvering +
      warrantedOpSums.cargoOps +
      warrantedOpSums.idle;
    this.designConsumptionGroupTotalMt =
      designOpSums.maneuvering + designOpSums.cargoOps + designOpSums.idle;
    this.optimizationConsumptionGroupTotalMt =
      optimizationOpSums.maneuvering +
      optimizationOpSums.cargoOps +
      optimizationOpSums.idle;

    // Align top cards with the same operation-leg totals shown in column headers.
    this.totalActualConsumption = this.actualConsumptionGroupTotalMt;
    this.totalCPConsumption = this.warrantedConsumptionGroupTotalMt;
    this.totalCpOverall = calculateCpOverallPercent(
      this.warrantedConsumptionGroupTotalMt,
      this.actualConsumptionGroupTotalMt,
    );
    this.totalDesignOverallPercent = calculateDesignOverallPercent(
      this.designConsumptionGroupTotalMt,
      this.actualConsumptionGroupTotalMt,
    );

    const eventTimeSums = source.reduce(
      (
        acc: { maneuvering: number; idle: number; cargoOps: number },
        item: any,
      ) => {
        acc.maneuvering += this.toFiniteNumber(
          item.time_taken?.maneuvering_time_taken,
        );
        acc.idle += this.toFiniteNumber(item.time_taken?.idle_time_taken);
        acc.cargoOps += this.toFiniteNumber(
          item.time_taken?.cargo_ops_time_taken,
        );
        return acc;
      },
      { maneuvering: 0, idle: 0, cargoOps: 0 },
    );
    this.eventTimeSums = { ...eventTimeSums };
    this.totalEventTimeDays =
      eventTimeSums.maneuvering + eventTimeSums.idle + eventTimeSums.cargoOps;

    // Dashboard cards
    this.sharedDataService.updateTotals({
      cp: Number(this.totalCpOverallMt),
      design: Number(this.totalDesignOverall),
      optimization: Number(this.totalOptimizationMT),
      voyages: this.totalVessels,
    });
  }

  formatCpOverallPercentDisplay(): string {
    return formatCpOverallPercent(this.totalCpOverall);
  }

  formatDesignOverallPercentDisplay(): string {
    return formatDesignOverallPercent(this.totalDesignOverallPercent);
  }

  hasCpOverallRangeData(): boolean {
    return !!this.cpOverallDatasetBounds && !!this.cpOverallSliderBounds;
  }

  hasDesignOverallRangeData(): boolean {
    return (
      !!this.designOverallDatasetBounds && !!this.designOverallSliderBounds
    );
  }

  get cpOverallRangeStep(): number {
    return 0.01;
  }

  get designOverallRangeStep(): number {
    return 0.01;
  }

  formatOverallRangeDisplay(value: unknown): string {
    return formatOverallRangeDisplay(value);
  }

  getOverallRangeEndpointClass(value: unknown): string {
    return getOverallRangeEndpointClass(value);
  }

  getCpOverallZeroPosition(): number {
    return getZeroPositionPercent(this.cpOverallSliderBounds);
  }

  getDesignOverallZeroPosition(): number {
    return getZeroPositionPercent(this.designOverallSliderBounds);
  }

  get minCpOverallSliderVal(): number | null {
    const val = this.filterForm?.get('minCpOverall')?.value;
    const bounds = this.cpOverallSliderBounds;
    if (!bounds || val === null || val === undefined || val === '')
      return bounds?.min ?? null;
    const num = Number(val);
    if (!Number.isFinite(num)) return bounds.min;
    return Math.max(bounds.min, Math.min(bounds.max, num));
  }

  get maxCpOverallSliderVal(): number | null {
    const val = this.filterForm?.get('maxCpOverall')?.value;
    const bounds = this.cpOverallSliderBounds;
    if (!bounds || val === null || val === undefined || val === '')
      return bounds?.max ?? null;
    const num = Number(val);
    if (!Number.isFinite(num)) return bounds.max;
    return Math.max(bounds.min, Math.min(bounds.max, num));
  }

  get minDesignOverallSliderVal(): number | null {
    const val = this.filterForm?.get('minDesignOverall')?.value;
    const bounds = this.designOverallSliderBounds;
    if (!bounds || val === null || val === undefined || val === '')
      return bounds?.min ?? null;
    const num = Number(val);
    if (!Number.isFinite(num)) return bounds.min;
    return Math.max(bounds.min, Math.min(bounds.max, num));
  }

  get maxDesignOverallSliderVal(): number | null {
    const val = this.filterForm?.get('maxDesignOverall')?.value;
    const bounds = this.designOverallSliderBounds;
    if (!bounds || val === null || val === undefined || val === '')
      return bounds?.max ?? null;
    const num = Number(val);
    if (!Number.isFinite(num)) return bounds.max;
    return Math.max(bounds.min, Math.min(bounds.max, num));
  }

  onCpOverallSliderChange(type: 'min' | 'max', value: number): void {
    const controlName = type === 'min' ? 'minCpOverall' : 'maxCpOverall';
    this.filterForm.patchValue({ [controlName]: value });
    this.scheduleLiveTableFilter();
  }

  onDesignOverallSliderChange(type: 'min' | 'max', value: number): void {
    const controlName =
      type === 'min' ? 'minDesignOverall' : 'maxDesignOverall';
    this.filterForm.patchValue({ [controlName]: value });
    this.scheduleLiveTableFilter();
  }

  getCpOverallError(type: 'min' | 'max'): string | null {
    return getOverallRangeError(
      type,
      this.filterForm.get('minCpOverall')?.value,
      this.filterForm.get('maxCpOverall')?.value,
      this.cpOverallDatasetBounds || this.cpOverallSliderBounds,
    );
  }

  getDesignOverallError(type: 'min' | 'max'): string | null {
    return getOverallRangeError(
      type,
      this.filterForm.get('minDesignOverall')?.value,
      this.filterForm.get('maxDesignOverall')?.value,
      this.designOverallDatasetBounds || this.designOverallSliderBounds,
    );
  }

  onCpOverallRangeInputBlur(): void {
    const bounds = this.cpOverallSliderBounds || this.cpOverallDatasetBounds;
    if (bounds) {
      let minVal = parseOverallRangeValue(
        this.filterForm.get('minCpOverall')?.value,
      );
      let maxVal = parseOverallRangeValue(
        this.filterForm.get('maxCpOverall')?.value,
      );
      let updated = false;
      const patch: Record<string, number> = {};

      if (minVal !== null) {
        if (minVal < bounds.min) {
          patch['minCpOverall'] = bounds.min;
          updated = true;
        } else if (minVal > bounds.max) {
          patch['minCpOverall'] = bounds.max;
          updated = true;
        }
      }
      if (maxVal !== null) {
        if (maxVal > bounds.max) {
          patch['maxCpOverall'] = bounds.max;
          updated = true;
        } else if (maxVal < bounds.min) {
          patch['maxCpOverall'] = bounds.min;
          updated = true;
        }
      }
      if (updated) {
        this.suppressFilterCascade = true;
        this.filterForm.patchValue(patch, { emitEvent: false });
        this.suppressFilterCascade = false;
      }
    }
    this.scheduleLiveTableFilter();
  }

  onDesignOverallRangeInputBlur(): void {
    const bounds =
      this.designOverallSliderBounds || this.designOverallDatasetBounds;
    if (bounds) {
      let minVal = parseOverallRangeValue(
        this.filterForm.get('minDesignOverall')?.value,
      );
      let maxVal = parseOverallRangeValue(
        this.filterForm.get('maxDesignOverall')?.value,
      );
      let updated = false;
      const patch: Record<string, number> = {};

      if (minVal !== null) {
        if (minVal < bounds.min) {
          patch['minDesignOverall'] = bounds.min;
          updated = true;
        } else if (minVal > bounds.max) {
          patch['minDesignOverall'] = bounds.max;
          updated = true;
        }
      }
      if (maxVal !== null) {
        if (maxVal > bounds.max) {
          patch['maxDesignOverall'] = bounds.max;
          updated = true;
        } else if (maxVal < bounds.min) {
          patch['maxDesignOverall'] = bounds.min;
          updated = true;
        }
      }
      if (updated) {
        this.suppressFilterCascade = true;
        this.filterForm.patchValue(patch, { emitEvent: false });
        this.suppressFilterCascade = false;
      }
    }
    this.scheduleLiveTableFilter();
  }

  private updateOverallRangeBoundsFromDataset(): void {
    const records = Array.isArray(this.elements) ? this.elements : [];
    this.cpOverallDatasetBounds = computeOverallRangeBounds(
      records,
      'cp_overall_mt',
    );
    this.cpOverallSliderBounds = this.cpOverallDatasetBounds
      ? ensureSliderBounds(this.cpOverallDatasetBounds)
      : null;
    this.designOverallDatasetBounds = computeOverallRangeBounds(
      records,
      'design_overall_mt',
    );
    this.designOverallSliderBounds = this.designOverallDatasetBounds
      ? ensureSliderBounds(this.designOverallDatasetBounds)
      : null;
  }

  private resetOverallRangeFiltersToDefaults(): void {
    const patch: Record<string, number | null> = {
      minCpOverall: null,
      maxCpOverall: null,
      minDesignOverall: null,
      maxDesignOverall: null,
    };

    if (this.cpOverallDatasetBounds) {
      patch['minCpOverall'] = this.cpOverallDatasetBounds.min;
      patch['maxCpOverall'] = this.cpOverallDatasetBounds.max;
    }

    if (this.designOverallDatasetBounds) {
      patch['minDesignOverall'] = this.designOverallDatasetBounds.min;
      patch['maxDesignOverall'] = this.designOverallDatasetBounds.max;
    }

    this.suppressFilterCascade = true;
    this.filterForm.patchValue(patch, { emitEvent: false });
    this.suppressFilterCascade = false;
  }

  private normalizeOverallRangeControl(
    controlName:
      | 'minCpOverall'
      | 'maxCpOverall'
      | 'minDesignOverall'
      | 'maxDesignOverall',
  ): void {
    const sliderBounds =
      controlName === 'minCpOverall' || controlName === 'maxCpOverall'
        ? this.cpOverallSliderBounds
        : this.designOverallSliderBounds;

    if (!sliderBounds) {
      return;
    }

    const minControl =
      controlName === 'minCpOverall' || controlName === 'maxCpOverall'
        ? 'minCpOverall'
        : 'minDesignOverall';
    const maxControl =
      controlName === 'minCpOverall' || controlName === 'maxCpOverall'
        ? 'maxCpOverall'
        : 'maxDesignOverall';

    const normalized = normalizeOverallRangeSelection(
      this.filterForm.get(minControl)?.value,
      this.filterForm.get(maxControl)?.value,
      sliderBounds,
    );

    this.suppressFilterCascade = true;
    this.filterForm.patchValue(
      {
        [minControl]: normalized.min,
        [maxControl]: normalized.max,
      },
      { emitEvent: false },
    );
    this.suppressFilterCascade = false;
  }

  clearClick(): void {
    this.suppressFilterCascade = true;
    this.filterForm.reset();
    this.filterForm.patchValue({ clientStatus: 'Active', segment: 'ALL' });
    this.selectedClientStatuses = ['Active'];

    this.shipNameSuggestions = [];
    this.selectedShips = [];
    this.dropdownOpen = false;
    this.dbShipDropdownOpen = false;
    this.dbShipSearchText = '';

    this.selectedFilterPort = [];
    this.selectedFilterPortLabel = '';
    this.PortDropdownOpen = false;
    this.dbPortDropdownOpen = false;
    this.dbPortSearchText = '';

    this.selectedOverConsumptions = [];
    this.selectedSegments = [];
    this.selectedClients = [];
    this.selectedOperations = [];
    this.selectedVoyageStatuses = [];
    this.selectedPortRoles = [];

    this.apiClientOptions = [];
    this.vesselNameOptions = [];
    this.tableFilterOperationOptions = [];
    this.tableFilterPortOptions = [];

    this.dbOverConsDropdownOpen = false;
    this.dbOperationDropdownOpen = false;
    this.dbOverConsSearchText = '';
    this.dbOperationSearchText = '';

    this.yearDropdownOpen = false;

    this.resetPcBenchmarkFiltersToDefaults({ reloadChart: false });
    this.resetTcpBenchmarkFiltersToDefaults({ reloadChart: false });
    this.updateOverallRangeBoundsFromDataset();
    this.resetOverallRangeFiltersToDefaults();

    this.filteredData = this.elements;
    this.count(this.filteredData);
    this.resetPagination();
    this.suppressFilterCascade = false;

    this.loadFilterOptions();
    this.rebuildTableFilterCascadeOptions();
    this.resetBenchmarkChartSortState();
    this.triggerChartUpdate();
    this.createTcpChart();
    this.createChart();
    this.closeFilterDrawer();
    this.cdr.markForCheck();
  }

  // filterClick() {
  //   const formValues = this.filterForm.value;

  //   this.filteredData = this.elements.filter((item: any) => {
  //     const matchesCondition =
  //       !formValues.condition || item.condition === formValues.condition;

  //     const matchesSegment =
  //       !formValues.segment ||
  //       item.segment?.trim().toLowerCase() ===
  //       formValues.segment.trim().toLowerCase();

  //     const matchesVesselName =
  //       !this.selectedShips.length ||
  //       this.selectedShips.includes(item.vessel_name);
  //     const matchesport =
  //       !this.selectedFilterPort.length ||
  //       this.selectedFilterPort.includes(item.port);

  //     const matchesOperations =
  //       !formValues.operation ||
  //       item.operations?.trim().toLowerCase() ===
  //       formValues.operation.trim().toLowerCase();

  //     this.clientName = formValues.client;
  //     const matchesClient =
  //       !formValues.client ||
  //       item.client?.trim().toLowerCase() ===
  //       formValues.client.trim().toLowerCase();

  //     const matchesFromDate =
  //       !formValues.from_date ||
  //       new Date(item.reportDate) >= new Date(formValues.from_date);

  //     const matchesToDate =
  //       !formValues.to_date ||
  //       new Date(item.reportDate) <= new Date(formValues.to_date);
  //     const matchesOverConsumption = () => {
  //       const selection = formValues.overConsumption;
  //       if (!selection) return true;

  //       if (selection === 'CP Overall') {
  //         return item.cp_overall_mt < 0;
  //       } else if (selection === 'Design Overall') {
  //         return item.design_overall_mt < 0;
  //       }

  //       return true;
  //     };
  //     const matchesOver = matchesOverConsumption();

  //     return (
  //       matchesCondition &&
  //       matchesSegment &&
  //       matchesVesselName &&
  //       matchesClient &&
  //       matchesFromDate &&
  //       matchesToDate &&
  //       matchesOver &&
  //       matchesport &&
  //       matchesOperations
  //     );
  //   });
  //   this.count(this.filteredData);
  //   this.currentPage = 0;
  //   this.paginateData();
  //   this.createTcpChart();
  //   this.createChart();

  //   if (this.filteredData.length === 0) {
  //     this.toastr.info('No matching records found.');
  //   }
  //   // Filter vessels based on selected client
  //   if (formValues.client) {
  //     const selectedClient = formValues.client.trim().toLowerCase();

  //     this.clientFilteredVessels = this.vessels.filter(
  //       (v: any) => v.client?.trim().toLowerCase() === selectedClient
  //     );
  //   } else {
  //     // No client selected → show all vessels
  //     this.clientFilteredVessels = [...this.vessels];
  //   }
  // }

  filterClick() {
    if (!Array.isArray(this.elements) || !this.elements.length) {
      this.toastr.warning('No data available to filter.');
      return;
    }

    const formValues = this.filterForm.getRawValue();

    if (
      (formValues.from_date && !formValues.to_date) ||
      (!formValues.from_date && formValues.to_date)
    ) {
      this.toastr.info(ToasterMessages.SelectDate);
      return;
    }

    this.syncTableFilterStateFromUi();
    this.applyLocalFiltering(formValues);
    this.closeFilterDrawer();
    this.cdr.detectChanges();
  }

  private normalize(value: string): string {
    return value?.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  private extractSegments(data: any[]): string[] {
    return [
      ...new Set(
        data
          .map((v: any) => v.segment?.trim().toUpperCase())
          .filter((s: string) => s),
      ),
    ];
  }

  applyLocalFiltering(
    formValues: any,
    options: { notifyWhenEmpty?: boolean } = {},
  ) {
    const notifyWhenEmpty = options.notifyWhenEmpty !== false;
    const source = Array.isArray(this.elements) ? this.elements : [];

    this.filteredData = source.filter((item: any) => {
      const segmentSelection = this.resolveSegmentSelection(formValues);
      const matchesSegment = !segmentSelection.length
        ? true
        : matchesAnySegment(item.segment, segmentSelection);

      const matchesVesselName = matchesSelectedVesselNames(
        item.vessel_name,
        this.selectedShips,
      );

      const matchesport = matchesSelectedPorts(
        item.port,
        this.selectedFilterPort,
      );

      const matchesOperations = matchesDatabaseOperationFilter(
        item.operations || item.operation,
        this.selectedOperations,
      );

      // Client Status only scopes the Client dropdown options.
      // Select All / empty → no client constraint on rows.
      const matchesClient =
        !this.selectedClients.length ||
          isAllFilterOptionsSelected(this.selectedClients, this.apiClientOptions)
          ? true
          : matchesAnyClient(
            this.resolveRecordClient(item),
            this.selectedClients,
          );

      const matchesVoyageStatus = matchesSelectedValues(
        item.final_status,
        this.selectedVoyageStatuses,
      );

      const matchesDateRange = matchesPortCallDateRange(
        item.arrival_date_and_time,
        item.departure_date_and_time,
        formValues.from_date,
        formValues.to_date,
      );

      const matchesOverConsumption = () => {
        // Normalize overConsumption value whether it comes as an array or a single string
        const rawSelection =
          formValues.overConsumption || this.selectedOverConsumptions;

        if (
          !rawSelection ||
          (Array.isArray(rawSelection) && !rawSelection.length)
        ) {
          return true;
        }

        const selections = Array.isArray(rawSelection)
          ? rawSelection
          : [rawSelection];

        return selections.some((selection: any) => {
          const normalized = String(selection).trim().toLowerCase();

          if (selection === 'CP Overall') {
            return item.cp_overall_mt < 0;
          }
          if (selection === 'Design Overall') {
            return item.design_overall_mt < 0;
          }
          if (normalized === 'yes') {
            return item.cp_overall_mt < 0 || item.design_overall_mt < 0;
          }
          if (normalized === 'no') {
            return item.cp_overall_mt >= 0 && item.design_overall_mt >= 0;
          }
          return true;
        });
      };

      const matchesCpOverallRange = matchesOverallRangeField(
        item.cp_overall_mt,
        parseOverallRangeValue(formValues.minCpOverall),
        parseOverallRangeValue(formValues.maxCpOverall),
        this.cpOverallDatasetBounds,
      );

      const matchesDesignOverallRange = matchesOverallRangeField(
        item.design_overall_mt,
        parseOverallRangeValue(formValues.minDesignOverall),
        parseOverallRangeValue(formValues.maxDesignOverall),
        this.designOverallDatasetBounds,
      );

      return (
        matchesSegment &&
        matchesVesselName &&
        matchesClient &&
        matchesDateRange &&
        matchesOverConsumption() &&
        matchesCpOverallRange &&
        matchesDesignOverallRange &&
        matchesport &&
        matchesOperations &&
        matchesVoyageStatus &&
        this.matchesPortRoleFilter(item)
      );
    });

    this.clientName =
      this.selectedClients.length > 0 ? this.selectedClients[0] : 'ALL';

    // Recalculate summary cards from full filtered set, then paginate table rows only.
    this.count(this.filteredData);
    this.resetPagination();
    this.createTcpChart();
    this.createChart();
    this.triggerChartUpdate();

    if (this.filteredData.length === 0 && notifyWhenEmpty) {
      this.toastr.info('No matching records found.');
    }
  }

  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }

  @HostBinding('class.pc-light') get pcLight(): boolean {
    return !this.isDarkMode();
  }
  private resetPagination(): void {
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.paginateData();
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.itemsPerPage = event.pageSize || this.itemsPerPage;
    this.paginateData();
  }
  private updatePageSizeOptions(_total: number) {
    this.pageSizeOptions = [10];
  }
  getColor(value: number): string {
    if (value > 0) {
      return '#a8d296'; // Positive values in green
    } else if (value < 0) {
      return '#ff9090'; // Negative values in red
    }
    return 'inherit'; // Default color for zero
  }
  exportClick() {
    const data = this.filteredData || this.elements;
    if (!data.length) return;

    const firstItem = data[0];
    const shipName = firstItem.vessel_name || 'UnknownShip';
    const segment = firstItem.segment || 'UnknownSegment';

    const csvHeader = [
      'S.No',
      'Vessel Name',
      'Vessel IMO',
      'Voyage Number',
      'Segment',
      'PIC',
      'Final Status',
      'Port',
      'Operations',
      'Report Sent Date',
      'Arrival Date & Time',
      'Departure Date & Time',
      'Cargo Quantity (m³)',
      'Total Time',
      'BROB Arrival HSFO',
      'BROB Arrival LSGO',
      'BROB Arrival VLSFO',
      'Bunker Received HSFO',
      'Bunker Received LSGO',
      'Bunker Received VLSFO',
      'BROB Departure HSFO',
      'BROB Departure LSGO',
      'BROB Departure VLSFO',
      'Actual Cons. HSFO',
      'Actual Cons. LSGO',
      'Actual Cons. VLSFO',
      'CP Warranted HSFO',
      'CP Warranted LSGO',
      'CP Warranted VLSFO',
      'CP Loss/Gain HSFO',
      'CP Loss/Gain LSGO',
      'CP Loss/Gain VLSFO',
      'CP Overall',
      'Tech Design HSFO',
      'Tech Design LSGO',
      'Tech Design VLSFO',
      'Tech Loss/Gain HSFO',
      'Tech Loss/Gain LSGO',
      'Tech Loss/Gain VLSFO',
      'Design Overall',
      'Actual Maneuvering',
      'Actual Idle',
      'Actual Cargo Ops',
      'Warranted Maneuvering',
      'Warranted Idle',
      'Warranted Cargo Ops',
      'Design Maneuvering',
      'Design Idle',
      'Design Cargo Ops',
      'Optimization Consumption Maneuvering',
      'Optimization Consumption Cargo Ops',
      'Optimization Consumption Idle',
      'Optimization Consumption Total',
      'Optimization Consumption Loss/Gain Maneuvering',
      'Optimization Consumption Loss/Gain Cargo Ops',
      'Optimization Consumption Loss/Gain Idle',
      'Optimization Consumption Overall',
      'Event Time Maneuvering (days)',
      'Event Time Cargo Ops (days)',
      'Event Time Idle (days)',
      'Claim Raised',
      'Maneuvering Distance',
      'Maneuvering Speed',
    ].join(',');

    const formatCsvNum = (val: number | null | undefined): string =>
      val !== null && val !== undefined && Number.isFinite(val)
        ? Number(val).toFixed(2)
        : '';

    const csvRows = data.map((item: any, index: number) => {
      const optMan = this.getOptimizationLegConsumption(item, 'maneuvering');
      const optCargo = this.getOptimizationLegConsumption(item, 'cargoOps');
      const optIdle = this.getOptimizationLegConsumption(item, 'idle');
      const hasAnyOpt =
        optMan !== null || optCargo !== null || optIdle !== null;
      const optTotal = hasAnyOpt
        ? (optMan ?? 0) + (optCargo ?? 0) + (optIdle ?? 0)
        : null;

      const actMan = getOpActualValue(item, 'maneuvering');
      const actCargo = getOpActualValue(item, 'cargoOps');
      const actIdle = getOpActualValue(item, 'idle');
      const hasAnyAct =
        actMan !== null || actCargo !== null || actIdle !== null;
      const actTotal = hasAnyAct
        ? (actMan ?? 0) + (actCargo ?? 0) + (actIdle ?? 0)
        : null;

      const diffMan =
        optMan !== null && actMan !== null ? optMan - actMan : null;
      const diffCargo =
        optCargo !== null && actCargo !== null ? optCargo - actCargo : null;
      const diffIdle =
        optIdle !== null && actIdle !== null ? optIdle - actIdle : null;
      const diffOverall =
        optTotal !== null && actTotal !== null ? optTotal - actTotal : null;

      return [
        index + 1,
        item.vessel_name,
        item.vessel_imo,
        item.voyage_number,
        this.formatSegment(item.segment),
        item.pic,
        item.final_status,
        item.port,
        item.operations,
        this.formatUTCDate(item.report_sent_date),
        this.formatPortLocalAsUTC(item.arrival_date_and_time, item.port),
        this.formatPortLocalAsUTC(item.departure_date_and_time, item.port),
        item.cargo_quentity ?? '',
        item.total_time,

        // BROB Arrival
        item.brob_arrival_mt?.brob_arrival_mt_hsfo || '',
        item.brob_arrival_mt?.brob_arrival_mt_lsgo || '',
        item.brob_arrival_mt?.brob_arrival_mt_vlsfo || '',

        // Bunker Received
        item.bunker_received?.bunker_received_hsfo || '',
        item.bunker_received?.bunker_received_lsgo || '',
        item.bunker_received?.bunker_received_vlsfo || '',

        // BROB Departure
        item.brob_departure_mt?.brob_departure_mt_hsfo || '',
        item.brob_departure_mt?.brob_departure_mt_lsgo || '',
        item.brob_departure_mt?.brob_departure_mt_vlsfo || '',

        // Actual Consumption
        item.actual_consumptions_mt?.actual_consumptions_mt_hsfo || '',
        item.actual_consumptions_mt?.actual_consumptions_mt_lsgo || '',
        item.actual_consumptions_mt?.actual_consumptions_mt_vlsfo || '',

        // CP Warranted
        item.cp_warranted_consumptions_mt?.cp_warranted_consumptions_mt_hsfo ||
        '',
        item.cp_warranted_consumptions_mt?.cp_warranted_consumptions_mt_lsgo ||
        '',
        item.cp_warranted_consumptions_mt?.cp_warranted_consumptions_mt_vlsfo ||
        '',

        // CP Loss/Gain
        item.cp_warrantedloss_or_gain?.cp_warrantedloss_or_gain_mt__hsfo || '',
        item.cp_warrantedloss_or_gain?.cp_warrantedloss_or_gain_mt__lsgo || '',
        item.cp_warrantedloss_or_gain?.cp_warrantedloss_or_gain_mt__vlsfo || '',

        // CP Overall
        item.cp_overall_mt || '',

        // Technical Design
        item.technical_design_consumptions_mt
          ?.technical_design_consumptions_mt_hsfo || '',
        item.technical_design_consumptions_mt
          ?.technical_design_consumptions_mt_lsgo || '',
        item.technical_design_consumptions_mt
          ?.technical_design_consumptions_mt_vlsfo || '',

        // Tech Loss/Gain
        item.technical_designloss_or_gain_mt
          ?.technical_designloss_or_gain_mt_hsfo || '',
        item.technical_designloss_or_gain_mt
          ?.technical_designloss_or_gain_mt_lsgo || '',
        item.technical_designloss_or_gain_mt
          ?.technical_designloss_or_gain_mt_vlsfo || '',

        // Design Overall
        item.design_overall_mt || '',

        // Actual maneuvering/cargo/idle
        item.actual_consumptions_mt_maneuvering_cargo_idle
          ?.maneuvering_actual_consumption_mt || '',
        item.actual_consumptions_mt_maneuvering_cargo_idle
          ?.idle_actual_consumption_mt || '',
        item.actual_consumptions_mt_maneuvering_cargo_idle
          ?.cargo_ops_actual_consumption_mt || '',

        // Warranted maneuvering/cargo/idle
        this.getOpWarrantedValue(item, 'maneuvering') ?? '',
        this.getOpWarrantedValue(item, 'idle') ?? '',
        this.getOpWarrantedValue(item, 'cargoOps') ?? '',

        // Design maneuvering/cargo/idle
        this.getOpDesignValue(item, 'maneuvering') ?? '',
        this.getOpDesignValue(item, 'idle') ?? '',
        this.getOpDesignValue(item, 'cargoOps') ?? '',

        // Design Consumption Optimization
        formatCsvNum(optMan),
        formatCsvNum(optCargo),
        formatCsvNum(optIdle),
        formatCsvNum(optTotal),
        formatCsvNum(diffMan),
        formatCsvNum(diffCargo),
        formatCsvNum(diffIdle),
        formatCsvNum(diffOverall),

        // Event Time (days) — Maneuvering / Cargo Ops / Idle
        item.time_taken?.maneuvering_time_taken ?? '',
        item.time_taken?.cargo_ops_time_taken ?? '',
        item.time_taken?.idle_time_taken ?? '',

        // Claim
        item.claim_raised || '',

        // Maneuvering Performance
        item.maneuvering_performance?.maneuvering_distance ?? '',
        item.maneuvering_performance?.maneuvering_speed ?? '',
      ]
        .map((field: any) => `"${field ?? ''}"`) // handle commas safely
        .join(',');
    });

    const csvContent = `${csvHeader}\n${csvRows.join('\n')}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Port_DB_Table.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  toggleMapView(event?: MatSlideToggleChange): void {
    this.graph = event ? event.checked : !this.graph;
    localStorage.removeItem('mapToggle');
    if (this.graph) {
      this.ensureBenchmarkDataLoaded();
      this.loadBenchmarkCharts();
    }
  }

  private ensureBenchmarkDataLoaded(): void {
    if (this.benchmarkDataLoaded) {
      return;
    }
    this.benchmarkDataLoaded = true;
    this.loadBenchmarkSegments();
    this.benchmarkOperations();
    this.loadPcBenchmarkClients();
    this.loadTcpBenchmarkSegments();
  }

  trackByRow(index: number, item: any): any {
    return item?.unique_id || item?.vessel_imo || item?.report_id || index;
  }

  private readManeuveringMetric(element: any, keys: string[]): number | null {
    const sources = [
      element?.maneuvering_performance,
      element?.manoeuvring_performance,
      element,
      element?.time_taken,
      element?.maneuvering,
      element?.manoeuvring,
      element?.tcp,
    ];

    for (const source of sources) {
      if (!source) {
        continue;
      }
      for (const key of keys) {
        const raw = source[key];
        if (
          raw === null ||
          raw === undefined ||
          String(raw).trim() === '' ||
          String(raw).trim() === '--'
        ) {
          continue;
        }
        const parsed = this.toFiniteNumber(raw);
        if (parsed !== 0 || raw === 0 || raw === '0') {
          return parsed;
        }
      }
    }

    return null;
  }

  getManeuveringDistanceValue(element: any): number | null {
    return this.readManeuveringMetric(element, [
      'maneuvering_distance',
      'manoeuvring_distance',
      'maneuvering_distance_nm',
      'manoeuvring_distance_nm',
      'distance',
    ]);
  }

  getManeuveringSpeedValue(element: any): number | null {
    return this.readManeuveringMetric(element, [
      'maneuvering_speed',
      'manoeuvring_speed',
      'maneuvering_speed_kn',
      'manoeuvring_speed_kn',
      'speed',
    ]);
  }

  formatManeuveringDistance(element: any): string {
    const value = this.getManeuveringDistanceValue(element);
    return value === null ? '--' : value.toFixed(2);
  }

  formatManeuveringSpeed(element: any): string {
    const value = this.getManeuveringSpeedValue(element);
    return value === null ? '--' : value.toFixed(2);
  }

  getTotalConsumption(element: any): string {
    const hsfo =
      element.actual_consumptions_mt.actual_consumptions_mt_hsfo || 0;
    const lsgo =
      element.actual_consumptions_mt.actual_consumptions_mt_lsgo || 0;
    const vlsfo =
      element.actual_consumptions_mt.actual_consumptions_mt_vlsfo || 0;

    const total = hsfo + lsgo + vlsfo;
    return total === 0 ? '__' : total.toFixed(2);
  }

  private toFiniteNumber(value: any): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const cleaned = String(value).replace(/,/g, '').trim();
    if (!cleaned) return 0;
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getTotalConsumptionMtNumeric(element: any): number {
    const hsfo = this.toFiniteNumber(
      element?.actual_consumptions_mt?.actual_consumptions_mt_hsfo,
    );
    const lsgo = this.toFiniteNumber(
      element?.actual_consumptions_mt?.actual_consumptions_mt_lsgo,
    );
    const vlsfo = this.toFiniteNumber(
      element?.actual_consumptions_mt?.actual_consumptions_mt_vlsfo,
    );
    return hsfo + lsgo + vlsfo;
  }
  azSortChart(condition: any) {
    if (condition) {
      this.azsort = true;
      this.isAscending = !this.isAscending;
      this.isSorting = false;
    }
    if (condition === 'tcp') {
      this.sortTcpGraph();
    } else {
      this.SortPcGraph();
    }
  }
  sortConsChart(condition: any) {
    if (condition) {
      this.isSorting = true;
      this.sortMapData = !this.sortMapData;
      this.azsort = false;
    }
    if (condition === 'tcp') {
      this.sortTcpGraph();
    } else {
      this.SortPcGraph();
    }
  }
  sortTcpGraph() {
    if (!this.tcpData?.length) {
      this.tcpChartOptions = null;
      return;
    }

    let sortedData = [...this.tcpData].sort((a: any, b: any) =>
      a.vessel_name.localeCompare(b.vessel_name),
    );
    if (this.sortMapData && this.isSorting) {
      sortedData = sortedData.sort(
        (a: any, b: any) => b.data?.total - a.data?.total,
      );
    } else if (this.isSorting && !this.sortMapData) {
      // Sort from small to large
      sortedData = sortedData.sort(
        (a: any, b: any) => a.data?.total - b.data?.total,
      );
    }
    if (this.isAscending && this.azsort) {
      sortedData = sortedData.sort((a: any, b: any) =>
        a.vessel_name.localeCompare(b.vessel_name),
      );
    } else if (this.azsort && !this.isAscending) {
      sortedData = sortedData.sort((a: any, b: any) =>
        b.vessel_name.localeCompare(a.vessel_name),
      );
    }

    this.applyTcpChartFromData(sortedData);
  }
  SortPcGraph() {
    this.buildPcChartFromData();
  }

  private segmentColorMap: { [key: string]: string } = {
    mr: '#c27036',
    handymax: '#369047',
    suezmax: '#6a6a6a',
    vlcc: '#3249d4',
    aframax: '#7e3b3b',
    'gas tanker': '#5b5418',
    handy: '#35707b',
    lr1: '#2a644f',
    lr2: '#556ba6',
    coaster: '#859914ff',
    handysize: '#541e85',
    supramax: '#6b181f',
    ultramax: '#85501c',
    panamax: '#723768',
    kamsarmax: '#077c5f',
    'ultra panamax': '',
    capesize: '#7c7440',
    chinamax: '',
    'lr1/panamax': '#42bfaaff',
    'lr2/aframax': '#427ebf',
    'lr3/suezmax': '#bf4281ff',
  };

  getDynamicSegmentStyle(segment: string): any {
    if (!segment) return {};

    const key = segment.toLowerCase();

    // If NOT in static mapping → assign dynamic color
    if (!this.segmentColorMap[key]) {
      this.segmentColorMap[key] = this.generateDarkColorFromText(key);
    }

    const color = this.segmentColorMap[key];

    return {
      'background-color': color,
      'border-radius': '12px',
      padding: '0px 6px',
      color: '#fff',
      display: 'inline-block',
      'font-weight': '500',
      'text-transform': 'uppercase',
      'font-size': '12px',
      'letter-spacing': '0.5px',
    };
  }

  /** Generates a consistent dark color for unknown segments */
  private generateDarkColorFromText(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    let r = (hash >> 16) & 0xff;
    let g = (hash >> 8) & 0xff;
    let b = hash & 0xff;

    // Force dark tones
    r = Math.floor(r * 0.4);
    g = Math.floor(g * 0.4);
    b = Math.floor(b * 0.4);

    return `rgb(${r}, ${g}, ${b})`;
  }
  formatVesselName(name: string): string {
    if (!name) return '';

    return name
      .toLowerCase()
      .split(' ')
      .filter((word) => word.trim().length > 0)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  formatToDays(totalTime: string): string {
    if (!totalTime) return '0.00 ';
    return `${parseTotalTimeToDays(totalTime).toFixed(2)}`;
  }

  private getFilteredTableRows(): any[] {
    return this.filteredData ?? this.elements ?? [];
  }

  getTotalTimeDaysHeader(): string {
    return 'Total Time (Days)';
  }

  private maskDemoVesselName(name: string, index: number): string {
    if (this.subusername?.toLowerCase() !== 'demo') {
      return name;
    }

    // Based on DEMO_VESSEL_PC order / IMO mapping
    const demoImos = ['9399480', '9438406'];

    const imo = demoImos[index];

    if (imo === '9399480') {
      return 'Vessel 1';
    }

    if (imo === '9438406') {
      return 'Vessel 2';
    }

    return `Vessel ${index + 1}`;
  }


  private filterDemoVesselsOnly(rows: any[]): any[] {
    if (this.subusername?.toLowerCase() !== 'demo') {
      return rows || [];
    }

    return (rows || []).filter((item: any) => {
      const rawImo = item.vessel_imo ?? item.imo ?? item.vessel_id ?? '';
      const stringImo = String(rawImo).trim();

      return DEMO_VESSEL_PC.includes(stringImo);
    });
  }

  /**
   * Bound in an `*ngIf` on every table row, so this runs once per row per
   * change-detection pass. Decode the token once and reuse the result — the
   * auth token cannot change while the component is mounted.
   */
  isAdminUser(): boolean {
    if (this.adminUserCache === null) {
      this.adminUserCache = this.resolveAdminUser();
    }
    return this.adminUserCache;
  }

  private resolveAdminUser(): boolean {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return false;
      const parts = token.split('.');
      if (parts.length < 2) return false;
      const payload = JSON.parse(atob(parts[1]));
      const user = String(payload?.user || payload?.sub || '').toLowerCase().trim();
      return ['meraki_admin', 'mera_admin'].includes(user);
    } catch (err) {
      return false;
    }
  }

  confirmAndDeleteDatabaseRow(row: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      (event.currentTarget as HTMLElement)?.blur();
    }
    (document.activeElement as HTMLElement)?.blur();
    if (!row) return;

    const arrDate = this.formatPortLocalAsUTC(row.arrival_date_and_time, row.port);
    const depDate = this.formatPortLocalAsUTC(row.departure_date_and_time, row.port);
    const datesFormatted = (arrDate || depDate)
      ? `${arrDate || '--'}  →  ${depDate || '--'}`
      : undefined;

    const rawPort = row.port || row.port_name || row.resolved_port_name;
    const resolvedPort = Array.isArray(rawPort) ? (rawPort[0] || '') : (rawPort || '');

    const rawVessel = row.vessel_name || row.vesselName;
    const resolvedVessel = Array.isArray(rawVessel) ? (rawVessel[0] || '') : (rawVessel || '');

    const dialogRef = this.dialog.open(PortDeleteConfirmPopupComponent, {
      width: '460px',
      maxWidth: '92vw',
      panelClass: 'pc-delete-confirm-dialog',
      backdropClass: 'pc-delete-confirm-backdrop',
      autoFocus: false,
      restoreFocus: false,
      data: {
        title: 'Delete Database Record',
        message: 'Are you sure you want to delete this database entry? This will remove the record from the database and analytics.',
        subMessage: 'The related My Request log will remain intact in My Requests.',
        vesselName: resolvedVessel,
        vesselImo: row.vessel_imo || row.original_vessel_imo,
        portName: resolvedPort,
        dates: datesFormatted,
        confirmText: 'Delete Record',
        cancelText: 'Cancel',
        isDanger: true,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        const payload = {
          unique_id: row.unique_id,
          vessel_imo: row.vessel_imo || row.original_vessel_imo,
          arrival_date_and_time: row.arrival_date_and_time,
          departure_date_and_time: row.departure_date_and_time,
        };

        this.prequestFormService.deleteDatabaseRow(payload).subscribe({
          next: () => {
            this.toastr.success('Database record deleted successfully');
            this.loadData();
          },
          error: (err: any) => {
            console.error('Delete database record failed', err);
            this.toastr.error(err?.error?.message || err?.error?.error || 'Could not delete database record');
          },
        });
      }
    });
  }
}

